import React, { useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import { Navigate, replace, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  Feature,
  FeatureBase,
  FloatingFeature,
  IStringHash, 
  ModelBase
} from "@models";
import { 
  Creature, 
  TemplateBase,
  UpgradableNumber,
  HitDiceUpgradable,
  AbilityScoresUpgradable,
  Spell,
  Resource,
  TableBase,
  TableEntry,
  CreatureInstance,
  // SpellcastingEntry,
  LegendariesTable,
  Legendaries,
  Attributes,
  SpellcastingEntry,
  UpgradableNumberFeature,
  CreatureAbility,
  AbilityEffect,
  Potence,
  Sense,
  SENSES,
  Skill,
  SKILLS,
  // SKILLS,
} from "@dndModels";
import { 
  ABILITY_SCORES, 
  CREATURE_TYPES, 
  SIZE_OPTIONS,
  SLOT_LEVELS,
  DICE_SIZES,
  CR_OPTIONS,
  // SIZE_MAP,
  CREATURE_ALIGNMENTS,
  CREATURE_FEATURE_TYPES,
  ABILITY_SCORE_FULL_TO_ABBREV_MAP,
  DAMAGE_TYPES,
  AREA_TYPES,
  SIZE_TO_NUM_MAP,
} from "@dndModels/Constants";
import { AbilityScoreType, AreaType, CreatureFBType, CreatureType, DamageType, isCreatureFBType, isCreatureType, isDamageType, isSpeedType, isStatType, SpeedType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
// import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

import TemplateBox from "@dndComponents/TemplateBox";

import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import FloatMenu from "@components/input/menus/FloatMenu";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CreatureAbilityScoresUpgradableInput from "@dndComponents/creature/CreatureAbilityScoresUpgradableInput";
import LoadingBox from "@components/input/LoadingBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import DeleteButton from "@components/input/buttons/DeleteButton";
import AddButton from "@components/input/buttons/AddButton";
import CheckBox from "@components/input/CheckBox";
import TableInput from "@dndComponents/other/TableInput";
import ButtonBox from "@components/input/buttons/ButtonBox";
import FeatureBaseInput from "@dndComponents/feature/FeatureBaseInput";
import FeatureBasesInput from "@dndComponents/feature/FeatureBasesInput";
import CreatureSpells from "@dndComponents/creature/CreatureSpells";
import CreatureAttributes from "@dndComponents/creature/CreatureAttributes";
import ArrowBackButton from "@components/input/buttons/ArrowBackButton";
import LegendariesTableInput from "@dndComponents/other/LegendariesTableInput";
import DisplayStringWithReplacementsRecursive from '@dndComponents/display/DisplayStringWithReplacementsRecursive';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();

type TabType = "main" | "hp" | "attributes" | "more_attributes" | "even_more_attributes" | "ability_scores" | "resources" | "actions" | "legendary_actions" | "lair_actions" | "traits" | "spells" | "table" | "parse";

export default function CreatureEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Creature>(api.applyWorkingOn(new Creature()));
  const [parse_creature, setParseCreature] = useState<Creature | undefined>(undefined);
  const [parsed_creature_text, setParsedCreatureText] = useState("");
  // const [spells, setSpells] = useState<Spell[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState<TabType>("main");
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  const [parse_string, setParseString] = useState("");
  const [resource_cr, setResourceCR] = useState(-1);
  const [legendaries_cr, setLegendariesCR] = useState(-1);
  const [creature_instances, setCreatureInstances] = useState<CreatureInstance[]>([]);
  const [level_filter, setLevelFilter] = useState(-1);
  const [topHeight, setTopHeight] = useState(0);
  const [ready_for_height, setReadyForHeight] = useState(false);
    
  const heightFinder: any = React.createRef();

  const parseRef = useRef<HTMLDivElement>(null);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  // const formHeight = window.innerHeight - (window.innerWidth > 697 ? 280 : 280) - (tab === "main" ? 45 : 0);

  const updateCreatureInstances = useCallback(async (use_obj: Creature) => {
    const crs = CR_OPTIONS.map(o => +o).filter(o => o >= use_obj.min_challenge_rating && o <= use_obj.max_challenge_rating);
    
    const new_cis: CreatureInstance[] = [];
    for (const cr of crs) {
      const ci = new CreatureInstance();
      await ci.copyCreature(use_obj, cr);
      new_cis.push(ci);
    }
    
    setCreatureInstances(new_cis);
  }, []);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["tool","condition","spell","resource","creature"]});
    setResources(res.resource as Resource[]);
    // setSpells(res.spell as Spell[]);
    
    if (id && id !== "") {
      const creatures = res.creature;
      // debugger;
      const c = creatures.find(o => o._id === id);
      if (c instanceof Creature) {
        await api.loadObject(c);
        setObj(c);
        updateCreatureInstances(c);
        if (c.legendaries_table.entries.length > 0) {
          const min = Math.min(...c.legendaries_table.entries.map(o => o.level));
          setLegendariesCR(min);
        }
        if (c.resources_table.entries.length > 0) {
          const min = Math.min(...c.resources_table.entries.map(o => o.level));
          setResourceCR(min);
        }
        setLoading(false);
        setReadyForHeight(true);
      } else {
        debugger;
      }
    } else {
      // It's here from clicking Create on index.  
      // If the user is not admin, then we need to give the floating feature their id as owner or it will be disabled.
      const new_obj = obj;
      if (api.user && !api.user.admin && new_obj.name === "" && new_obj.owner_id === "") {
        new_obj.owner_id = api.user._id;
      }
      setObj(new_obj);
      updateCreatureInstances(new_obj);
      setChangeTime(common_util.get_time_number());
      setLoading(false);
      setReadyForHeight(true);
    }
  }, [id, obj, updateCreatureInstances]);

  const updateHeight = useCallback(() => {
    // console.log(heightFinder);
    if (heightFinder.current instanceof HTMLElement) {
      setTopHeight(heightFinder.current.clientHeight + 25);
    }
  }, [heightFinder]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
    // Update height on window resize
    window.addEventListener('resize', updateHeight);

    // Clean up the event listener
    return () => window.removeEventListener('resize', updateHeight);
  }, [fetched, updateHeight, load]);

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      setObj(obj);
      setSubmitting(true);
    }
  }

  useEffect(() => {
    if (submitting) {
      debugger;
      setSubmitting(false);
      // Check for unsaved ffs.
      const fb_with_unsaved = obj.fbs.find(o => o.features.filter(o2 => o2.floating_feature_id === "").length > 0);
      // console.log(fb_with_unsaved?.name??"None");
      if (fb_with_unsaved && fb_with_unsaved.features[0] && fb_with_unsaved.features[0].floating_feature) {
        const new_ff = fb_with_unsaved.features[0].floating_feature;

        api.upsertObject(new_ff.data_type, new_ff).then(res => {
          if (res) {
            new_ff._id = res.body.id;
            api.add_floating_feature(new_ff);
            fb_with_unsaved.features[0].floating_feature_id = new_ff._id;
            setSubmitting(true);
          }
        });
      } else {
        // debugger;
        api.upsertObject("creature", obj).then(res => {
          setProcessing(false);
          setNavigateTo("/dandd/creature");
        });
      }
    }
  }, [submitting, setSubmitting, obj]);

  useEffect(() => {
    if (ready_for_height) {
      setReadyForHeight(false);
      updateHeight();
    }
     
  }, [ready_for_height, setReadyForHeight, updateHeight]);

  const render_tab = () => {
    switch (tab) {
      case "ability_scores": 
        return (
          <CreatureAbilityScoresUpgradableInput
            obj={obj}
            disabled={disabled}
            onChange={(changed: AbilityScoresUpgradable) => {
              // console.log(changed);
              const new_obj = obj;
              new_obj.ability_scores = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      case "attributes":
        return (
          <CreatureAttributes 
            obj={obj} 
            disabled={disabled} 
            onChange={() => {
              const new_obj = obj;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        );
      case "resources": return renderResources();
      case "actions": return renderActions();
      case "legendary_actions": return renderLegendaryActions();
      case "lair_actions": return renderLairActions();
      case "traits": return renderTraits();
      case "spells":
        return (
          <CreatureSpells
            obj={obj}
            disabled={disabled}
            onChange={() => {
              const new_obj = obj;
              setObj(new_obj);
            }}
          />
        ); 
      case "parse": return renderParse();
      case "hp": return renderHP();
      case "more_attributes": return renderMoreAttributes();
      case "even_more_attributes": return renderEvenMoreAttributes();
      case "table": return renderTable();
      default: return renderMain();
    }
  }

  const renderResources = () => {
    const cr_options = CR_OPTIONS.filter(o => 
      +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating 
    );
    const use_entry = obj.resources_table.getEntry(resource_cr);
    return (
      <Grid container spacing={0} direction="row">
        { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
          (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
          <Grid
            size={{
              xs: 12
            }}>
            <TableInput
              obj={obj.resources_table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.resources_table.copyBase(changed);
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox 
            name="Select CR for Resources"
            options={cr_options}
            value={`${resource_cr}`}
            onChange={(changed: string) => {
              const new_obj = obj;
              const new_cr = +changed;
              // let resources = new_obj.old_resources[new_cr];
              // if (!resources) {
              //   const highest_lower_cr = Math.max(-1,...Object.keys(new_obj.old_resources).map(o => +o).filter(o => o < new_cr));
              //   resources = [];
              //   if (highest_lower_cr > 0) {
              //     const lower_cr_resources = new_obj.old_resources[highest_lower_cr];
              //     resources = lower_cr_resources.map(o => {
              //       return {...o};
              //     });
              //   } else {
              //     const lowest_higher_cr = Math.min(200,...Object.keys(new_obj.old_resources).map(o => +o).filter(o => o > new_cr));
              //     resources = [];
              //     if (lowest_higher_cr < 200) {
              //       const higher_cr_resources = new_obj.old_resources[lowest_higher_cr];
              //       resources = higher_cr_resources.map(o => {
              //         return {...o};
              //       });
              //     }
              //   }
              //   new_obj.old_resources[new_cr] = resources;
              //   setObj(new_obj);
              //   setChangeTime(common_util.get_time_number());
              // }
              const old_entry = new_obj.resources_table.getEntry(new_cr);
              if (!old_entry || old_entry.level !== new_cr) {
                const entry = old_entry ? old_entry.clone() : new TableEntry();
                entry.level = new_cr;
                new_obj.resources_table.entries.push(entry);
              } else {
                const entry = new TableEntry();
                entry.level = new_cr;
                new_obj.resources_table.entries.push(entry);
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
              setResourceCR(new_cr);
              setReloading(true);
            }}
          />
        </Grid>
        { use_entry && use_entry.level === resource_cr &&
          <>
            <Grid
              size={{
                xs: 10
              }}>
              <SelectObjectBox
                name="New Resource"
                data_type="resource"
                extra_options={SLOT_LEVELS.filter(o => Object.keys(use_entry.data).filter(o2 => o2 === o).length === 0)}
                filter={{ _id: { "$nin": Object.keys(use_entry.data) }}}
                value=""
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const changing_entry = new_obj.resources_table.getEntry(resource_cr);
                  if (changing_entry) {
                    changing_entry.data[changed] = 1;
                    if (!new_obj.resources_table.fields.includes(changed)) {
                      new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = changed;
                    }
                    if (!changed.includes("Slot")) {
                      const resource = resources.find(o => o._id === changed);
                      if (resource && resource.default_dice_size !== 1) {
                        changing_entry.data[`${changed}_size`] = resource.default_dice_size;
                        if (!new_obj.resources_table.fields.includes(`${changed}_size`)) {
                          new_obj.resources_table.field_order[new_obj.resources_table.fields.length] = `${changed}_size`;
                        }
                      }
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 2
              }}>
              <DeleteButton
                label="Delete Resource Level"
                onClick={() => {
                  const new_obj = obj;
                  new_obj.resources_table.entries = new_obj.resources_table.entries.filter(o => o.level !== resource_cr);
                  const other_crs = new_obj.resources_table.entries.map(o => o.level);
                  if (other_crs.length > 0) {
                    setResourceCR(+other_crs[0]);
                  } else {
                    setResourceCR(-1);
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              />
            </Grid>
            { !reloading && 
              obj.resources_table.fields.filter(o => !o.endsWith("_size")).map((field, key) => {
                const resource = field.includes("Slot") ? undefined : resources.find(o => o._id === field);
                const count = use_entry.data[field];
                const size = use_entry.data[`${field}_size`];
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      size={{
                        xs: 4
                      }}>
                      { field.includes("Slot") ? field : 
                        <DisplayObjects type="resource" ids={[field]} />
                      }
                    </Grid>
                    <Grid
                      size={{
                        xs: 2
                      }}>
                      { resource && resource.default_dice_size !== 1 &&
                        <SelectStringBox
                          name="Dice Size"
                          options={DICE_SIZES}
                          value={`${size}`}
                          disabled={disabled}
                          onChange={(changed: string) => {
                            const new_obj = obj;
                            const new_entry = new_obj.resources_table.getEntry(resource_cr);
                            if (new_entry) {
                              new_entry.data[`${field}_size`] = +changed;
                              setObj(new_obj);
                              setChangeTime(common_util.get_time_number());
                            }
                          }}
                        />
                      }
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <StringBox 
                        name="Count"
                        value={`${count}`}
                        type="number"
                        disabled={disabled}
                        onBlur={(changed: string) => {
                          const new_obj = obj;
                          const new_entry = new_obj.resources_table.getEntry(resource_cr);
                          if (new_entry) {
                            new_entry.data[field] = +changed;
                            setObj(new_obj);
                            setChangeTime(common_util.get_time_number());
                          }
                        }}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 3
                      }}>
                      <DeleteButton
                        label="Remove Resource"
                        disabled={disabled}
                        onClick={() => {
                          const new_obj = obj;
                          const new_entry = new_obj.resources_table.getEntry(resource_cr);
                          if (new_entry) {
                            delete new_entry.data[field];
                            delete new_entry.data[`${field}_size`];
                            const remove_field = new_obj.resources_table.entries.filter(o => o.data[field] !== undefined).length === 0;
                            if (remove_field) {
                              let indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                              let found = false;
                              for (let i = 0; i < Math.max(...indexes); ++i) {
                                if (found) {
                                  new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                } else {
                                  if (new_obj.resources_table.field_order[i] === field) {
                                    found = true;
                                  }
                                }
                              }
                              delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                              if (new_obj.resources_table.fields.includes(`${field}_size`)) {
                                indexes = Object.keys(new_obj.resources_table.field_order).map(o => +o).sort((a, b) => { return a - b; });
                                found = false;
                                for (let i = 0; i < Math.max(...indexes); ++i) {
                                  if (found) {
                                    new_obj.resources_table.field_order[i - 1] = new_obj.resources_table.field_order[i];
                                  } else {
                                    if (new_obj.resources_table.field_order[i] === `${field}_size`) {
                                      found = true;
                                    }
                                  }
                                }
                                delete new_obj.resources_table.field_order[new_obj.resources_table.fields.length - 1];
                              }
                            }
                            setObj(new_obj);
                            setChangeTime(common_util.get_time_number());
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                );
              })
            }
          </>
        }
      </Grid>
    );
  }

  const renderActions = () => {
    if (expanded_feature_base) {
      // Creature FBs are simple, and only ever have one F, and the FB's name and description match the FF.
      const feature = expanded_feature_base.features[0];
      if (feature) {
        const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")}
              feature_source={obj}
              disabled={disabled}
              cfs={creature_instance}
              simple
              allowed_feature_types={CREATURE_FEATURE_TYPES}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Actions";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setObj(new_obj);
                  setExpandedFeatureBase(null);
                  setChangeTime(common_util.get_time_number());
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      }
      return <>Error</>;
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Actions")} 
          feature_source={obj}
          // parent_id={myObj._id} 
          // parent_type="Race"
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Actions";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderLegendaryActions = () => {
    if (expanded_feature_base) {
      const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")}
            feature_source={obj}
            disabled={disabled}
            cfs={creature_instance}
            simple
            allowed_feature_types={CREATURE_FEATURE_TYPES}
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Legendary Actions";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const cr_options: string[] = [];
      for (let cr = obj.min_challenge_rating; cr <= obj.max_challenge_rating; ++cr) {
        cr_options.push(`${cr}`);
      }
      const use_entry = obj.legendaries_table.getEntry(legendaries_cr);
      // console.log(use_entry);
      const height = window.innerHeight - 64;
      return (
        <Grid container spacing={0} direction="row">
          { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
            (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
            <Grid
              size={{
                xs: 12
              }}>
              <LegendariesTableInput
                obj={obj.legendaries_table}
                disabled={disabled}
                creature
                onChange={(changed: LegendariesTable) => {
                  const new_obj = obj;
                  new_obj.legendaries_table.copy(changed);
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
          }
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox 
              name="Select CR for Legendaries"
              options={cr_options}
              value={`${legendaries_cr}`}
              onChange={(changed: string) => {
                // debugger;
                const new_obj = obj;
                const new_cr = +changed;
                const old_entry = new_obj.legendaries_table.getEntry(new_cr);
                if (!old_entry || old_entry.level !== new_cr) {
                  const entry = old_entry ? old_entry.clone() : new Legendaries();
                  entry.level = new_cr;
                  new_obj.legendaries_table.entries.push(entry);
                } else {
                  const entry = new Legendaries();
                  entry.level = new_cr;
                  new_obj.legendaries_table.entries.push(entry);
                }
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
                setLegendariesCR(new_cr);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !reloading &&
              <StringBox
                name="Legendary Actions"
                value={ use_entry ? `${use_entry.actions}` : "0" }
                type="number"
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
                  if (!old_entry) {
                    const entry = new Legendaries();
                    entry.level = legendaries_cr;
                    entry.actions = +changed;
                    new_obj.legendaries_table.entries.push(entry);
                  } else {
                    old_entry.actions = +changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              /> 
            }
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !reloading &&
              <StringBox
                name="Legendary Resistances"
                value={ use_entry ? `${use_entry.resistances}` : "0" }
                type="number"
                onChange={(changed: string) => {
                  const new_obj = obj;
                  const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
                  if (!old_entry) {
                    const entry = new Legendaries();
                    entry.level = legendaries_cr;
                    entry.resistances = +changed;
                    new_obj.legendaries_table.entries.push(entry);
                  } else {
                    old_entry.resistances = +changed;
                  }
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                  setReloading(true);
                }}
              />
            }
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <FeatureBasesInput 
              feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")} 
              feature_source={obj}
              // parent_id={myObj._id} 
              // parent_type="Race"
              disabled={disabled}
              onExpand={setExpandedFeatureBase}
              creature_instances={creature_instances}
              level_filter={level_filter}
              onLevelFilterChange={setLevelFilter}
              height={height}
              onAdd={() => {
                const new_obj = obj;
                const new_fb = new FeatureBase();
                new_fb.creature_fb_type = "Legendary Actions";
                new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
                new_fb.feature_source_id = new_obj._id;
                new_fb.feature_source_type = "creature";
                const new_f = new Feature();
                new_f.parent_type = new_fb.feature_source_type;
                new_f.parent_id = new_fb.feature_source_id;
                new_f.base_id = new_fb.id;
                new_fb.features.push(new_f);
                new_obj.fbs.push(new_fb);
                setObj(new_obj);
                setExpandedFeatureBase(new_fb);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        </Grid>
      );
    }
  }

  const renderLairActions = () => {
    
    if (expanded_feature_base) {
      const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")}
            feature_source={obj}
            disabled={disabled}
            cfs={creature_instance}
            simple
            allowed_feature_types={CREATURE_FEATURE_TYPES}
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Lair Actions";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")} 
          feature_source={obj}
          // parent_id={myObj._id} 
          // parent_type="Race"
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Lair Actions";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderTraits = () => {
    if (expanded_feature_base) {
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")}
            feature_source={obj}
            disabled={disabled}
            simple
            allowed_feature_types={CREATURE_FEATURE_TYPES}
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Traits";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                setObj(new_obj);
                setExpandedFeatureBase(null);
                setChangeTime(common_util.get_time_number());
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Traits")} 
          feature_source={obj}
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          creature_instances={creature_instances}
          level_filter={level_filter}
          onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Traits";
            new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj._id;
            new_fb.feature_source_type = "creature";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            setObj(new_obj);
            setExpandedFeatureBase(new_fb);
            setChangeTime(common_util.get_time_number());
          }}
        />
      );
    }
  }

  const renderParse = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6} style={{ maxHeight: "300px", overflowY: "scroll" }}>
          <StringBox 
            name="Parser"
            value={parse_string}
            // multiline
            onBlur={(changed: string) => {
              while (changed.includes("–")) {
                changed = changed.replace("–","-");
              }
              setParseString(changed);
            }}
          />
        </Grid>
        <Grid size={6}>
          <Button variant="contained" color="primary" className="padded"
            onClick={parse}>
            Parse
          </Button>
          <Button variant="contained" color="primary" className="padded"
            disabled={parse_creature === undefined}
            onClick={() => {
              if (parse_creature) {
                const new_obj = obj.clone();
                new_obj.copy(parse_creature);
                debugger
                setObj(new_obj);
              }
            }}>
            Use Parsed
          </Button>
        </Grid>
        {/* <Grid item xs={3}>
          <Button variant="contained" color="primary"
            onClick={reparse}>
            Reparse
          </Button>
        </Grid> */}
        <Grid size={12}
          style={{ maxHeight: "300px", overflowY: "scroll" }}>
          { parse_creature ?
            <DisplayStringWithReplacementsRecursive display_string={parsed_creature_text} cfs={parse_creature} />
          :
            <div ref={parseRef} dangerouslySetInnerHTML={{ __html: parse_string }}></div> 
          }
        </Grid>
      </Grid>
    );
  }

  const renderHP = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Hit Points Called"
            value={obj.hit_points_called}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.hit_points_called = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { !disabled &&
            <AddButton
              label="Hit Dice"
              label_separate
              onClick={ () => {
                const new_obj = obj;
                new_obj.hit_dice.push(new HitDiceUpgradable());
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <UpgradableNumberBox 
            name="Hit Dice Bonus"
            value={ obj.hit_dice_bonus }
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["CON"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.hit_dice_bonus = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox 
            name="Set Starting HP"
            value={ obj.starting_hp !== null }
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.starting_hp = changed ? new UpgradableNumber() : null;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { obj.starting_hp && 
            <UpgradableNumberBox 
              name="Starting HP"
              value={ obj.starting_hp }
              disabled={disabled}
              attached_table={obj.table}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.starting_hp = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !reloading && obj.hit_dice.map((hd, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <UpgradableNumberBox
                    name="Hit Dice Count"
                    value={hd.count} 
                    disabled={disabled}
                    attached_table={obj.table}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      const hd_finder = new_obj.hit_dice.filter(o => o.true_id === hd.true_id);
                      if (hd_finder.length === 1) {
                        hd_finder[0].count = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  Hit Dice Size is determined by Creature Size
                  {/* <SelectStringBox 
                    options={["d4","d6","d8","d10","d12","d20"]}
                    value={`d${hd.size}`} 
                    name="Hit Dice Size"
                    disabled={disabled}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const hd_finder = new_obj.hit_dice.filter(o => o.true_id === hd.true_id);
                      if (hd_finder.length === 1) {
                        hd_finder[0].size = +(changed.substring(1));
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  /> */}
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { !disabled &&
                    <DeleteButton
                      disabled={disabled}
                      onClick={ () => {
                        const new_obj = obj;
                        new_obj.hit_dice = new_obj.hit_dice.filter(o => o.true_id !== hd.true_id);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <UpgradableNumberBox 
            name="Finishing Blow"
            value={ obj.finishing_blow }
            disabled={disabled}
            attached_table={obj.table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.finishing_blow = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !disabled &&
            <AddButton
              label="Add Extra HP Pool"
              label_separate
              onClick={ () => {
                const new_obj = obj;
                new_obj.extra_hp_pools.push({ true_id: uuidv4().toString(), called: "", max_hp: new UpgradableNumber() });
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { !reloading && obj.extra_hp_pools.map((hpp, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <StringBox
                    name="HP Pool Called"
                    value={hpp.called} 
                    disabled={disabled}
                    onBlur={(changed: string) => {
                      const new_obj = obj;
                      const finder = new_obj.extra_hp_pools.find(o => o.true_id === hpp.true_id);
                      if (finder) {
                        finder.called = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 5
                  }}>
                  <UpgradableNumberBox
                    name="Max HP"
                    value={hpp.max_hp} 
                    disabled={disabled}
                    attached_table={obj.table}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      const finder = new_obj.extra_hp_pools.find(o => o.true_id === hpp.true_id);
                      if (finder) {
                        finder.max_hp = changed;
                      }
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { !disabled &&
                    <DeleteButton
                      disabled={disabled}
                      onClick={ () => {
                        const new_obj = obj;
                        new_obj.extra_hp_pools = new_obj.extra_hp_pools.filter(o => o.true_id !== hpp.true_id);
                        setObj(new_obj);
                        updateCreatureInstances(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    ); 
  }

  const renderMoreAttributes = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox 
            name="Spellcasting Ability"
            value={obj.spellcasting_ability} 
            options={ABILITY_SCORES}
            extra_options={["None"]}
            disabled={disabled}
            onChange={(changed: AbilityScoreType | "None") => {
              const new_obj = obj;
              new_obj.spellcasting_ability = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox
            name="Spell DC"
            value={ obj.dc } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DC INT","DC WIS","DC CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.dc = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Spell Attack"
            value={ obj.magic_attack } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["Attack INT","Attack WIS","Attack CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.magic_attack = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Non-Magic Attack"
            value={ obj.non_magic_attack } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["Attack STR","Attack DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.non_magic_attack = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Magic Damage Bonus"
            value={ obj.magic_dmg_bonus }
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["INT","WIS","CHA"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.magic_dmg_bonus = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Non-Magic Damage Bonus"
            value={ obj.non_magic_dmg_bonus } 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["STR","DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.non_magic_dmg_bonus = changed;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
      </Grid>
    ); 
  }

  const renderEvenMoreAttributes = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          style={{ color: "blue", fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          Strings
          <AddButton
            disabled={disabled}
            onClick={ () => {
              const new_obj = obj;
              new_obj.string_hash["New String"] = "";
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { Object.keys(obj.string_hash).map((hash_key, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name="Name"
                  value={ hash_key } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    if (changed !== "" && changed !== hash_key) {
                      const new_obj = obj;
                      new_obj.string_hash[changed] = obj.string_hash[hash_key];
                      delete new_obj.string_hash[hash_key];
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name={ hash_key }
                  value={ obj.string_hash[hash_key] } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    const new_obj = obj;
                    new_obj.string_hash[hash_key] = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DeleteButton
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    delete new_obj.string_hash[hash_key];
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
        <Grid
          style={{ color: "blue", fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          Upgradable Numbers
          <AddButton
            disabled={disabled}
            onClick={ () => {
              const new_obj = obj;
              new_obj.upgradable_hash["New Upgradable"] = new UpgradableNumber();
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { Object.keys(obj.upgradable_hash).map((hash_key, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 4
              }}>
              <Grid
                size={{
                  xs: 4
                }}>
                <StringBox 
                  name="Name"
                  value={ hash_key } 
                  disabled={disabled}
                  onBlur={(changed: string) => {
                    if (changed !== "" && changed !== hash_key) {
                      const new_obj = obj;
                      new_obj.upgradable_hash[changed] = obj.upgradable_hash[hash_key];
                      delete new_obj.upgradable_hash[hash_key];
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox 
                  name={ hash_key }
                  value={ obj.upgradable_hash[hash_key] } 
                  disabled={disabled}
                  attached_table={obj.table}
                  // no_formula_key
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.upgradable_hash[hash_key] = changed;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <DeleteButton
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    delete new_obj.upgradable_hash[hash_key];
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    ); 
  }

  const renderTable = () => {
    return (
      <Grid container spacing={0} direction="row">
        { !disabled &&
          <>
            { obj.table.entries.length === 0 ? 
              <Grid size={12}>
                <ButtonBox
                  name="Convert to Table Usage"
                  onClick={() => {
                    const new_obj = obj;
                    const entry = new TableEntry();
                    entry.level = obj.challenge_rating;
                    entry.data = {
                      "STR": obj.ability_scores.strength.base,
                      "DEX": obj.ability_scores.dexterity.base,
                      "CON": obj.ability_scores.constitution.base,
                      "INT": obj.ability_scores.intelligence.base,
                      "WIS": obj.ability_scores.wisdom.base,
                      "CHA": obj.ability_scores.charisma.base,
                    };
                    new_obj.ability_scores.strength.base = 0;
                    new_obj.ability_scores.strength.makeBasic();
                    new_obj.ability_scores.strength.table_type = "Attached";
                    new_obj.ability_scores.strength.table_id = "Attached";
                    new_obj.ability_scores.strength.table_field = "STR";
                    new_obj.ability_scores.dexterity.base = 0;
                    new_obj.ability_scores.dexterity.makeBasic();
                    new_obj.ability_scores.dexterity.table_type = "Attached";
                    new_obj.ability_scores.dexterity.table_id = "Attached";
                    new_obj.ability_scores.dexterity.table_field = "DEX";
                    new_obj.ability_scores.constitution.base = 0;
                    new_obj.ability_scores.constitution.makeBasic();
                    new_obj.ability_scores.constitution.table_type = "Attached";
                    new_obj.ability_scores.constitution.table_id = "Attached";
                    new_obj.ability_scores.constitution.table_field = "CON";
                    new_obj.ability_scores.intelligence.base = 0;
                    new_obj.ability_scores.intelligence.makeBasic();
                    new_obj.ability_scores.intelligence.table_type = "Attached";
                    new_obj.ability_scores.intelligence.table_id = "Attached";
                    new_obj.ability_scores.intelligence.table_field = "INT";
                    new_obj.ability_scores.wisdom.base = 0;
                    new_obj.ability_scores.wisdom.makeBasic();
                    new_obj.ability_scores.wisdom.table_type = "Attached";
                    new_obj.ability_scores.wisdom.table_id = "Attached";
                    new_obj.ability_scores.wisdom.table_field = "WIS";
                    new_obj.ability_scores.charisma.base = 0;
                    new_obj.ability_scores.charisma.makeBasic();
                    new_obj.ability_scores.charisma.table_type = "Attached";
                    new_obj.ability_scores.charisma.table_id = "Attached";
                    new_obj.ability_scores.charisma.table_field = "CHA";
                    if (obj.hit_dice.length === 1 && obj.hit_dice[0].count.isBasic()) {
                      entry.data["Hit Dice Count"] = Math.floor(obj.hit_dice[0].count.base);
                      new_obj.hit_dice[0].count.base = 0;
                      new_obj.hit_dice[0].count.table_type = "Attached";
                      new_obj.hit_dice[0].count.table_id = "Attached";
                      new_obj.hit_dice[0].count.table_field = "Hit Dice Count";
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      entry.data["Armor Class"] = obj.armor_class.base;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    entry.data["Size"] = obj.size_ugn.base;
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    new_obj.table.entries.push(entry);
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Hit Dice Count") ?
              <Grid size={12}>
                <ButtonBox
                  name="Add Missing Fields"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      let count = 0;
                      if (new_obj.hit_dice.length > 0) {
                        count = new_obj.hit_dice[0].count.base;
                        if (!new_obj.hit_dice[0].count.isBasic()) {
                          // if it's not basic it's usually just a CR multiplier,
                          // and we don't want to worry about anything else.
                          count += new_obj.hit_dice[0].count.add_char_level_mult * entry.level;
                        }
                      }
                      entry.data["Hit Dice Count"] = Math.floor(count);
                      if (new_obj.armor_class.add_char_level_mult > 0) {
                        entry.data["Armor Class"] = new_obj.armor_class.base + new_obj.armor_class.add_char_level_mult * entry.level;
                      } else {
                        entry.data["Armor Class"] = new_obj.armor_class.base;
                      }
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    if (obj.hit_dice.length === 1) {
                      new_obj.hit_dice[0].count.makeBasic();
                      new_obj.hit_dice[0].count.base = 0;
                      new_obj.hit_dice[0].count.table_type = "Attached";
                      new_obj.hit_dice[0].count.table_id = "Attached";
                      new_obj.hit_dice[0].count.table_field = "Hit Dice Count";
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      new_obj.armor_class.add_char_level_mult = 0;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";

                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Armor Class") ?
              <Grid size={12}>
                <ButtonBox
                  name="Add Missing Fields"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      if (new_obj.armor_class.add_char_level_mult > 0) {
                        entry.data["Armor Class"] = new_obj.armor_class.base + new_obj.armor_class.add_char_level_mult * entry.level;
                      } else {
                        entry.data["Armor Class"] = new_obj.armor_class.base;
                      }
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    if (obj.armor_class.isBasicExcept(["add_ability_mod_mult","add_ability_scores"])) {
                      new_obj.armor_class.add_char_level_mult = 0;
                      new_obj.armor_class.base = 0;
                      new_obj.armor_class.table_type = "Attached";
                      new_obj.armor_class.table_id = "Attached";
                      new_obj.armor_class.table_field = "Armor Class";
                    } 
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            : !obj.table.fields.includes("Size") &&
              <Grid size={12}>
                <ButtonBox
                  name="Add Missing Field"
                  onClick={() => {
                    const new_obj = obj;
                    for (const entry of new_obj.table.entries) {
                      entry.data["Size"] = obj.size_ugn.base;
                    }
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                    
                    new_obj.table.field_order = {
                      0: "STR", 1: "DEX", 2: "CON", 3: "INT", 4: "WIS", 5: "CHA", 6: "Hit Dice Count", 7: "Armor Class", 8: "Size"
                    };
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
          </>
        }
        {/* { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
          (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) &&  */}
          <Grid size={12}>
            <TableInput
              obj={obj.table}
              disabled={disabled}
              creature
              onChange={(changed: TableBase) => {
                const new_obj = obj;
                new_obj.table.copyBase(changed);
                setObj(new_obj);
                updateCreatureInstances(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        {/* } */}
      </Grid>
    ); 
  }

  const renderMain = () => {
    // console.log(obj.alignment);
    // console.log(CREATURE_ALIGNMENTS);
    return (
      <Grid container spacing={0} direction="row">
        {/* Names and Alignment */}
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Name"
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Name Invalid"} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.name = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Small Name"
            value={obj.small_name} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.small_name = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox 
            name="Title"
            value={obj.title} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.title = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox 
            name="Alignment"
            options={CREATURE_ALIGNMENTS}
            value={obj.alignment} 
            disabled={disabled}
            // log_it
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.alignment = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        {/* Types */}
        <Grid size={4}>
          <SelectStringBox 
            name="Creature Type" 
            options={ CREATURE_TYPES }
            value={obj.creature_type} 
            disabled={disabled}
            onChange={(changed: CreatureType) => {
              const new_obj = obj;
              new_obj.creature_type = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }} 
          />
        </Grid>
        <Grid size={4}>
          <SuggestionStringBox 
            name="Subtypes"
            field="subtypes"
            values={obj.subtypes}
            disabled={disabled}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.subtypes = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={4}>
          <CheckBox 
            name="Swarm" 
            value={obj.swarm} 
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.swarm = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }} 
          />
        </Grid>
        <Grid size={6}>
          <SelectObjectBox 
            name="Group"
            data_type="creature_group"
            value={obj.group_id}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.group_id = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={6}>
          <SelectObjectBox 
            name="Classes"
            data_type="game_class"
            values={obj.class_ids}
            disabled={disabled}
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj;
              new_obj.class_ids = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        {/* Age and Size */}
        <Grid size={3}>
          <SuggestionStringBox 
            name="Age Category"
            field="age_category"
            value={obj.age_category}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.age_category = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={9}> 
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: obj.size_ugn.isBasic() ? 12 : 6
              }}>
              <SelectStringBox 
                name="Size"
                options={SIZE_OPTIONS}
                extra_options={["Use Upgradable"]}
                value={!obj.size_ugn.isBasic() ? "Use Upgradable" : `${obj.size_ugn.base}`} 
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  if (changed === "Use Upgradable") {
                    new_obj.size_ugn.base = 0;
                    new_obj.size_ugn.table_type = "Attached";
                    new_obj.size_ugn.table_id = "Attached";
                    new_obj.size_ugn.table_field = "Size";
                  } else {
                    new_obj.size_ugn.makeBasic();
                    new_obj.size_ugn.base = +changed;
                  }
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
            </Grid>
            { !obj.size_ugn.isBasic() &&
              <Grid
                size={{
                  xs: 6
                }}>
                <UpgradableNumberBox
                  name="Size Upgradable"
                  value={ obj.size_ugn } 
                  disabled={disabled}
                  attached_table={obj.table}
                  // no_formula_key
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj;
                    new_obj.size_ugn = changed;
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
          </Grid>
        </Grid>
        {/* Init and AC */}
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Initiative Modifier"
            value={obj.initiative_modifier} 
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.initiative_modifier = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <UpgradableNumberBox 
            name="Armor Class"
            value={obj.armor_class}
            disabled={disabled}
            attached_table={obj.table}
            quick_options={["DEX"]}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.armor_class = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="AC Flavor"
            value={obj.ac_flavor} 
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.ac_flavor = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { renderChallengeRatings() }
        {/* ModelBaseDetails */}
        <Grid
          size={{
            xs: 12
          }}>
          <ModelBaseDetails
            obj={obj}
            no_grid
            disabled={disabled}
            change_time={change_time}
            onChange={(changed: ModelBase) => {
              if (changed instanceof Creature) {
                const new_obj = obj;
                new_obj.copy(changed);
                setObj(new_obj);
                updateCreatureInstances(new_obj);
                setChangeTime(common_util.get_time_number());
              }
            }}
            // get_creature_homebrewery_string={get_creature_homebrewery_string}
            onSubmit={submit}
            processing={processing}
          />
        </Grid>
      </Grid>
    ); 
  }

  const renderChallengeRatings = () => {
    // console.log("Rendering");
    return (
      <>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Default Challenge Rating"
            value={`${obj.challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (new_obj.challenge_rating === new_obj.min_challenge_rating && 
                new_obj.min_challenge_rating === new_obj.max_challenge_rating) {
                new_obj.min_challenge_rating = val;
                new_obj.max_challenge_rating = val;
              } else {
                if (val < new_obj.min_challenge_rating) {
                  new_obj.min_challenge_rating = val;
                } else if (val > new_obj.max_challenge_rating) {
                  new_obj.max_challenge_rating = val;
                }
              }
              new_obj.challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              // setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Min Challenge Rating"
            value={`${obj.min_challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (val >= new_obj.max_challenge_rating) {
                new_obj.challenge_rating = val;
                new_obj.max_challenge_rating = val;
                new_obj.name_table = [];
              } else if (val > new_obj.challenge_rating) {
                new_obj.challenge_rating = val;
              }
              new_obj.min_challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <StringBox 
            name="Max Challenge Rating"
            value={`${obj.max_challenge_rating}`} 
            type="number"
            disabled={disabled}
            change_time={change_time}
            onBlur={(changed: string) => {
              const new_obj = obj;
              const val = +changed;
              if (val <= new_obj.min_challenge_rating) {
                new_obj.challenge_rating = val;
                new_obj.min_challenge_rating = val;
                new_obj.name_table = [];
              } else if (val < new_obj.challenge_rating) {
                new_obj.challenge_rating = val;
              }
              new_obj.max_challenge_rating = val;
              setObj(new_obj);
              updateCreatureInstances(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { obj.min_challenge_rating !== obj.max_challenge_rating && 
          <>
            <Grid
              size={{
                xs: 12
              }}>
              <AddButton
                disabled={disabled || (obj.name_table.length > 0 && Math.max(...obj.name_table.map(o => o.level)) === obj.max_challenge_rating)}
                onClick={() => {
                  const new_obj = obj;
                  if (new_obj.name_table.length === 0) {
                    new_obj.name_table.push({ level: new_obj.min_challenge_rating, name: new_obj.name, age_category: new_obj.age_category });
                  } else {
                    const max = Math.max(...new_obj.name_table.map(o => o.level));
                    const entry = new_obj.name_table.filter(o => o.level === max)[0]; // I already know it exists
                    let level = entry.level;
                    switch (level) {
                      case 0: level = 1/8; break;
                      case 0.125: level = 1/4; break;
                      case 0.25: level = 1/2; break;
                      case 0.5: level = 1; break;
                      default: level++; break;
                    }
                    new_obj.name_table.push({ level, name: entry.name, age_category: entry.age_category });
                  }
                  setObj(new_obj);
                  updateCreatureInstances(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}
              />
              &nbsp;
              Names by CR
            </Grid>
            { !reloading && obj.name_table.sort((a,b) => { return a.level - b.level; }).map((name_entry, key) => {
              const other_named_crs = obj.name_table.filter(o => o.level !== name_entry.level).map(o => o.level);
              const cr_options = CR_OPTIONS.filter(o => 
                +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating && 
                !other_named_crs.includes(+o)
              );
              return (
                <Grid
                  key={key}
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <SelectStringBox
                      name="Starting at CR"
                      value={`${name_entry.level}`}
                      options={cr_options}
                      disabled={disabled}
                      onChange={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.level = +changed;
                        }
                        setObj(new_obj);
                        updateCreatureInstances(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <StringBox
                      name="Name"
                      value={name_entry.name}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.name = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <StringBox
                      name="Age Category"
                      value={name_entry.age_category}
                      disabled={disabled}
                      onBlur={(changed: string) => {
                        const new_obj = obj;
                        const entry = new_obj.name_table.find(o => o.level === name_entry.level);
                        if (entry) {
                          entry.age_category = changed;
                        }
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DeleteButton
                      disabled={disabled}
                      onClick={() => {
                        const new_obj = obj;
                        new_obj.name_table = new_obj.name_table.filter(o => o.level !== name_entry.level);
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                        setReloading(true);
                      }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </>
        }
      </>
    );
  }

  const blurg = () => {
    // const get_creature_homebrewery_string = (ci: CreatureInstance | undefined = undefined) => {
    //   const creature = obj;
    //   let str = `# ${ci ? ci.name : creature.true_name}\n\r___\n\r`;
    //   let ac = "See Properties Table";
    //   let hp = "See Properties Table";
    //   let ability_scores_string = "";
    //   let saving_throws = "";
    //   let skills = "";
    //   let senses_string = "";
    //   let tools = "";
    //   let immunities_string = "";
    //   let resistances_string = "";
    //   let vulnerabilities_string = "";
    //   let condition_immunities_string = "";
    //   let languages = "";
    //   let cr_string = "";
    //   let prof_bonus = "See Properties Table";
    //   let table_string = "";
    //   let resource_table_string = "";
    //   const legendaries_table_string = "";
    //   let attributes_table_string = "";
    //   let size = ci ? `${ci.size} ` : creature.size_ugn.isBasic() ? `${SIZE_MAP[creature.size_ugn.base]} ` : "";
    //   let one_resources_entry: TableEntry | null = null;
    //   if (ci) {
    //     ac = `${ci.armor_class}`;
    //     hp = `${creature.hit_dice_string_for_cr(ci.challenge_rating)}`;
    //     saving_throws = ci.saving_throws_string;
    //     skills = ci.skills_string;
    //     senses_string = ci.senses_string;
    //     tools = ci.tools_string;
    //     immunities_string = ci.immunities_string;
    //     resistances_string = ci.resistances_string;
    //     vulnerabilities_string = ci.vulnerabilities_string;
    //     condition_immunities_string = ci.condition_immunities_string;
    //     languages = ci.languages;
    //     cr_string = `<b>Challenge</b> ${ci.challenge_rating} (${common_util.cr_to_xp_map[ci.challenge_rating]} XP)\n\r___\n\r`;
    //     prof_bonus = `+${ci.proficiency_modifier}`;
    //     ability_scores_string = `<table><tr><th>STR</th><th>DEX</th><th>CON</th></tr>
    //       <tr>
    //         <td>${ci.current_ability_scores.strength} (${common_util.addPlusMaybe(ci.current_ability_scores.STR)})</td>
    //         <td>${ci.current_ability_scores.dexterity} (${common_util.addPlusMaybe(ci.current_ability_scores.DEX)})</td>
    //         <td>${ci.current_ability_scores.constitution} (${common_util.addPlusMaybe(ci.current_ability_scores.CON)})</td>
    //       </tr>
    //       <tr><th>INT</th><th>WIS</th><th>CHA</th></tr>
    //       <tr>
    //         <td>${ci.current_ability_scores.intelligence} (${common_util.addPlusMaybe(ci.current_ability_scores.INT)})</td>
    //         <td>${ci.current_ability_scores.wisdom} (${common_util.addPlusMaybe(ci.current_ability_scores.WIS)})</td>
    //         <td>${ci.current_ability_scores.charisma} (${common_util.addPlusMaybe(ci.current_ability_scores.CHA)})</td>
    //       </tr>
    //       </table>`;
    //   } else {
    //     const attributes = creature.attributes_table.entries[0];
    //     if (creature.attributes_table.entries.length === 1) {
    //       saving_throws = attributes.saving_throws_string;
    //       skills = attributes.skills_string;
    //       senses_string = attributes.senses_string;
    //       tools = attributes.tools_string;
    //       immunities_string = attributes.immunities_string;
    //       resistances_string = attributes.resistances_string;
    //       vulnerabilities_string = attributes.vulnerabilities_string;
    //       condition_immunities_string = attributes.condition_immunities_string;
    //       languages = attributes.languages;
    //     } else if (attributes) {
    //       const unchanged: string[] = [];
    //       const attributes_table = creature.attributes_table;
    //       if (attributes_table.save_profs_unchanged) {
    //         unchanged.push("save_profs");
    //         saving_throws = attributes.saving_throws_string;
    //       } else {
    //         saving_throws = "See Attributes Table";
    //       }
    //       // console.log(attributes_table.skill_profs_unchanged);
    //       if (attributes_table.skill_profs_unchanged) {
    //         unchanged.push("skill_profs");
    //         skills = attributes.skills_string;
    //       } else {
    //         skills = "See Attributes Table";
    //       }
    //       if (attributes_table.tool_profs_unchanged) {
    //         unchanged.push("tool_profs");
    //         tools = attributes.tools_string;
    //       } else {
    //         tools = "See Attributes Table";
    //       }
    //       if (attributes_table.damage_immunities_unchanged) {
    //         unchanged.push("damage_immunities");
    //         immunities_string = attributes.immunities_string;
    //       } else {
    //         immunities_string = "See Attributes Table";
    //       }
    //       if (attributes_table.damage_resistances_unchanged) {
    //         unchanged.push("damage_resistances");
    //         resistances_string = attributes.resistances_string;
    //       } else {
    //         resistances_string = "See Attributes Table";
    //       }
    //       if (attributes_table.damage_vulnerabilities_unchanged) {
    //         unchanged.push("damage_vulnerabilities");
    //         vulnerabilities_string = attributes.vulnerabilities_string;
    //       } else {
    //         vulnerabilities_string = "See Attributes Table";
    //       }
    //       if (attributes_table.condition_immunities_unchanged) {
    //         unchanged.push("condition_immunities");
    //         condition_immunities_string = attributes.condition_immunities_string;
    //       } else {
    //         condition_immunities_string = "See Attributes Table";
    //       }
    //       if (attributes_table.languages_unchanged) {
    //         unchanged.push("languages");
    //         languages = attributes.languages;
    //       } else {
    //         languages = "See Attributes Table";
    //       }
    //       if (attributes_table.senses_unchanged) {
    //         unchanged.push("senses");
    //         senses_string = attributes.senses_string;
    //       } else {
    //         senses_string = "See Attributes Table";
    //       }
          
    //       const attribute_fields = attributes_table.all_attributes.filter(o => !unchanged.includes(o));
    //       if (attribute_fields.length > 0) {
    //         // Show the table.
    //         const attribute_map: { [key: string]: string } = {
    //           "damage_immunities": "Immunities",
    //           "damage_resistances": "Resistances",
    //           "damage_vulnerabilities": "Vulnerabilities",
    //           "condition_immunities": "Condition Immunities",
    //           "senses": "Senses",
    //           "skill_profs": "Skill Proficiencies",
    //           "save_profs": "Save Proficiencies",
    //           "speeds": "Speeds",
    //           "tool_profs": "Tool Proficiencies",
    //           "languages": "Languages"
    //         };
    //         attributes_table_string += "<b>Attributes by CR</b>\n\r";
    //         const add_attributes_table = (using_fields: string[]) => {
    //           attributes_table_string += "<table>\n\r";
    //           attributes_table_string += `<tr><th>CR</th>${using_fields.map(o => { 
    //             return "<th>" + attribute_map[o] + "</th>"; 
    //           }).join("")}</tr>\n\r`;
    //           for (const entry of attributes_table.entries.sort((a, b) => { return a.level - b.level; })) {
    //             attributes_table_string += "<tr>";
    //             attributes_table_string += "<td>" + entry.level + "</td>";
    //             attributes_table_string += using_fields.map(field => { 
    //               const data = (entry as any)[field];
    //               let str = "";
    //               if (typeof data === "object") {
    //                 if (data.length) {
    //                   str = data.map((o: any) => `${o}`).join(", ");
    //                 } else {
    //                   const keys = Object.keys(data);
    //                   if (field === "skill_profs") {
    //                     str = keys.map(key => {
    //                       const skill = SKILLS.find(o => o._id === key);
    //                       const prof = +data[key];
    //                       if (skill) {
    //                         return `${skill.name}${prof > 1 ? " (Expert)" : ""}`;
    //                       }
    //                       return "";
    //                     }).filter(o => o !== "").join("\n\r___\n\r");
    //                   } else {
    //                     str = keys.map(key => {
    //                       return `${key}: ${data[key]}`;
    //                     }).join("\n\r___\n\r");
    //                   }
    //                 }
    //               } else {
    //                 str = `${data}`;
    //               }
    //               return "<td>" + str + "</td>"; 
    //             }).join("");
    //             attributes_table_string += "</tr>\n\r";
    //           }
    //           attributes_table_string += "</table>\n\r___\n\r";
    //         }
    //         switch (attribute_fields.length) {
    //           case 6: 
    //             if (true) {
    //               const fields1 = attribute_fields.slice(0, 2);
    //               const fields2 = attribute_fields.slice(2);
    //               add_attributes_table(fields1); 
    //               attributes_table_string += "<b>More Attributes by CR</b>\n\r";
    //               add_attributes_table(fields2); 
    //             }
    //           break;
    //           case 7: case 8:
    //             if (true) {
    //               const fields1 = attribute_fields.slice(0, 3);
    //               const fields2 = attribute_fields.slice(3);
    //               add_attributes_table(fields1); 
    //               attributes_table_string += "<b>More Attributes by CR</b>\n\r";
    //               add_attributes_table(fields2); 
    //             }
    //           break;
    //           case 9: case 10:
    //             if (true) {
    //               const fields1 = attribute_fields.slice(0, 2);
    //               const fields2 = attribute_fields.slice(2, 5);
    //               const fields3 = attribute_fields.slice(5);
    //               add_attributes_table(fields1); 
    //               attributes_table_string += "<b>More Attributes by CR</b>\n\r";
    //               add_attributes_table(fields2); 
    //               attributes_table_string += "<b>Even More Attributes by CR</b>\n\r";
    //               add_attributes_table(fields3); 
    //             }
    //           break;
    //           default: add_attributes_table(attribute_fields); break;
    //         }
    //       }
    //     }
    //     // Show the table.
    //     let table = creature.table;

    //     if (table.entries.length > 0 && table.fields.length > 0) {
    //       let show_size = false;
    //       if (table.fields.includes("Size")) {
    //         const sizes = table.entries.map(o => o.data["Size"]);
    //         const min_size = Math.min(...sizes);
    //         const max_size = Math.max(...sizes);
    //         if (min_size === max_size) {
    //           size = SIZE_MAP[min_size];
    //         } else {
    //           show_size = true;
    //         }
    //       }
    //       const fields = table.fields.filter(o => o !== "Armor Class" && o !== "Size");
    //       table_string = "<b>Ability Scores by CR</b>\n\r___\n\r";
    //       table_string += "<table>\n\r";
    //       table_string += `<tr><th>CR</th>${fields.map(o => { 
    //         if (o.length < 4) {
    //           return "<th>&nbsp;" + o + "&nbsp;</th>"; 
    //         } else {
    //           return "<th>" + o + "</th>"; 
    //         }
    //       }).join("")}<th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
    //       for (const entry of table.entries.filter(o => 
    //         o.level >= creature.min_challenge_rating && 
    //         o.level <= creature.max_challenge_rating).sort((a, b) => {
    //         return a.level - b.level;
    //       })) {
    //         // console.log(entry);
    //         table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
    //       }
    //       table_string += "</table>\n\r___\n\r";
    //       table_string = "<b>Creature Properties by CR</b>\n\r___\n\r";
    //       table_string += "<table>\n\r";
    //       table_string += `<tr><th>CR</th><th>Proficiency Bonus</th><th>Max HP</th><th>AC</th>${show_size ? "<th>Size</th>" : ""}</tr>\n\r`;
    //       let ac_of_others = 0;
    //       for (const entry of table.entries.filter(o => 
    //         o.level >= creature.min_challenge_rating && 
    //         o.level <= creature.max_challenge_rating).sort((a, b) => {
    //         return a.level - b.level;
    //       })) {
    //         // console.log(entry);
    //         table_string += `<tr><td>${entry.level}</td>`;
    //         const ac_for_cr = creature.ac_for_cr(entry.level);
    //         if (ac_of_others === 0) {
    //           ac_of_others = ac_for_cr;
    //         } else if (ac_of_others > 0 && ac_of_others !== ac_for_cr) {
    //           ac_of_others = -1;
    //         }
    //         table_string += `<td>${creature.get_proficiency_modifier(entry.level)}</td><td>${creature.max_hp_for_cr(entry.level)}</td><td>${ac_for_cr}</td>${show_size ? "<td>" + SIZE_MAP[entry.data["Size"]] + "</td>" : ""}</tr>\n\r`;
    //       }
    //       if (ac_of_others > 0) {
    //         ac = `${ac_of_others}`;
    //       }
    //       table_string += "</table>\n\r___\n\r";
    //     } else {
    //       table_string += "TABLE NOT FOUND!\n\r___\n\r";
    //     }

    //     table = creature.resources_table;

    //     if (table.entries.length > 1 && table.fields.length > 0) {
    //       const fields = table.fields.filter(o => !o.endsWith("_size"));
    //       resource_table_string = "<b>Resources by CR</b>\n\r___\n\r";
    //       resource_table_string += "<table>\n\r";
    //       resource_table_string += `<tr><th>CR</th>${fields.map(o => { 
    //         if (o.length < 4) {
    //           return "<th>&nbsp;" + o + "&nbsp;</th>"; 
    //         } else {
    //           return "<th>" + o + "</th>"; 
    //         }
    //       }).join("")}</tr>\n\r`;
    //       for (const entry of table.entries.filter(o => 
    //         o.level >= creature.min_challenge_rating && 
    //         o.level <= creature.max_challenge_rating).sort((a, b) => {
    //         return a.level - b.level;
    //       })) {
    //         resource_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
    //       }
    //       resource_table_string += "</table>\n\r___\n\r";
    //     } else if (table.entries.length === 1) {
    //       one_resources_entry = table.entries[0];
    //     }

    //     // if (creature.legendaries_table.entries.length > 1) {
    //     //   legendaries_table_string = "<b>Legendaries by CR</b>\n\r___\n\r";
    //     //   legendaries_table_string += "<table>\n\r";
    //     //   legendaries_table_string += `<tr><th>CR</th>${fields.map(o => { 
    //     //     if (o.length < 4) {
    //     //       return "<th>&nbsp;" + o + "&nbsp;</th>"; 
    //     //     } else {
    //     //       return "<th>" + o + "</th>"; 
    //     //     }
    //     //   }).join("")}</tr>\n\r`;
    //     //   for (const entry of table.entries.filter(o => 
    //     //     o.level >= creature.min_challenge_rating && 
    //     //     o.level <= creature.max_challenge_rating).sort((a, b) => {
    //     //     return a.level - b.level;
    //     //   })) {
    //     //     legendaries_table_string += `<tr><td>${entry.level}</td>${fields.map(o => { return "<td>" + entry.data[o] + "</td>" }).join("")}</tr>\n\r`;
    //     //   }
    //     //   legendaries_table_string += "</table>\n\r___\n\r";
    //     // } else if (table.entries.length === 1) {
    //     //   one_resources_entry = table.entries[0];
    //     // }
    //   }
    //   str += `<style>
    //     div.hr {
    //       width: 0; 
    //       height: 0; 
    //       border-top: 5px solid transparent;
    //       border-bottom: 5px solid transparent;
    //       border-left: 300px solid brown;
    //       }
    //     td {
    //       justify-content: center;
    //       text-align: center;
    //       align-items: center;
    //     }
    //     th {
    //       font-weight: bold
    //     }
    //     b {
    //       font-weight: bold
    //     }
    //     </style>`;
    //   str += `${size}${creature.creature_type}${creature.alignment === "" ? "" : ", " + creature.alignment}\n\r___\n\r`;
    //   str += `<div class="hr"></div>\n\r___\n\r`;
    //   str += `<b>Armor Class</b> ${ac} ${creature.ac_flavor.length > 0 ? "(" + creature.ac_flavor + ")" : ""}\n\r___\n\r`;
    //   str += `<b>Hit Points</b> ${hp}\n\r___\n\r`;
    //   str += `<b>Speed</b> ${creature.speed_string}\n\r___\n\r`;
    //   str += `<div class="hr"></div>\n\r___\n\r`;
    //   if (ability_scores_string.length > 0) {
    //     str += ability_scores_string;
    //     str += `<div class="hr"></div>\n\r___\n\r`;
    //   }
    //   if (saving_throws.length > 0) {
    //     str += `<b>Saving Throws</b> ${saving_throws}\n\r___\n\r`;
    //   }
    //   if (skills.length > 0) {
    //     str += `<b>Skills</b> ${skills}\n\r___\n\r`;
    //   }
    //   if (tools.length > 0) {
    //     str += `<b>Tools</b> ${tools}\n\r___\n\r`;
    //   }
    //   if (immunities_string.length > 0) {
    //     str += `<b>Damage Immunities</b> ${immunities_string}\n\r___\n\r`;
    //   }
    //   if (resistances_string.length > 0) {
    //     str += `<b>Damage Resistances</b> ${resistances_string}\n\r___\n\r`;
    //   }
    //   if (vulnerabilities_string.length > 0) {
    //     str += `<b>Damage Vulnerabilities</b> ${vulnerabilities_string}\n\r___\n\r`;
    //   }
    //   if (condition_immunities_string.length > 0) {
    //     str += `<b>Condition Immunities</b> ${condition_immunities_string}\n\r___\n\r`;
    //   }
    //   if (senses_string.length > 0) {
    //     str += `<b>Senses</b> ${senses_string}\n\r___\n\r`;
    //   }
    //   if (languages.length > 0) {
    //     str += `<b>Languages</b> ${languages}\n\r___\n\r`;
    //   }
    //   str += cr_string;
    //   str += `<b>Proficiency Bonus</b> ${prof_bonus}\n\r___\n\r`;
    //   str += `<div class="hr"></div>\n\r___\n\r`;
    //   str += get_homebrewery_fbs_string(ci, one_resources_entry);
    //   if (creature.description.length > 0) {
    //     str += "## Description\n\r___\n\r";
    //     str += `${creature.description}\n\r___\n\r`;
    //   }
    //   if (table_string.length > 0 || resource_table_string.length > 0 || attributes_table_string.length > 0 || legendaries_table_string.length > 0) {
    //     str += "\\page\n\r";
    //     str += "## Tables\n\r___\n\r";
    //     if (table_string.length > 0 || resource_table_string.length > 0) {
    //       str += table_string;
    //       str += resource_table_string;
    //       if (attributes_table_string.length > 0) {
    //         str += "\\column\n\r";
    //         str += attributes_table_string;
    //       }
    //     } else if (attributes_table_string.length > 0) {
    //       str += attributes_table_string;
    //     }
    //     str += legendaries_table_string;
    //   }
    //   return str;
    // }

    // const get_homebrewery_fbs_string = (ci: CreatureInstance | undefined, one_resources_entry: TableEntry | null) => {
    //   const creature = obj;
    //   let str = "";
    //   let fbs = ci ? creature.fbs.filter(o => o.level <= ci.challenge_rating) : creature.traits;
    //   if (fbs.length > 0 || (creature.legendaries_table.entries.length > 0 && (!ci || creature.legendary_resistances_for_cr(ci.challenge_rating) > 0))) {
    //     str += "## Traits\n\r___\n\r";
    //     if (ci) {
    //       for (const fb of fbs) {
    //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //       }
    //     } else {
    //       const crs = (common_util.unique_array(fbs.map(o => +o.level))).sort((a, b) => { return a - b; });
    //       if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
    //         for (const fb of fbs) {
    //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //         }
    //       } else if (crs.length > 0) {
    //         for (const cr of crs) {
    //           str += `### Beginning At CR ${cr}\n\r___\n\r`;
    //           for (const fb of fbs.filter(o => o.level === cr)) {
    //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //           }
    //         }
    //       }
    //     }
    //     // if (creature.legendaries_table.entries.length > 0) {
    //     //   if (ci) {
    //     //     const legendary_resistances = creature.legendary_resistances_for_cr(ci.challenge_rating);
    //     //     if (legendary_resistances > 0) {
    //     //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
    //     //     }
    //     //   } else {
    //     //     let legendary_resistances = ""; 
    //     //     if (creature.legendaries_table.entries.length === 1 && 
    //     //       creature.legendaries_table.entries[0].level <= creature.min_challenge_rating) {
    //     //       legendary_resistances = `${creature.legendaries_table.entries[0].data["Legendary Resistances"]}`;
    //     //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
    //     //     } else if (creature.legendaries_table.entries.length === 1) {
    //     //       str += `### Beginning At CR ${creature.legendaries_table.entries[0].level}\n\r___\n\r`;
    //     //       legendary_resistances = `${creature.legendaries_table.entries[0].data["Legendary Resistances"]}`;
    //     //       str += `<b>Legendary Resistance (${legendary_resistances}/Day).</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it can choose to succeed instead.\n\r___\n\r`;
    //     //     } else {
    //     //       str += `<b>Legendary Resistance.</b> If ${creature.small_name === "" ? creature.name : "the " + creature.small_name} fails a saving throw, it may be able to choose to succeed instead.  See the Legendaries Table to see how many uses per day if any it has of this feature.\n\r___\n\r`;
    //     //     }
    //     //   }
    //     // }
    //   }
    //   fbs = ci ? creature.action_fbs.filter(o => o.level <= ci.challenge_rating) : creature.action_fbs;
    //   if (fbs.length > 0 || creature.spellcasting_table.entries.length > 0) {
    //     str += "## Actions\n\r___\n\r";
    //     if (ci) {
    //       const spellcasting_entry = creature.spellcasting_table.getEntry(ci.challenge_rating);
    //       if (ci.spellcasting_description.length > 0 && spellcasting_entry) {
    //         str += `<b>Spellcasting.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: ci.spellcasting_description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //         const resources_entry = ci ? creature.resources_table.getEntry(ci.challenge_rating) : one_resources_entry;
    //         const ci_spells = ci.use_spells;
    //         for (const level of ci.spell_levels.sort((a, b) => { return a - b; })) {
    //           const spells_of_level = ci_spells.filter(o => o.spell_level === level);
    //           if (spells_of_level.length > 0) {
    //             if (level === 0) {
    //               let level_string = "At will: ";
    //               for (const spell_of_level of spells_of_level) {
    //                 const spell_id = spell_of_level.spell_id;
    //                 const spell = spells.find(o => o._id === spell_id);
    //                 if (spell) {
    //                   if (level_string.length > 9) {
    //                     level_string += ", ";
    //                   }
    //                   level_string += spell.name;
    //                 }
    //               }
    //               str += level_string + "\n\r___\n\r";
    //             } else {
    //               const cast_types = common_util.unique_array(spells_of_level.map(o => o.cast_type));
    //               for (const cast_type of cast_types) {
    //                 let level_string = "";
    //                 switch (cast_type) {
    //                   case "Slot": 
    //                     // Check how many slots of this level they have
    //                     let level_name = "";
    //                     switch(level) {
    //                       case 1: level_name = "1st"; break;
    //                       case 2: level_name = "2nd"; break;
    //                       case 3: level_name = "3rd"; break;
    //                       default: level_name = `${level}th`; break;
    //                     }
    //                     const slots_of_level = resources_entry && Object.keys(resources_entry.data).includes(`Slot: ${level}`) ? resources_entry.data[ `Slot: ${level}`] : 0;
    //                     if (slots_of_level) {
    //                       level_string += `${level_name} level (${slots_of_level}):`;
    //                     } else if (!resources_entry) {
    //                       level_string += `${level_name} level (found in the Resources table):`;
    //                     }
    //                   break;
    //                   case "At Will":
    //                     level_string = "At will: ";
    //                   break;
    //                   case "Ritual": 
    //                     level_string = "As a Ritual: ";
    //                   break;
    //                   default:
    //                     if (spellcasting_entry) {
    //                       // Check if they're all the same.  They usually are.
    //                       const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spells_of_level[0].spell_id)[0].x;
    //                       if (x_day) {
    //                         let matches = true;
    //                         for (const spell_of_level of spells_of_level.filter(o => o.cast_type === "X/Day")) {
    //                           if (spellcasting_entry.spells.filter(o => o.spell_id === spell_of_level.spell_id)[0].x !== x_day) {
    //                             matches = false;
    //                             break;
    //                           }
    //                         }
    //                         if (matches) {
    //                           level_string = `${x_day}/Day each: `;
    //                         }
    //                       } else {
    //                         console.error("x_day missing");
    //                       }
    //                     } else {
    //                       level_string = `A number of times per day found in the Per Day Spells Table: `;
    //                     }
    //                   break;
    //                 }
    //                 if (level_string.endsWith(": ")) {
    //                   for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
    //                     const spell_id = spell_of_level.spell_id;
    //                     const spell = spells.find(o => o._id === spell_id);
    //                     if (spell) {
    //                       if (!level_string.endsWith(": ")) {
    //                         level_string += ", ";
    //                       }
    //                       level_string += spell.name;
    //                     }
    //                   }
    //                   str += level_string + "\n\r___\n\r";
    //                 } else if (cast_type === "X/Day") {
    //                   // Show them each in their own row
    //                   for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
    //                     const spell_id = spell_of_level.spell_id;
    //                     const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spell_id)[0].x;
    //                     const spell = spells.find(o => o._id === spell_id);
    //                     if (x_day && spell) {
    //                       str += `${spell.name} (${x_day}/Day)\n\r___\n\r`;
    //                     }
    //                   }
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       }
    //       for (const fb of fbs) {
    //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //       }
    //     } else {
    //       const handleSpellcasting = (spellcasting_entry: SpellcastingEntry) => {
    //         str += `<b>Spellcasting.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: spellcasting_entry.description, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //         const resources_entry = creature.resources_table.getEntry(creature.min_challenge_rating);
    //         const ci_spells = spellcasting_entry.spells;
    //         for (const level of spellcasting_entry.spell_levels.sort((a, b) => { return a - b; })) {
    //           const spells_of_level = ci_spells.filter(o => o.spell_level === level);
    //           if (spells_of_level.length > 0) {
    //             if (level === 0) {
    //               let level_string = "At will: ";
    //               for (const spell_of_level of spells_of_level) {
    //                 const spell_id = spell_of_level.spell_id;
    //                 const spell = spells.find(o => o._id === spell_id);
    //                 if (spell) {
    //                   if (level_string.length > 9) {
    //                     level_string += ", ";
    //                   }
    //                   level_string += spell.name;
    //                 }
    //               }
    //               str += level_string + "\n\r___\n\r";
    //             } else {
    //               const cast_types = common_util.unique_array(spells_of_level.map(o => o.cast_type));
    //               for (const cast_type of cast_types) {
    //                 let level_string = "";
    //                 switch (cast_type) {
    //                   case "Slot": 
    //                     // Check how many slots of this level they have
    //                     let level_name = "";
    //                     switch(level) {
    //                       case 1: level_name = "1st"; break;
    //                       case 2: level_name = "2nd"; break;
    //                       case 3: level_name = "3rd"; break;
    //                       default: level_name = `${level}th`; break;
    //                     }
    //                     const slots_of_level = resources_entry && Object.keys(resources_entry.data).includes(`Slot: ${level}`) ? resources_entry.data[ `Slot: ${level}`] : 0;
    //                     if (slots_of_level) {
    //                       level_string += `${level_name} level (${slots_of_level}):`;
    //                     } else if (!resources_entry) {
    //                       level_string += `${level_name} level (found in the Resources table):`;
    //                     }
    //                   break;
    //                   case "At Will":
    //                     level_string = "At will: ";
    //                   break;
    //                   case "Ritual": 
    //                     level_string = "As a Ritual: ";
    //                   break;
    //                   default:
    //                     if (spellcasting_entry) {
    //                       // Check if they're all the same.  They usually are.
    //                       const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spells_of_level[0].spell_id)[0].x;
    //                       if (x_day) {
    //                         let matches = true;
    //                         for (const spell_of_level of spells_of_level.filter(o => o.cast_type === "X/Day")) {
    //                           if (spellcasting_entry.spells.filter(o => o.spell_id === spell_of_level.spell_id)[0].x !== x_day) {
    //                             matches = false;
    //                             break;
    //                           }
    //                         }
    //                         if (matches) {
    //                           level_string = `${x_day}/Day each: `;
    //                         }
    //                       } else {
    //                         console.error("x_day missing");
    //                       }
    //                     } else {
    //                       level_string = `A number of times per day found in the Per Day Spells Table: `;
    //                     }
    //                   break;
    //                 }
    //                 console.log(level_string);
    //                 if (level_string.endsWith(": ")) {
    //                   for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
    //                     const spell_id = spell_of_level.spell_id;
    //                     const spell = spells.find(o => o._id === spell_id);
    //                     if (spell) {
    //                       if (!level_string.endsWith(": ")) {
    //                         level_string += ", ";
    //                       }
    //                       level_string += spell.name;
    //                     }
    //                   }
    //                   str += level_string + "\n\r___\n\r";
    //                 } else if (cast_type === "X/Day") {
    //                   // Show them each in their own row
    //                   for (const spell_of_level of spells_of_level.filter(o => o.cast_type === cast_type)) {
    //                     const spell_id = spell_of_level.spell_id;
    //                     const x_day = spellcasting_entry.spells.filter(o => o.spell_id === spell_id)[0].x;
    //                     const spell = spells.find(o => o._id === spell_id);
    //                     if (x_day && spell) {
    //                       str += `${spell.name} (${x_day}/Day)\n\r___\n\r`;
    //                     }
    //                   }
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       }
    //       const crs = (common_util.unique_array([...fbs.map(o => o.level),...creature.spellcasting_table.entries.map(o => o.level)])).sort((a, b) => { return a - b; });
    //       if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
    //         for (const fb of fbs) {
    //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //         }
    //         const entry = creature.spellcasting_table.getEntry(creature.min_challenge_rating);
    //         if (entry) {
    //           handleSpellcasting(entry);
    //         }
    //       } else if (crs.length > 0) {
    //         for (const cr of crs) {
    //           str += `### Beginning At CR ${cr}\n\r___\n\r`;
    //           for (const fb of fbs.filter(o => o.level === cr)) {
    //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //           }
    //           const entry = creature.spellcasting_table.getEntry(cr);
    //           if (entry) {
    //             handleSpellcasting(entry);
    //           }
    //         }
    //       }
    //     }
    //   }
    //   fbs = ci ? creature.legendary_action_fbs.filter(o => o.level <= ci.challenge_rating) : creature.legendary_action_fbs;
    //   if (fbs.length > 0) {
    //     str += "## Legendary Actions\n\r___\n\r";
    //     if (ci) {
    //       const legendary_actions = creature.legendary_actions_for_cr(ci.challenge_rating);
    //       str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
    //     } else if (creature.legendaries_table.entries.length === 1 && 
    //       creature.legendaries_table.entries[0].level <= creature.min_challenge_rating) {
    //       const legendary_actions = creature.legendaries_table.entries[0].actions;
    //       str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
    //     } else if (creature.legendaries_table.entries.length === 1) {
    //       str += `### Beginning At CR ${creature.legendaries_table.entries[0].level}\n\r___\n\r`;
    //       const legendary_actions = creature.legendaries_table.entries[0].actions;
    //       str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} can take ${legendary_actions} legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
    //     } else {
    //       str += `${creature.small_name === "" ? creature.name : "The " + creature.small_name} may be able to take legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn.  See the Legendaries Table to see how many uses if any it has of this feature. ${creature.small_name === "" ? creature.name : "The " + creature.small_name} regains spent legendary actions at the start of its turn.\n\r___\n\r`;
    //     }
    //     if (ci) {
    //       for (const fb of fbs) {
    //         str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //       }
    //     } else {
    //       const crs = (common_util.unique_array(fbs.map(o => o.level))).sort((a, b) => { return a - b; });
    //       if (crs.length === 1 && crs[0] <= creature.min_challenge_rating) {
    //         for (const fb of fbs) {
    //           str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //         }
    //       } else if (crs.length > 0) {
    //         for (const cr of crs) {
    //           if (crs.length > 1) {
    //             str += `### Beginning At CR ${cr}\n\r___\n\r`;
    //           }
    //           for (const fb of fbs.filter(o => o.level === cr)) {
    //             str += `<b>${fb.name}.</b> ${char_util.getStringWithReplacementsWithObject({ clean_me: fb.description, obj: ci, spellcasting_ability: creature.spellcasting_ability !== "None" ? creature.spellcasting_ability : undefined })}\n\r___\n\r`;
    //           }
    //         }
    //       }
    //     }
    //   }
    //   return str;
    // }
  }
  blurg();

  const parse = async () => {
    const elem = parseRef.current as HTMLElement;
    debugger;
    // const parsed = await api.parseCreature5e(elem, obj.source_book_ids[0], "", "");
    const parsed = await parseTablePH(elem, obj.source_book_ids[0], "Intoner", "Lair Actions");
    // const new_obj = obj.clone();
    // new_obj.copy(parsed);
    // setObj(new_obj);
    setParseCreature(parsed);
    setParsedCreatureText(await api.formattedCreatureStatBlock(parsed));
    // let stat_block_elements = elem.getElementsByClassName("mon-stat-block-2024");
    // if (stat_block_elements.length === 0) {
    //   stat_block_elements = elem.getElementsByClassName("mon-stat-block");
    // }
    // if (stat_block_elements.length === 0) {
    //   stat_block_elements = elem.getElementsByClassName("stat-block");
    // }
    // if (stat_block_elements.length === 1) {
    //   // while (elem && !elem.classList.contains("stat-block") && !elem.classList.contains("mon-stat-block") && !elem.classList.contains("mon-stat-block-2024")) {
    //   //   elem = elem.firstElementChild as (HTMLElement | null);
    //   // }
    //   debugger;
    //   api.parseCreature(obj, stat_block_elements[0] as HTMLElement).then(new_obj => {
    //     setObj(new_obj);
    //     updateCreatureInstances(new_obj);
    //     setChangeTime(common_util.get_time_number());
    //   });
    // }
  }

  

  // This is my attempt to be able to easily parse Pointy Hat's stat blocks
  const parseTablePHV1 = async (m_el: Element, sb_id: string, looking_for_creature_name: string, looking_for_fb_name: string) => {
    // const all_creatures = await api.getObjects<Creature>({ data_type: "creature" });
    // const old_creatures = all_creatures.filter(o => o.source_book_ids.includes(sb_id));

    // const c = new Creature();
    // c.ability_scores.strength = new UpgradableNumber(); // Force it to be blank, because this can't be true, so we can check if it has a valid value later.
    
    // if (sb_id) {
    //   c.source_books_old = [{ type: "Source Book", id: sb_id, third_party: false }];
    //   c.source_books = [];
    //   const sb = api.source_books.find(o => o._id === sb_id);
    //   if (sb) {
    //     c.source_books.push(sb);
    //   }
    // }

    // const attr_entry = new Attributes();
    // const tbl_entry = new TableEntry();
    // const resources_entry = new TableEntry();
    // c.attributes_table.entries = [attr_entry];
    // c.table.entries = [tbl_entry];
    // let legendary = false;
    // const leg_entry = new Legendaries();
    // let existing_c: Creature | undefined;
    // const table_els = m_el.getElementsByTagName("table");
    // const the_table = table_els[0];
    // if (the_table) {
    //   const the_rows = the_table.rows;
    //   let skills_el: HTMLElement | undefined;
    //   let fbType: CreatureFBType = "Traits";

    //   const processFB = (p: HTMLElement, extra_ps: HTMLElement[]) => {
    //     // It's probably a FeatureBase
    //     // <p class="Stat-Block-Styles_Stat-Block-Body"><span
    //     //     class="Sans-Serif-Character-Styles_Inline-Subhead-Sans-Serif">Amphibious.</span>&nbsp;The aboleth can breathe air
    //     //   and water.</p>
    //     // if (c.name.includes(looking_for_creature_name)) {
    //     //   const hangings = m_el.getElementsByClassName("Stat-Block-Styles_Stat-Block-Hanging");
    //     //   for (let k = 0; k < hangings.length; ++k) {
    //     //     const hanging = hangings[k];
    //     //     console.log(hanging.outerHTML);
    //     //   }
    //     // }
        
    //     const name_spans = p.getElementsByTagName("span");
    //     const name_strongs = p.getElementsByTagName("strong");
    //     let name = "";
    //     if (name_spans.length > 0 || name_strongs.length > 0) {
    //       const name_span = name_spans.length > 0 ? name_spans[0] : name_strongs[0];
    //       name = name_span.innerText.trim();
    //       if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
    //         console.log(name);
    //       }
    //       if (name.endsWith(".")) {
    //         name = name.substring(0, name.length - 1);
    //       }
    //       name = common_util.normalizeDashes(name);
    //       name = common_util.replaceAll(name, "’", "'");
    //     }
    //     let desc0 = common_util.normalizeSpaces(p.innerText.substring(name.length + 1)).trim();
    //     if (desc0.startsWith(".")) {
    //       desc0 = desc0.substring(1).trim();
    //     }
    //     for (const pk of extra_ps) {
    //       desc0 += `<p>${pk.innerText}</p>`;
    //     }
    //     if (fbType === "Lair Actions" && name.length > 50) {
    //       // debugger;
    //       desc0 = name + " " + desc0;
    //       name = `${c.name} Lair Action ${c.lair_action_fbs.length + 1}`;
    //     }
    //     let content_str = `${desc0}`;
    //     if (content_str.includes("cost it extra moment")) {
    //       content_str = common_util.replaceAll(content_str, "cost it extra moment", "cost it extra movement");
    //     }

    //     if (name.includes("Legendary Resistance")) {
    //       // Not a FeatureBase. We store these in the legendaries table
    //       legendary = true;
    //       let legendary_resistances_str = name.split("(")[1];
    //       if (legendary_resistances_str) {
    //         let lair_str = "";
    //         if (legendary_resistances_str.includes(" or ")) {
    //           lair_str = legendary_resistances_str.split(" or ")[1];
    //           lair_str = lair_str.substring(0, lair_str.indexOf("/"));
              
    //           leg_entry.res_lair = +lair_str;
    //           leg_entry.actions = 0;
    //         }
    //         legendary_resistances_str = legendary_resistances_str.substring(0, legendary_resistances_str.indexOf("/"));
    //         leg_entry.resistances = +legendary_resistances_str;
    //       }
    //     } else if (name.includes("Spellcasting")) {
    //       // Not a FeatureBase. We store this in the spellcasting table.
    //       // <p class="c13"><span class="c12">Spellcasting.</span><span class="c17">&nbsp;</span><span class="c1 c5">The
    //       //     intoner casts one of the following spells, requiring no material components and using Charisma as its
    //       //     spellcasting ability (spell save DC 19, +11 to hit with spell attacks):</span></p>
    //       // <p class="c3"><span class="c5 c1"></span></p>
    //       // <ul class="c20 lst-kix_wqejzcrszqhw-0 start">
    //       //   <li class="c13 c44 li-bullet-0"><span class="c1">At will: </span><span class="c19 c1">minor illusion,
    //       //       prestidigitation, vicious mockery, charm person</span></li>
    //       //   <li class="c13 c44 li-bullet-0"><span class="c1">3/day each: counterspell, </span><span class="c19 c1">cure
    //       //       wounds, shatter, suggestion, thunderwave (cast as a 3rd-level spell)</span></li>
    //       //   <li class="c13 c44 li-bullet-0"><span class="c1">2/day each: </span><span class="c19 c1">confusion, dominate
    //       //       person, modify memory</span></li>
    //       //   <li class="c13 c44 li-bullet-0"><span class="c1">1/day each: </span><span class="c1 c24">irresistible dance,
    //       //       mass suggestion, finger of death</span></li>
    //       // </ul>
    //       if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Spellcasting") {
    //         debugger;
    //       }
    //       const sc_entry = new SpellcastingEntry();
    //       sc_entry.level = c.challenge_rating;
    //       sc_entry.description = content_str;
    //       if (sc_entry.description.toLocaleLowerCase().includes("spellcasting ability is ")) {
    //         let spellcasting_ability = common_util.capitalize_first(
    //           sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf("spellcasting ability is ") + 24));
    //         spellcasting_ability = spellcasting_ability.substring(0, 3).toUpperCase();
    //         c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
    //       } else if (sc_entry.description.toLocaleLowerCase().includes("as the spellcasting")) {
    //         // The aarakocra casts one of the following spells, requiring no Material components 
    //         // and using Wisdom as the spellcasting ability (spell save DC 13):
    //         let index = sc_entry.description.toLocaleLowerCase().indexOf("as the spellcasting") - 2;
    //         while (index > 0 && sc_entry.description[index] !== " ") {
    //           index--;
    //         }
    //         index++;
    //         const spellcasting_ability = sc_entry.description.substring(index, index + 3).toLocaleUpperCase();
    //         if (ABILITY_SCORES.includes(spellcasting_ability)) {
    //           c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
    //         }
    //       } else if (sc_entry.description.toLocaleLowerCase().includes("as its spellcasting")) {
    //         // The aarakocra casts one of the following spells, requiring no Material components 
    //         // and using Wisdom as the spellcasting ability (spell save DC 13):
    //         let index = sc_entry.description.toLocaleLowerCase().indexOf("as its spellcasting") - 2;
    //         while (index > 0 && sc_entry.description[index] !== " ") {
    //           index--;
    //         }
    //         index++;
    //         const spellcasting_ability = sc_entry.description.substring(index, index + 3).toLocaleUpperCase();
    //         if (ABILITY_SCORES.includes(spellcasting_ability)) {
    //           c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
    //         }
    //       }
    //       if (sc_entry.description.toLocaleLowerCase().includes(" dc ")) {
    //         if (c.dc.isBlank()) {
    //           let dc_str = sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf(" dc ") + 4).trim();
    //           if (dc_str.includes(" ")) {
    //             dc_str = dc_str.substring(0, dc_str.indexOf(" "));
    //           }
    //           dc_str = dc_str.replace(",","").replace(".","").replace(")","").replace(":","").trim();
    //           const dc_num = +dc_str;
    //           if (c.spellcasting_ability !== "None" && 
    //             dc_num === (8 + c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
    //           ) {
    //             c.dc = new UpgradableNumber();
    //             c.dc.base = 8;
    //             c.dc.add_prof_bonus_mult = 1;
    //             c.dc.add_ability_mod_mult = 1;
    //             c.dc.add_ability_scores = [c.spellcasting_ability];
    //             sc_entry.description = common_util.replaceAll(sc_entry.description, ` dc ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
    //             sc_entry.description = common_util.replaceAll(sc_entry.description, ` DC ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
    //           } else {
    //             c.dc = new UpgradableNumber(dc_num);
    //           }
    //         }
    //       }
    //       if (sc_entry.description.toLocaleLowerCase().includes(" to hit ")) {
    //         let attack_str = sc_entry.description.substring(0, sc_entry.description.toLocaleLowerCase().indexOf(" to hit ")).trim();
    //         attack_str = attack_str.substring(attack_str.lastIndexOf("+")+1);
    //         const attack_num = +attack_str;
    //         if (c.spellcasting_ability !== "None" && 
    //           attack_num === (c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
    //         ) {
    //           c.magic_attack = new UpgradableNumber();
    //           c.magic_attack.add_prof_bonus_mult = 1;
    //           c.magic_attack.add_ability_mod_mult = 1;
    //           c.magic_attack.add_ability_scores = [c.spellcasting_ability];
    //           sc_entry.description = common_util.replaceAll(sc_entry.description, `+${attack_num} to hit `, `{attack_string.${c.spellcasting_ability}} to hit `);
    //         } else {
    //           c.magic_attack = new UpgradableNumber(attack_num);
    //         }
    //       }
    //       if (c.spellcasting_ability !== "None") {
    //         const stat = c.getBasicModifier(c.spellcasting_ability);
    //         const pb = c.proficiency_modifier;

    //         if (c.dc.base > 0) {
    //           c.dc.base -= stat + pb;
    //         } else {
    //           c.dc.base = 8;
    //         }
    //         c.dc.add_prof_bonus_mult = 1;
    //         c.dc.add_ability_mod_mult = 1;
    //         c.dc.add_ability_scores.push(c.spellcasting_ability);

    //         if (c.magic_attack.base > 0) {
    //           c.magic_attack.base -= stat + pb;
    //         }
    //         c.magic_attack.add_prof_bonus_mult = 1;
    //         c.magic_attack.add_ability_mod_mult = 1;
    //         c.magic_attack.add_ability_scores.push(c.spellcasting_ability);
    //         c.spellcasting_table.entries = [sc_entry];

    //         // Look for a <ul> right after p
    //         // debugger;
    //         let nextElem = p.nextElementSibling;
    //         while (nextElem && !(nextElem instanceof HTMLUListElement)) {
    //           nextElem = nextElem.nextElementSibling;
    //         }
    //         if (nextElem instanceof HTMLUListElement) {
    //           const li_els = nextElem.getElementsByTagName("li");
    //           for (let li_index = 0; li_index < li_els.length; ++li_index) {
    //             const li = li_els[li_index];
    //             const innerText = common_util.cleanForCompare(li.innerText);
    //             const start_string = innerText.substring(0, innerText.indexOf(":"));

    //             let spell_level = -1;
    //             let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "Slot";
    //             let x = -1;
    //             if (start_string.includes("cantrip") || start_string.includes("level")) {
    //               const level_char = start_string[0];
    //               if (level_char === "c") {
    //                 spell_level = 0;
    //               } else {
    //                 spell_level = +level_char;
    //                 if (isNaN(spell_level)) {
    //                   debugger;
    //                 }
    //               }
                  
    //               const pieces = start_string.split("(");
    //               if (pieces[1]) {
    //                 const use_string = pieces[1];
    //                 if (use_string.includes("at will")) {
    //                   cast_type = "At Will";
    //                 } else if (use_string.includes("ritual")) {
    //                   cast_type = "Ritual";
    //                 } else if (use_string.includes("/day")) {
    //                   const pieces2 = use_string.split("/");
    //                   x = +pieces2[0];
    //                 } else if (use_string.includes("slot")) {
    //                   const pieces2 = use_string.split(" ");
    //                   const slot_count = +pieces2[0];
    //                   if (!isNaN(slot_count)) {
    //                     resources_entry.data[`Slot: ${spell_level}`] = slot_count;
    //                   }
    //                 } else {
    //                   debugger;
    //                 }
    //               }
    //             } else {
    //               // Different format.
    //               // <p class="Stat-Block-Styles_Stat-Block-Hanging">At will:&nbsp;
    //               //   <span><a>detect magic</a></span>,&nbsp;
    //               //   <span><a>fog cloud</a></span>,&nbsp;
    //               //   <span><a>light</a></span>
    //               // </p>
    //               // <p class="Stat-Block-Styles_Stat-Block-Hanging">3/day each:&nbsp;
    //               //   <span><a>feather fall</a></span>,&nbsp;
    //               //   <span><a>fly</a></span>,&nbsp;
    //               //   <span><a>misty step</a></span>,&nbsp;
    //               //   <span><a>telekinesis</a></span>
    //               // </p>
    //               if (start_string.includes("at will")) {
    //                 cast_type = "At Will";
    //               } else if (start_string.includes("/day")) {
    //                 cast_type = "X/Day";
    //                 const pieces = start_string.split("/");
    //                 const x_str = pieces[0].trim();
    //                 x = +x_str;
    //                 if (isNaN(x)) {
    //                   debugger;
    //                 }
    //               } else {
    //                 debugger;
    //               }
    //             }

    //             // sc_entry.description += `\n${innerText}`;
    //             const spell_anchors = li.getElementsByTagName("a");
    //             if (spell_anchors.length > 0) {
    //               for (let k = 0; k < spell_anchors.length; ++k) {
    //                 const spell_anchor = spell_anchors[k];
    //                 let spell_name = common_util.cleanForCompare(spell_anchor.innerText);
    //                 spell_name = spell_name.replace("at will:", "").replace(`${x}/day each: `, "").trim();
    //                 let extra_text: string | undefined;
    //                 if (spell_name.includes("(")) {
    //                   let index = spell_name.indexOf("(");
    //                   let sub = spell_name.substring(index);
    //                   index = sub.indexOf(")");
    //                   sub = sub.replace("(","").replace(")","").trim();
    //                   extra_text = sub;
    //                   spell_name = spell_name.replace(`(${extra_text})`, "").trim();
    //                 }
    //                 let spells_with_name = api.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
    //                 let spell: Spell | undefined;
    //                 if (spells_with_name.length === 1) {
    //                   spell = spells_with_name[0];
    //                 } else if (spells_with_name.length > 1) {
    //                   spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
    //                   if (!spell) {
    //                     // const max_dominance_level = Math.max(...spells_with_name.map(o => o.dominance_level));
    //                     spell = spells_with_name[0].getDominant() as Spell; // spells_with_name.find(o => o.dominance_level === max_dominance_level);
    //                   }
    //                 }
    //                 if (spell) {
    //                   spell_level = spell.level;
    //                   sc_entry.spells.push({
    //                     spell_level,
    //                     spell_id: spell._id,
    //                     cast_type,
    //                     x,
    //                     extra_text
    //                   });
    //                 } else {
    //                   debugger;
    //                 }
    //               }
    //             } else {
    //               const spell_pieces = li.innerText.split(",").map(o => o.trim());
    //               let last_spell_id = "";
    //               for (let k = 0; k < spell_pieces.length; ++k) {
    //                 const spell_anchor = spell_pieces[k];
    //                 let spell_name = common_util.cleanForCompare(spell_anchor);
    //                 spell_name = spell_name.replace("at will:", "").replace(`${x}/day each: `, "").trim();
    //                 let extra_text: string | undefined;
    //                 if (spell_name.includes("(")) {
    //                   let index = spell_name.indexOf("(");
    //                   let sub = spell_name.substring(index);
    //                   index = sub.indexOf(")");
    //                   sub = sub.replace("(","").replace(")","").trim();
    //                   extra_text = sub;
    //                   spell_name = spell_name.replace(`(${extra_text})`, "").trim();
    //                 }
    //                 let spells_with_name = api.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
    //                 let spell: Spell | undefined;
    //                 if (spells_with_name.length === 1) {
    //                   spell = spells_with_name[0];
    //                 } else if (spells_with_name.length > 1) {
    //                   spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
    //                   if (!spell) {
    //                     // const max_dominance_level = Math.max(...spells_with_name.map(o => o.dominance_level));
    //                     spell = spells_with_name[0].getDominant() as Spell; // spells_with_name.find(o => o.dominance_level === max_dominance_level);
    //                   }
    //                 }
    //                 if (spell) {
    //                   spell_level = spell.level;
    //                   sc_entry.spells.push({
    //                     spell_level,
    //                     spell_id: spell._id,
    //                     cast_type,
    //                     x,
    //                     extra_text
    //                   });
    //                   last_spell_id = spell._id;
    //                 } else if (last_spell_id !== "") {
    //                   const c_spell = sc_entry.spells.find(o => o.spell_id === last_spell_id);
    //                   if (c_spell) {
    //                     if (!c_spell.extra_text) {
    //                       c_spell.extra_text = "";
    //                     }
    //                     c_spell.extra_text += spell_anchor;
    //                   } else {
    //                     debugger;
    //                   }
    //                 } else {
    //                   debugger;
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       }
    //     } else {
    //       const fb = new FeatureBase();
    //       fb.creature_fb_type = fbType;

    //       const descVersions: string[] = [desc0];
    //       content_str = common_util.normalizeDashes(content_str);
    //       content_str = common_util.replaceAll(content_str, "’", "'");
    //       if (desc0 !== content_str) {
    //         descVersions.push(content_str);
    //       }
    //       if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
    //         console.log([...descVersions]);
    //       }

    //       let ff: FloatingFeature | undefined = undefined; 
    //       if (!ff) {
    //         let desc_helper = common_util.cleanSpaces(common_util.replaceAll(common_util.replaceAll(content_str, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`));
    //         if (!descVersions.includes(desc_helper)) {
    //           descVersions.push(desc_helper);
    //         }
    //         if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
    //           console.log([...descVersions]);
    //         }
    //         let new_descs: string[] = [];
    //         for (const dv of descVersions) {
    //           const new_desc = common_util.replaceAll(common_util.replaceAll(dv, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`);
    //           if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
    //             new_descs.push(new_desc);
    //           }
    //         }
    //         descVersions.push(...new_descs);
    //         if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
    //           console.log([...descVersions]);
    //         }
    //         if (desc_helper === content_str) {
    //           const desc_helper2 = common_util.replaceAll(desc_helper, "\n", "");
    //           if (c.small_name.length === 0) {
    //             if (c.name.includes(" ")) {
    //               // debugger;
    //               const words = common_util.cleanForCompare(c.name).split(" ").map(o => o.toLocaleLowerCase());
    //               let small_name = "";
    //               for (let i = 0; i < words.length; ++i) {
    //                 const word = words[i];
    //                 if (desc_helper2.includes(` ${word} `)) {
    //                   if (small_name === "") {
    //                     small_name = word;
    //                   } else {
    //                     const prev_word = words[i - 1];
    //                     if (small_name.includes(prev_word) && desc_helper2.includes(` ${small_name} ${word} `)) {
    //                       small_name += ` ${word}`;
    //                     }
    //                   }
    //                 }
    //               }
    //               c.small_name = small_name;
    //             } else {
    //               // Check for some things that don't fit the above method.
    //               if (c.name.toLocaleLowerCase().includes("sphinx")) {
    //                 c.small_name = "sphinx";
    //               } else {
    //                 c.small_name = c.name.toLocaleLowerCase();
    //               }
    //             }
    //           }
    //           if (c.small_name.length > 0) {
    //             if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
    //               console.log(content_str);
    //             }
    //             desc_helper = common_util.replaceAll(content_str, c.small_name, `{cfs}`);
                
    //             new_descs = [];
    //             for (const dv of descVersions) {
    //               const new_desc = common_util.replaceAll(dv, c.small_name, `{cfs}`);
    //               if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
    //                 new_descs.push(new_desc);
    //               }
    //             }
    //             descVersions.push(...new_descs);
    //             if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
    //               console.log([...descVersions]);
    //             }
    //           }
    //         }
    //         const makeFF = (desc_str: string) => {
    //           const ff_obj: any = {
    //             name,
    //             description: desc_str,
    //             feature_type: "Creature Ability"
    //           };
    //           if (name === "Multiattack") {
    //             ff_obj.feature_type = "Minion Extra Attacks";
    //             // Usually says something along the lines of "makes X attacks";
    //             const sub = content_str.toLocaleLowerCase();
                
    //             const pieces = common_util.cleanForCompare(sub).split(" ");
    //             const num_strings = ["two","three","four","five","six","seven","eight","nine","ten","2","3","4","5","6","7","8","9","10"];
    //             let num_string = common_util.findOverlap(num_strings, pieces);
    //             if (num_string.length === 0) {
    //               // It's more complicated, so just make it 2
    //               num_string = "2";
    //             }
    //             if (num_string.length > 0) {
    //               switch(num_string) {
    //                 case "two": ff_obj.num = 2; break;
    //                 case "three": ff_obj.num = 3; break;
    //                 case "four": ff_obj.num = 4; break;
    //                 case "five": ff_obj.num = 5; break;
    //                 case "six": ff_obj.num = 6; break;
    //                 case "seven": ff_obj.num = 7; break;
    //                 case "eight": ff_obj.num = 8; break;
    //                 case "nine": ff_obj.num = 9; break;
    //                 case "ten": ff_obj.num = 10; break;
    //                 default: ff_obj.num = +num_string; break;
    //               }
    //               ff_obj.num -= 1;
    //               return new UpgradableNumberFeature(ff_obj);
    //             }
    //           } else {
    //             const ca = new CreatureAbility(ff_obj);
    //             if (ca instanceof CreatureAbility) {
    //               if (c.name.includes(looking_for_creature_name) && ca.name.includes(looking_for_fb_name)) {
    //                 console.log(desc_str);
    //               }
    //               let using_ability: AbilityScoreType | "" = "";
    //               let using_ability_mod = -5;
    //               let using_ability_atk: AbilityScoreType | "" = "";
    //               let using_ability_atk_mod = -5;
                  
    //               if (desc_str.includes("DC") && !desc_str.includes("rapple")) {
    //                 // New format: Strength Saving Throw: DC 12
    //                 // Old format: DC 19 Dexterity saving throw
    //                 // Needs to work with both
    //                 // debugger;
    //                 let dc = 0;
    //                 let save_type = "";
    //                 const pieces = common_util.cleanForCompare(desc_str).split(" ").map(o => o.replace(",",""));
    //                 let dc_index = -1;
    //                 for (let i = 0; i < pieces.length; ++i) {
    //                   const piece = pieces[i];
    //                   if (piece === "dc") {
    //                     dc_index = i;
    //                     break;
    //                   }
    //                 }
    //                 const dc_str = pieces[dc_index + 1].replace(",", "").replace(".","").replace(")","").replace(":","");
    //                 dc = +dc_str;
    //                 if (isNaN(dc)) {
    //                   console.error("DC Parse Error");
    //                   // debugger;
    //                 } else {
    //                   // Check New Method first
    //                   let type_maybe = pieces[dc_index - 3];
    //                   if (type_maybe.length > 3) {
    //                     type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
    //                     if (ABILITY_SCORES.includes(type_maybe)) {
    //                       save_type = type_maybe;
    //                     }
    //                   }
    //                   if (save_type === "") {
    //                     // Didn't find it, so Check Old Method
    //                     type_maybe = pieces[dc_index + 2];
    //                     if (type_maybe) {
    //                       if (type_maybe.length > 3) {
    //                         type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
    //                         if (ABILITY_SCORES.includes(type_maybe)) {
    //                           save_type = type_maybe;
    //                         }
    //                       }
    //                     } else {
    //                       let spell_index = pieces.indexOf("spell");
    //                       if (spell_index < 0) {
    //                         spell_index = pieces.indexOf("Spell");
    //                       }
    //                       let spell: ModelBase | undefined = undefined;
    //                       if (spell_index > -1) {
    //                         // Check the words before and after spell_index
    //                         let spell_name = "";
    //                         let use_index = spell_index - 1;
    //                         while (use_index > -1) {
    //                           spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
    //                           // console.log(spell_name);
    //                           if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
    //                             debugger;
    //                           }
                              
    //                           spell = api.getSpellByName(spell_name);
    //                           if (spell) {
    //                             break;
    //                           } else {
    //                             use_index--;
    //                           }
    //                         }
    //                         if (!spell) {
    //                           spell_name = "";
    //                           use_index = spell_index + 1;
    //                           while (use_index < pieces.length) {
    //                             spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
    //                             if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
    //                               debugger;
    //                             }
                                
    //                             spell = api.getSpellByName(spell_name);
    //                             if (spell) {
    //                               break;
    //                             } else {
    //                               use_index++;
    //                             }
    //                           }
    //                         }
    //                       }
    //                       if (spell instanceof Spell) {
    //                         const save_effect = spell.effects.find(o => o.attack_type === "Save");
    //                         if (save_effect) {
    //                           save_type = save_effect.saving_throw_ability_score;
    //                         } else {
    //                           console.error(pieces);
    //                           console.error(dc_index);
    //                           console.error(dc);
    //                           debugger;
    //                         }
    //                       } else {
    //                         spell_index = pieces.indexOf("casts");
    //                         if (spell_index < 0) {
    //                           spell_index = pieces.indexOf("cast");
    //                         }
    //                         if (spell_index > -1) {
    //                           // Check the words before and after spell_index
    //                           let spell_name = "";
    //                           let use_index = spell_index - 1;
    //                           while (use_index > -1) {
    //                             spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
    //                             // console.log(spell_name);
    //                             if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
    //                               debugger;
    //                             }
                                
    //                             spell = api.getSpellByName(spell_name);
    //                             if (spell) {
    //                               break;
    //                             } else {
    //                               use_index--;
    //                             }
    //                           }
    //                           if (!spell) {
    //                             spell_name = "";
    //                             use_index = spell_index + 1;
    //                             while (use_index < pieces.length) {
    //                               spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
    //                               if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
    //                                 debugger;
    //                               }
                                  
    //                               spell = api.getSpellByName(spell_name);
    //                               if (spell) {
    //                                 break;
    //                               } else {
    //                                 use_index++;
    //                               }
    //                             }
    //                           }
    //                         }
    //                         if (spell instanceof Spell) {
    //                           const save_effect = spell.effects.find(o => o.attack_type === "Save");
    //                           if (save_effect) {
    //                             save_type = save_effect.saving_throw_ability_score;
    //                           } else {
    //                             console.error(pieces);
    //                             console.error(dc_index);
    //                             console.error(dc);
    //                             // debugger;
    //                           }
    //                         } else {
    //                           console.error(pieces);
    //                           console.error(dc_index);
    //                           console.error(dc);
    //                           debugger;
    //                         }
    //                       }
    //                     }
    //                   }
                      
    //                   const ability_mod = dc - 8 - c.proficiency_modifier;
                      
    //                   if (ca.name.includes("Breath")) {
    //                     // Breath Weapons the DC is generally based on CON.
    //                     if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
    //                       using_ability = "CON";
    //                     }
    //                   }
    //                   if (using_ability === "") {
    //                     // This order is the most likely.
    //                     if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
    //                       using_ability = "CHA";
    //                     } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
    //                       using_ability = "STR";
    //                     } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
    //                       using_ability = "DEX";
    //                     } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
    //                       using_ability = "WIS";
    //                     } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
    //                       using_ability = "INT";
    //                     } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
    //                       using_ability = "CON";
    //                     }
    //                   }
    //                   if (using_ability === "") {
    //                     // Could be anything.  Just leave it as a base.
    //                     ca.dc.base = dc;
    //                   } else {
    //                     ca.dc.base = 8;
    //                     ca.dc.add_prof_bonus_mult = 1;
    //                     ca.dc.add_ability_mod_mult = 1;
    //                     ca.dc.add_ability_scores.push(using_ability);
    //                     ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
    //                     using_ability_mod = ability_mod;
    //                   }
    //                   ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[save_type];
    //                 }
    //               }
    //               if (desc_str.includes("Attack Roll:") || desc_str.includes("Attack:")) {
    //                 // New stuff:
    //                 // <p><strong><em>Fist.</em></strong> <em>Melee Attack
    //                 //   Roll:</em> +5, reach 5 ft. <em>Hit:</em> 5 (1d4 + 3) Bludgeoning damage.</p>
    //                 // Old stuff:
    //                 // <p><span>Tentacle.&nbsp;</span><span>Melee Weapon Attack:</span>&nbsp;+9 to hit, reach
    //                 //   10 ft., one target.&nbsp;<span>Hit:</span>&nbsp;12 (2d6 + 5) bludgeoning damage.
    //                 //   If the target is a creature, it must succeed on a DC 14 Constitution saving throw or become diseased. The
    //                 //   disease has no effect for 1 minute and can be removed by any magic that cures disease. After 1 minute, the
    //                 //   diseased creature’s skin becomes translucent and slimy, the creature can’t regain hit points unless it is
    //                 //   underwater, and the disease can be removed only by <a>heal</a> or
    //                 //   another disease-curing spell of 6th level or higher. When the creature is outside a body of water, it takes 6
    //                 //   (1d12) acid damage every 10 minutes unless moisture is applied to the skin before 10 minutes have passed.</p>

    //                 if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
    //                   debugger;
    //                 }
    //                 let attack_num = 0;
    //                 let index = desc_str.indexOf("Attack Roll:");
    //                 if (index > -1) {
    //                   let attack_str = desc_str.substring(index + 12).trim();
    //                   index = attack_str.indexOf(",");
    //                   attack_str = attack_str.substring(0, index);
    //                   attack_str = attack_str.replace("+", "");
    //                   attack_num = +attack_str;
    //                 } else {
    //                   index = desc_str.indexOf("Attack:");
    //                   if (index > -1) {
    //                     let attack_str = desc_str.substring(index + 7).trim();
    //                     index = attack_str.indexOf(",");
    //                     attack_str = attack_str.substring(0, index);
    //                     attack_str = attack_str.replace("+", "");
    //                     attack_str = attack_str.replace("to hit", "").trim();
    //                     attack_num = +attack_str;
    //                   }
    //                 }
    //                 if (isNaN(attack_num)) {
    //                   console.error("Attack Parse Error");
    //                   debugger;
    //                 } else {
    //                   // Check New Method first
    //                   const ability_mod = attack_num - c.proficiency_modifier;
                      
    //                   if (using_ability_atk === "") {
    //                     if (desc_str.includes("Spell")) {
    //                       // This order is the most likely.
    //                       if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
    //                         using_ability_atk = "CHA";
    //                       } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
    //                         using_ability_atk = "WIS";
    //                       } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
    //                         using_ability_atk = "INT";
    //                       } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
    //                         using_ability_atk = "STR";
    //                       } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
    //                         using_ability_atk = "DEX";
    //                       } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
    //                         using_ability_atk = "CON";
    //                       }
    //                     } else if (desc_str.includes("Ranged")) {
    //                       // This order is the most likely.
    //                       if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
    //                         using_ability_atk = "DEX";
    //                       } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
    //                         using_ability_atk = "STR";
    //                       } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
    //                         using_ability_atk = "CHA";
    //                       } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
    //                         using_ability_atk = "WIS";
    //                       } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
    //                         using_ability_atk = "INT";
    //                       } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
    //                         using_ability_atk = "CON";
    //                       }
    //                     } else {
    //                       // This order is the most likely.
    //                       if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
    //                         using_ability_atk = "STR";
    //                       } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
    //                         using_ability_atk = "DEX";
    //                       } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
    //                         using_ability_atk = "CHA";
    //                       } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
    //                         using_ability_atk = "WIS";
    //                       } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
    //                         using_ability_atk = "INT";
    //                       } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
    //                         using_ability_atk = "CON";
    //                       }
    //                     }
    //                   }
    //                   if (using_ability_atk === "") {
    //                     // Could be anything.  Just leave it as a base.
    //                     ca.attack_bonus.base = attack_num;
    //                   } else {
    //                     ca.attack_bonus.add_prof_bonus_mult = 1;
    //                     ca.attack_bonus.add_ability_mod_mult = 1;
    //                     ca.attack_bonus.add_ability_scores.push(using_ability_atk);
    //                     if (ca.description.includes(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`)) {
    //                       ca.description = ca.description.replace(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`, `Attack Roll: {attack_string.${using_ability_atk}}`);
    //                     } else if (ca.description.includes(`Attack: ${common_util.addPlusMaybe(attack_num)}`)) {
    //                       ca.description = ca.description.replace(`Attack: ${common_util.addPlusMaybe(attack_num)}`, `Attack: {attack_string.${using_ability_atk}}`);
    //                     }
    //                     using_ability_atk_mod = ability_mod;
    //                   }
    //                 }
    //               }
    //               if (desc_str.includes("reach")) {
    //                 let sub = desc_str.substring(desc_str.indexOf("reach") + 5).trim();
    //                 sub = sub.substring(0, sub.indexOf(" ")).trim();
    //                 ca.range = sub;
    //               } else if (desc_str.includes("range")) {
    //                 let sub = desc_str.substring(desc_str.indexOf("range") + 5).trim();
    //                 sub = sub.substring(0, sub.indexOf(" ")).trim();
    //                 ca.range = sub;
    //               }
    //               let damage_str = desc_str.toLocaleLowerCase();
    //               const lower_case = ca.description.toLowerCase();
    //               // if (name.includes(looking_for_fb_name)) {
    //               //   console.log(damage_str);
    //               // }
    //               while (damage_str.includes("damage")) {
    //                 let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
    //                 damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
    //                 const words = damage_sub.split(" ");
    //                 // console.log(words);
    //                 const damage_type = common_util.capitalize_first(words[words.length - 1]);
    //                 if (damage_sub.includes("(") && damage_sub.includes(")")) {
    //                   let start_index = damage_sub.indexOf("(");
    //                   start_index -= 2;
    //                   while (start_index >= 0 && damage_sub[start_index] !== " ") {
    //                     start_index--;
    //                   }
    //                   let replace_me = damage_sub.substring(start_index);
    //                   const end_index = replace_me.indexOf(")") + 1;
    //                   replace_me = replace_me.substring(0, end_index).trim();
    //                   damage_sub = damage_sub.substring(damage_sub.indexOf("("));
    //                   damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
    //                   damage_sub = damage_sub.replace("(","").replace(")","");
    //                   damage_sub = common_util.normalizeDashes(damage_sub);
    //                   let count = 0;
    //                   let size = 0;
    //                   let flat = 0;
    //                   if (damage_sub.includes("+")) {
    //                     const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
    //                     damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
    //                     flat = +flat_str;
    //                   } else if (damage_sub.includes("-")) {
    //                     const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
    //                     damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
    //                     flat = -1 * +flat_str;
    //                   } 
    //                   if (damage_sub.includes("d")) {
    //                     const pieces = damage_sub.split("d");
    //                     count = +pieces[0];
    //                     size = +pieces[1];
    //                   }
    //                   const effect = new AbilityEffect();
    //                   effect.type = "Damage";
    //                   effect.damage_types.push(DAMAGE_TYPES.includes(damage_type) ? damage_type as DamageType : "Typeless");
    //                   if (ca.effects.length === 0) {
    //                     const first_part = lower_case.substring(0, 20);
    //                     if (first_part.includes("melee")) {
    //                       if (first_part.includes("spell")) {
    //                         effect.attack_type = "Melee Spell";
    //                       } else {
    //                         effect.attack_type = "Melee";
    //                       }
    //                     } else if (first_part.includes("ranged")) {
    //                       if (first_part.includes("spell")) {
    //                         effect.attack_type = "Ranged Spell";
    //                       } else {
    //                         effect.attack_type = "Ranged";
    //                       }
    //                     } else if (ca.saving_throw_ability_score !== "") {
    //                       effect.attack_type = "Save";
    //                       effect.saving_throw_ability_score = ca.saving_throw_ability_score;
    //                     }
    //                     if (ca.range !== "") {
    //                       try {
    //                         const range = +ca.range;
    //                         if (!isNaN(range)) {
    //                           effect.target_int.range.base = range;
    //                         }
    //                       } catch (ex) { }
    //                     }
    //                     // X-foot cone
    //                     if (ca.description.includes("-foot")) {
    //                       const foot_index = ca.description.indexOf("-foot");
    //                       let area_str = "";
    //                       let i = 1;
    //                       while (ca.description[foot_index - i] !== " ") {
    //                         area_str = ca.description[foot_index - i] + area_str;
    //                         ++i;
    //                       }
    //                       // debugger;
    //                       i = 6;
    //                       let shape_str = "";
    //                       while (ca.description[foot_index + i] !== " ") {
    //                         shape_str += ca.description[foot_index + i];
    //                         ++i;
    //                       }
    //                       if (shape_str.endsWith(".")) {
    //                         shape_str = shape_str.substring(0, shape_str.length - 1);
    //                       }
    //                       effect.target_int.target_type = "Area";
    //                       try {
    //                         const area = +area_str;
    //                         if (!isNaN(area)) {
    //                           effect.target_int.area.base = area;
    //                         }
    //                       } catch (ex) { }
    //                       shape_str = common_util.capitalize_first(shape_str);
    //                       if (AREA_TYPES.includes(shape_str)) {
    //                         effect.target_int.area_type = shape_str as AreaType;
    //                       } else {
    //                         effect.target_int.area_type = "Sphere";
    //                       }
    //                     }
    //                   }
    //                   const potence = new Potence();
    //                   potence.level = 1;
    //                   potence.rolls.count.base = count;
    //                   potence.rolls.size.base = size;
    //                   // console.log(damage_str);
    //                   // console.log(damage_sub);
    //                   if (flat !== 0) {
    //                     if (using_ability !== "" && using_ability_atk === "") {
    //                       if (using_ability_mod <= flat) {
    //                         potence.rolls.flat.base = flat - using_ability_mod;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability);
    //                         }
    //                       } else {
    //                         potence.rolls.flat.base = flat;
    //                       }
    //                     } else if (using_ability === "" && using_ability_atk !== "") {
    //                       if (using_ability_atk_mod <= flat) {
    //                         potence.rolls.flat.base = flat - using_ability_atk_mod;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability_atk === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability_atk);
    //                         }
    //                       } else {
    //                         potence.rolls.flat.base = flat;
    //                       }
    //                     } else if (using_ability !== "" && using_ability_atk !== "") {
    //                       if (using_ability_atk_mod === flat) {
    //                         potence.rolls.flat.base = 0;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability_atk === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability_atk);
    //                         }
    //                       } else if (using_ability_mod === flat) {
    //                         potence.rolls.flat.base = 0;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability);
    //                         }
    //                       } else if (using_ability_mod < flat) {
    //                         potence.rolls.flat.base = flat - using_ability_mod;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability);
    //                         }
    //                       } else if (using_ability_atk_mod < flat) {
    //                         potence.rolls.flat.base = flat - using_ability_atk_mod;
    //                         potence.rolls.flat.add_ability_mod_mult = 1;
    //                         if (using_ability_atk === c.spellcasting_ability) {
    //                           potence.rolls.flat.add_ability_scores.push("Spellcasting");
    //                         } else {
    //                           potence.rolls.flat.add_ability_scores.push(using_ability_atk);
    //                         }
    //                       } else {
    //                         potence.rolls.flat.base = flat;
    //                       }
    //                     }
    //                   }
    //                   ca.description = ca.description.replace(replace_me, potence.potence_string);
    //                   effect.potences.push(potence);
    //                   ca.effects.push(effect);
    //                 }
    //               }
    //               if (ca.effects.length === 0) {
    //                 const effect = new AbilityEffect();
    //                 if (ca.dc.isBlank()) {
    //                   effect.type = "Utility";
    //                 } else {
    //                   effect.type = "Control";
    //                   effect.attack_type = "Save";
    //                   effect.saving_throw_ability_score = ca.saving_throw_ability_score;
    //                   const conditions = api.conditions;
    //                   for (const condition of conditions.filter(o => o.common)) {
    //                     const condition_name = condition.name;
    //                     if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
    //                       effect.utility_detail = condition_name;
    //                     }
    //                   }
    //                 }
    //                 ca.effects.push(effect);
    //               }
    //               if (fbType === "Legendary Actions") {
    //                 // debugger;
    //                 ca.consumed_resource.resource_consumed = "Legendary Action";
    //                 if (ca.name.includes("(Costs") && ca.name.includes("Actions)")) {
    //                   try {
    //                     // (Costs X Actions)
    //                     const actions_str = ca.name.substring(ca.name.indexOf("(Costs"));
    //                     const pieces = common_util.cleanForCompare(actions_str).split(" ");
    //                     const action_num = +pieces[1];
    //                     if (!isNaN(action_num)) {
    //                       ca.consumed_resource.amount_consumed = action_num;
    //                     }
    //                   } catch (ex) { }
    //                 } else {
    //                   ca.consumed_resource.amount_consumed = 1;
    //                 }
    //               } else {
    //                 if (ca.name.includes("/Day)")) {
    //                   ca.consumed_resource.amount_consumed = 1;
                      
    //                   const times_index = ca.name.indexOf("/Day");
                      
    //                   let times_str = "";
    //                   let i = 1;
    //                   while (ca.name[times_index - i] !== " ") {
    //                     times_str = ca.name[times_index - i] + times_str;
    //                     ++i;
    //                   }
                      
    //                   try {
    //                     const times = +times_str;
    //                     if (!isNaN(times)) {
    //                       ca.consumed_resource.special_resource_amount = new UpgradableNumber(times);
    //                     }
    //                   } catch (ex) { }
    //                 }
    //                 if (fbType === "Bonus Actions") {
    //                   ca.casting_time = "BA";
    //                 } else if (fbType === "Reactions") {
    //                   ca.casting_time = "RA";
    //                 }
    //               }
    //               // Bite. Melee Weapon Attack: +12 to hit, reach 10 ft., one target. 
    //               // Hit: 18 (2d10 + 7) piercing damage plus 5 (1d10) lightning damage.

    //               // Frightful Presence. Each creature of the dragon's choice that is 
    //               // within 120 feet of the dragon and aware of it must succeed on a 
    //               // DC 17 Wisdom saving throw or become frightened for 1 minute. 
    //               // A creature can repeat the saving throw at the end of each of its turns, 
    //               // ending the effect on itself on a success. If a creature's saving throw 
    //               // is successful or the effect ends for it, the creature is immune to the 
    //               // dragon's Frightful Presence for the next 24 hours.

    //               if (ca.range.length > 0 && ca.effects.length > 0) {
    //                 const pieces = ca.range.split("/");
    //                 const range1 = +pieces[0];
    //                 const range2 = pieces.length > 1 ? +pieces[1] : 0;
    //                 if (!isNaN(range1) && !isNaN(range2)) {
    //                   ca.effects[0].target_int.range.base = range1;
    //                   ca.effects[0].target_int.range2.base = range2;
    //                 }
    //               }
    //               // Lightning Breath (Recharge 5–6)
    //               // Ink Cloud (1/Day)
    //               if (ca.name.includes("(Recharge 5-6)")) {
    //                 ca.consumed_resource.recharge_die = 6;
    //                 ca.consumed_resource.recharge_on = [5,6];
    //                 ca.consumed_resource.resource_consumed = "Recharge";
    //               } else if (ca.name.includes("(Recharge 4-6)")) {
    //                 ca.consumed_resource.recharge_die = 6;
    //                 ca.consumed_resource.recharge_on = [4,5,6];
    //                 ca.consumed_resource.resource_consumed = "Recharge";
    //               } else if (ca.name.includes("(Recharge 6)")) {
    //                 ca.consumed_resource.recharge_die = 6;
    //                 ca.consumed_resource.recharge_on = [6];
    //                 ca.consumed_resource.resource_consumed = "Recharge";
    //               } else if (ca.name.includes("/Day)")) {
    //                 ca.consumed_resource.resource_consumed = "Special";
    //                 ca.consumed_resource.amount_consumed = 1;
    //                 ca.consumed_resource.special_resource_refresh_rule = "Long Rest";
    //                 try {
    //                   let pieces = ca.name.split("(");
    //                   pieces = pieces[1].split("/");
    //                   const x_day = +pieces[0];
    //                   if (!isNaN(x_day)) {
    //                     ca.consumed_resource.special_resource_amount = new UpgradableNumber(x_day);
    //                   }
    //                 } catch (ex) {
    //                   ca.consumed_resource.special_resource_amount = new UpgradableNumber(1);
    //                 }
    //               }
    //             }
    //             return ca;
    //           }
    //         }
    //         new_descs = [];
    //         for (const dv of descVersions) {
    //           const temp_ff = makeFF(dv);
              
    //           if (temp_ff && !descVersions.includes(temp_ff.description)) {
    //             new_descs.push(temp_ff.description);
    //           }
    //         }
    //         descVersions.push(...new_descs);
    //         ff = makeFF(desc_helper);
    //         if (ff && !descVersions.includes(ff.description)) {
    //           descVersions.push(ff.description);
    //         }
            
    //         const desc = common_util.cleanSpaces(ff ? `${ff.description}` : `${content_str}`);
    //         if (!descVersions.includes(desc)) {
    //           descVersions.push(desc);
    //         }
    //         if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
    //           console.log([...descVersions]);
    //         }
    //         new_descs = [];
    //         for (const dv of descVersions) {
    //           const new_desc = common_util.cleanSpaces(dv);
    //           if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
    //             new_descs.push(new_desc);
    //           }
    //         }
    //         descVersions.push(...new_descs);
    //         let ffsWithName: FloatingFeature[] = [];
    //         if (ff) {
    //           if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
    //             console.log([...descVersions]);
    //           }
    //           const feature_type = ff.feature_type;
    //           ffsWithName = ff && ff.feature_type === "Creature Ability" ? 
    //             api.all_creature_features.filter(o => o.name === name) :
    //             api.all_floating_features.filter(o => o.feature_type === feature_type && o.name === name);
    //         }
    //         if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
    //           console.log([...descVersions]);
    //           console.log(ffsWithName.map(o => o.description));
    //         }
    //         content_str = desc; // Update the fb's description to be the best cleaned one, so we can update the ff to use it as well.
    //         let old_ff = ffsWithName.find(o => 
    //           common_util.cleanCompare(o.description, desc) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
    //         );
    //         if (!old_ff) {
    //           for (const dv of descVersions) {
    //             old_ff = ffsWithName.find(o => 
    //               common_util.cleanCompare(o.description, dv) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
    //             );
    //             if (old_ff) {
    //               break;
    //             }
    //           }
    //         }
    //         if (old_ff) {
    //           ff = old_ff;
    //         }
    //       }
    //       if (ff) {
    //         // debugger;
    //         if (fbType === "Actions" || fbType === "Bonus Actions" || fbType === "Reactions") {
    //           const feature = new Feature();
    //           feature.floating_feature = ff;
    //           feature.floating_feature_id = ff._id;
    //           fb.creature_fb_type = "Actions";
    //           fb.name = ff.name;
    //           fb.level = c.min_challenge_rating;
    //           fb.features.push(feature);
    //         } else if (fbType === "Legendary Actions") {
    //           const feature = new Feature();
    //           feature.floating_feature = ff;
    //           feature.floating_feature_id = ff._id;
    //           fb.creature_fb_type = "Legendary Actions";
    //           fb.name = ff.name;
    //           fb.level = c.min_challenge_rating;
    //           fb.features.push(feature);
    //         } else if (fbType === "Lair Actions") {
    //           const feature = new Feature();
    //           feature.floating_feature = ff;
    //           feature.floating_feature_id = ff._id;
    //           fb.creature_fb_type = "Lair Actions";
    //           fb.name = ff.name;
    //           fb.level = c.min_challenge_rating;
    //           fb.features.push(feature);
    //         } else if (fbType === "Traits") {
    //           const feature = new Feature();
    //           feature.floating_feature = ff;
    //           feature.floating_feature_id = ff._id;
    //           fb.creature_fb_type = "Traits";
    //           fb.name = ff.name;
    //           fb.level = c.min_challenge_rating;
    //           fb.features.push(feature);
    //         } else {
    //           // console.log(action_type);
    //           // console.log(content_str);
    //           debugger;
    //         }
    //       } else {
    //         // console.log(action_type);
    //         // console.log(content_str);
    //         debugger;
    //       }

    //       fb.name = name;
    //       fb.description = content_str;
    //       c.fbs.push(fb);
    //     }
    //   }

    //   for (let row_index = 0; row_index < the_rows.length; ++row_index) {
    //     const row = the_rows[row_index];
    //     const rowText = row.innerText;
    //     if (c.name === "") {
    //       // <tr class="c38">
    //       //   <td class="c73" colspan="6" rowspan="1">
    //       //     <h1 class="c13 c51 c72" id="h.hljn1xgkggk5"><span class="c22 c27 c34">INTONER</span><span
    //       //         class="c22 c27 c48"><br></span><span class="c56 c24 c27 c65">Medium Undead, Any Alignment</span></h1>
    //       //   </td>
    //       // </tr>
    //       const h1s = row.getElementsByTagName("h1");
    //       if (h1s.length === 1 && h1s[0].childElementCount > 1 && h1s[0].children[0] instanceof HTMLElement) {
    //         c.name = h1s[0].children[0].innerText;
    //       }
    //       let content = rowText.replace(c.name, "").trim();
    //       c.name = common_util.capitalize_firsts(c.name.trim().toLocaleLowerCase());
    //       existing_c = old_creatures.find(o => o.name === c.name);
    //       if (existing_c) {
    //         await api.loadObject(existing_c);
    //       }
          
    //       if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Metadata") {
    //         debugger;
    //       }
    //       const pieces = content.split(",");
    //       const pieces2 = common_util.cleanForCompare(pieces[0]).split(" ");
    //       c.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(pieces2[0])];
    //       if (pieces2.length === 2) {
    //         const ct_str = common_util.capitalize_first(pieces2[1]);
    //         if (isCreatureType(ct_str)) {
    //           c.creature_type = ct_str;
    //         }
    //       } else if (pieces2.length > 2) {
    //         // Huge fiend (demon), chaotic evil
    //         const ct_str = common_util.capitalize_first(pieces2[1]);
    //         if (isCreatureType(ct_str)) {
    //           c.creature_type = ct_str;
    //         }
    //         let subtype_str = pieces[0].substring(common_util.cleanForCompare(pieces[0]).indexOf(pieces2[1]) + pieces2[1].length).trim();
    //         subtype_str = common_util.capitalize_firsts(subtype_str.replace("(", "").replace(")", ""));
    //         c.subtypes = [subtype_str];
    //       } else {
    //         debugger;
    //       }
    //       if (common_util.cleanForCompare(c.name).includes(c.creature_type.toLocaleLowerCase())) {
    //         c.small_name = c.creature_type.toLocaleLowerCase();
    //       }
    //       const alignment_string = pieces[1].trim();
    //       c.alignment = alignment_string;
    //     } else if (rowText.includes("Armor Class") && c.armor_class.isBlank()) {
    //       const p_els = row.getElementsByTagName("p");
    //       for (let p_index = 0; p_index < p_els.length; ++p_index) {
    //         const p = p_els[p_index];
    //         if (c.armor_class.isBlank() && p.innerText.includes("Armor Class")) {
    //           let pieces = common_util.cleanForCompare(p.innerText).split(" ");
    //           if (pieces.length > 2) {
    //             c.armor_class.base = +pieces[2];
    //             pieces = pieces.slice(3);
    //             c.ac_flavor = pieces.join(" ").replace("(","").replace(")","");
    //           }
    //         } else if (c.hit_dice.length === 0 && p.innerText.includes("Hit Points")) {
    //           let index = p.innerText.indexOf("(");
    //           let hd_str = p.innerText.substring(index + 1).replace(")", "");
    //           index = hd_str.indexOf(" ");
    //           if (index > 0) {
    //             hd_str = hd_str.substring(0, index);
    //           } else {
    //             index = hd_str.indexOf("+");
    //             if (index > 0) {
    //               hd_str = hd_str.substring(0, index);
    //             } else {
    //               index = hd_str.indexOf("-");
    //               if (index > 0) {
    //                 hd_str = hd_str.substring(0, index);
    //               } 
    //             }
    //           }
    //           const pieces = hd_str.split("d");
    //           if (pieces.length === 2) {
    //             const hd = new HitDiceUpgradable();
    //             hd.count.base = +pieces[0];
    //             c.hit_dice.push(hd);
    //           }
    //         } else if (Object.keys(attr_entry.speeds).length === 0 && p.innerText.includes("Speed")) {
    //           if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Speed") {
    //             debugger;
    //           }
    //           let spd_str = p.innerText;
    //           const pieces = common_util.cleanForCompare(spd_str).split(" ");
    //           let spd_type: SpeedType = "walk";
    //           for (const piece of pieces) {
    //             if (isSpeedType(piece)) {
    //               spd_type = piece;
    //             } else if (spd_type === "fly" && piece.includes("hover")) {
    //               attr_entry.hover = true;
    //             } else if (!piece.includes("ft.")) {
    //               attr_entry.speeds[spd_type] = +piece;
    //             }
    //           }
    //         }
    //       }
    //     } else {
    //       if (c.ability_scores.strength.isBlank() && rowText.includes("STR")) {
    //         // <tr class="c67">
    //         //   <td class="c45" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">STR</span></p>
    //         //     <p class="c6"><span class="c1 c37 c27">11 (+0)</span></p>
    //         //   </td>
    //         //   <td class="c36" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">DEX</span></p>
    //         //     <p class="c6"><span class="c1 c27">20 (+5)</span></p>
    //         //   </td>
    //         //   <td class="c36" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">CON</span></p>
    //         //     <p class="c6"><span class="c1 c27">16 (+3)</span></p>
    //         //   </td>
    //         //   <td class="c36" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">INT</span></p>
    //         //     <p class="c6"><span class="c1 c27">18 (+4)</span></p>
    //         //   </td>
    //         //   <td class="c36" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">WIS</span></p>
    //         //     <p class="c6"><span class="c1 c27">14 (+2)</span></p>
    //         //   </td>
    //         //   <td class="c71" colspan="1" rowspan="1">
    //         //     <p class="c6"><span class="c4 c37">CHA</span></p>
    //         //     <p class="c6"><span class="c1 c27">23 (+6)</span></p>
    //         //   </td>
    //         // </tr>
    //         const cells = row.cells;
    //         for (let p_index = 0; p_index < cells.length; ++p_index) {
    //           const cell = cells[p_index];
    //           const p_els = cell.getElementsByTagName("p");
    //           const stat_p = p_els[0];
    //           const score_p = p_els[1];
    //           const stat = common_util.cleanForCompare(stat_p.innerText.trim()).toLocaleUpperCase();
    //           if (isStatType(stat)) {
    //             const pieces = common_util.cleanForCompare(score_p.innerText).split(" ");
    //             const score = +pieces[0];
    //             // console.log(score);
    //             if (!isNaN(score)) {
    //               // console.log(`${stat}: ${score}`);
    //               c.ability_scores.setAbilityScore(stat, score);
    //             } else {
    //               debugger;
    //             }
    //           } else {
    //             debugger;
    //           }
    //         }
    //       } else if (rowText.includes("Challenge") && c.challenge_rating === 0) {
    //         // <tr class="c8">
    //         //   <td class="c25" colspan="6" rowspan="1">
    //         //     <p class="c35"><span class="c4">Saving Throws</span><span class="c1 c37 c27">&nbsp;DEX +11, INT +10, CHA
    //         //         +13</span></p>
    //         //     <p class="c35"><span class="c4">Skills</span><span class="c1 c37 c27">&nbsp;Arcana +10, Deception +13, Insight
    //         //         +8, Performance +13, Persuasion +13</span></p>
    //         //     <p class="c35"><span class="c4">Damage Resistances</span><span class="c1 c27 c37">&nbsp;Cold, Necrotic, Poison;
    //         //         Bludgeoning, Piercing, and Slashing from Nonmagical Attacks</span></p>
    //         //     <p class="c35"><span class="c4">Condition Immunities</span><span class="c1 c27">&nbsp;Charmed, Exhaustion,
    //         //         Frightened, Paralyzed, Poisoned<br></span><span class="c4">Senses </span><span class="c1 c37 c27">Darkvision
    //         //         120 ft., passive Perception 12</span></p>
    //         //     <p class="c35"><span class="c4">Languages</span><span class="c1 c37 c27">&nbsp;The languages it knew in
    //         //         life</span></p>
    //         //     <p class="c35"><span class="c4">Challenge </span><span class="c1 c27">18 (20,000 XP) &nbsp; &nbsp; &nbsp; &nbsp;
    //         //         &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
    //         //         &nbsp; &nbsp; &nbsp; &nbsp;</span><span class="c4">Proficiency Bonus</span><span
    //         //         class="c1 c27">&nbsp;+6</span></p>
    //         //   </td>
    //         // </tr>
    //         const p_els = row.getElementsByTagName("p");
    //         for (let p_index = 0; p_index < p_els.length; ++p_index) {
    //           const p = p_els[p_index];
    //           if (p.innerText.includes("Challenge") && c.challenge_rating === 0) {
    //             const pieces = p.innerText.replace("Challenge", "").trim().split(" ");
    //             if (pieces.length > 0) {
    //               const cr_str = pieces[0];
    //               if (cr_str.includes("/")) { // 1/4 or 1/2
    //                 const pieces2 = cr_str.split("/");
    //                 const num = +pieces2[0];
    //                 const den = +pieces2[1];
    //                 if (!isNaN(num) && !isNaN(den)) {
    //                   c.challenge_rating = num / den;
    //                 } else {
    //                   debugger;
    //                 }
    //               } else {
    //                 const num = +cr_str;
    //                 if (!isNaN(num)) {
    //                   c.challenge_rating = num;
    //                 } else {
    //                   debugger;
    //                 }
    //               }
    //             } else {
    //               debugger;
    //             }
    //           } else if (p.innerText.includes("Saving Throws") && attr_entry.save_profs.length === 0) {
    //             const pieces = common_util.cleanForCompare(p.innerText).split(" ");
    //             for (const piece of pieces) {
    //               const stat = piece.replace(",", "").toLocaleUpperCase();
    //               if (isStatType(stat)) {
    //                 attr_entry.save_profs.push(stat);
    //               }
    //             }
    //           } else if (p.innerText.includes("Skills") && !skills_el) {
    //             skills_el = p; // It needs to be processed after we know the challenge rating.
    //           } else if (p.innerText.includes("Resistances") && attr_entry.resistances_string.length === 0) {
    //             // debugger;
    //             // <p class="c35"><span class="c4">Damage Resistances</span><span class="c1 c27 c37">&nbsp;Cold, Necrotic, Poison;
    //             //     Bludgeoning, Piercing, and Slashing from Nonmagical Attacks</span></p>
    //             let str = common_util.cleanForCompare(p.innerText);
    //             str = str.substring(str.indexOf("damage resistances") + 18).trim();
    //             const pieces = str.split(";");
    //             // debugger;
    //             if (pieces.length === 1) {
    //               // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
    //               const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //               let bad = false;
    //               for (const piece of pieces2) {
    //                 if (isDamageType(piece)) {
    //                   attr_entry.damage_resistances.push(piece);
    //                 } else {
    //                   bad = true;
    //                   break;
    //                 }
    //               }
    //               if (bad) {
    //                 attr_entry.damage_resistances = [];
    //                 attr_entry.extra_damage_resistances = pieces2.join(", ");
    //               }
    //             } else {
    //               const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //               for (const piece of pieces2) {
    //                 if (isDamageType(piece)) {
    //                   attr_entry.damage_resistances.push(piece);
    //                 } else {
    //                   debugger; // Shouldn't come here.
    //                 }
    //               }
    //               attr_entry.extra_damage_resistances = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
    //             }
    //           } else if (p.innerText.includes("Immunities") || p.innerText.includes("Senses")) {
    //             // <p class="c35"><span class="c4">Condition Immunities</span><span class="c1 c27">&nbsp;Charmed, Exhaustion,
    //             //   Frightened, Paralyzed, Poisoned<br></span><span class="c4">Senses </span><span class="c1 c37 c27">Darkvision
    //             //   120 ft., passive Perception 12</span></p>
    //             // debugger;
    //             let what_were_doing = "";
    //             const children = p.children;
    //             for (let c_index = 0; c_index < children.length; ++c_index) {
    //               const child = children[c_index];
    //               if (child instanceof HTMLSpanElement) {
    //                 let str = common_util.cleanForCompare(child.innerText);
    //                 if (str.includes("immunities") || str.includes("senses")) {
    //                   what_were_doing = str;
    //                 } else if (what_were_doing === "condition immunities") {
    //                   const pieces = str.split(" ");
    //                   const conditions = api.conditions;
    //                   for (const piece of pieces) {
    //                     const cond_str = common_util.capitalize_first(piece).replace(",", "").trim();
    //                     const cond = conditions.find(o => o.name === cond_str);
    //                     if (cond) {
    //                       attr_entry.condition_immunities.push(cond.name);
    //                     } else {
    //                       debugger;
    //                     }
    //                   }
    //                 } else if (what_were_doing === "damage immunities") {
    //                   let str = common_util.cleanForCompare(p.innerText);
    //                   str = str.substring(str.indexOf("damage immunities") + 17).trim();
    //                   const pieces = str.split(";");
    //                   // debugger;
    //                   if (pieces.length === 1) {
    //                     // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
    //                     const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //                     let bad = false;
    //                     for (const piece of pieces2) {
    //                       if (isDamageType(piece)) {
    //                         attr_entry.damage_immunities.push(piece);
    //                       } else {
    //                         bad = true;
    //                         break;
    //                       }
    //                     }
    //                     if (bad) {
    //                       attr_entry.damage_immunities = [];
    //                       attr_entry.extra_damage_immunities = pieces2.join(", ");
    //                     }
    //                   } else {
    //                     const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //                     for (const piece of pieces2) {
    //                       if (isDamageType(piece)) {
    //                         attr_entry.damage_immunities.push(piece);
    //                       } else {
    //                         debugger; // Shouldn't come here.
    //                       }
    //                     }
    //                     attr_entry.extra_damage_immunities = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
    //                   }
    //                 } else if (what_were_doing === "senses") {
    //                   if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Senses") {
    //                     debugger;
    //                   }
    //                   const pieces = common_util.cleanForCompare(p.innerText).split(" ");
    //                   let sense: Sense | undefined;
    //                   for (const piece of pieces) {
    //                     const sense_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
    //                     if (sense) {
    //                       const num = +sense_str;
    //                       if (!isNaN(num)) {
    //                         attr_entry.senses[sense.name] = num;
    //                         continue;
    //                       } else {
    //                         sense = undefined;
    //                       }
    //                     }
    //                     const sense_maybe = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ", "").includes(sense_str));
    //                     if (sense_maybe) {
    //                       sense = sense_maybe;
    //                     }
    //                   }
    //                 }
    //               }
    //             }
    //           } else if (p.innerText.includes("Vulnerabilities")) {
    //             let str = common_util.cleanForCompare(p.innerText);
    //             str = str.substring(str.indexOf("damage vulnerabilities") + 22).trim();
    //             const pieces = str.split(";");
    //             // debugger;
    //             if (pieces.length === 1) {
    //               // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
    //               const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //               let bad = false;
    //               for (const piece of pieces2) {
    //                 if (isDamageType(piece)) {
    //                   attr_entry.damage_vulnerabilities.push(piece);
    //                 } else {
    //                   bad = true;
    //                   break;
    //                 }
    //               }
    //               if (bad) {
    //                 attr_entry.damage_vulnerabilities = [];
    //                 attr_entry.extra_damage_vulnerabilities = pieces2.join(", ");
    //               }
    //             } else {
    //               const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
    //               for (const piece of pieces2) {
    //                 if (isDamageType(piece)) {
    //                   attr_entry.damage_vulnerabilities.push(piece);
    //                 } else {
    //                   debugger; // Shouldn't come here.
    //                 }
    //               }
    //               attr_entry.extra_damage_vulnerabilities = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
    //             }
    //           } else if (p.innerText.includes("Languages")) {
    //             const language_str = p.innerText.replace("Languages", "").trim();
    //             attr_entry.languages = language_str;
    //           }
    //         }
    //       } else {
    //         const compareText = common_util.cleanForCompare(rowText);
    //         if (compareText === "actions") {
    //           // <tr class="c64">
    //           //   <td class="c18" colspan="6" rowspan="1">
    //           //     <p class="c13"><span class="c27 c28">&nbsp;</span><span class="c11">A</span><span class="c0">CTIONS</span></p>
    //           //   </td>
    //           // </tr>
    //           fbType = "Actions";
    //         } else if (compareText === "legendary actions") {
    //           fbType = "Legendary Actions";
    //         } else if (compareText === "lair actions") {
    //           fbType = "Lair Actions";
    //         } else if (compareText === "reactions") {
    //           fbType = "Reactions";
    //         } else if (compareText === "bonus actions") {
    //           fbType = "Bonus Actions";
    //         } else {
    //           const p_els = row.getElementsByTagName("p");
    //           let p: HTMLParagraphElement | undefined;
    //           for (let p_index = 0; p_index < p_els.length; ++p_index) {
    //             p = p_els[p_index];
    //             const p_text = p.innerText;
    //             if (p_text.length > 0) {
    //               if (fbType === "Legendary Actions" && p_text.includes("Only one legendary action option")) {
    //                 // <p class="c13"><span class="c5 c1">The intoner can take 3 legendary actions, choosing from the options below.
    //                 //   Only one legendary action option can be used at a time and only at the end of another creature’s turn. The
    //                 //   intoner regains spent legendary actions at the start of its turn.</span></p>
    //                 let desc = p_text;
    //                 let index = desc.indexOf("can take");
    //                 desc = desc.substring(index + 8);
    //                 index = desc.indexOf("legendary actions");
    //                 desc = desc.substring(0, index).trim();
    //                 const pieces = common_util.cleanForCompare(desc).split(" ");
    //                 const num = pieces.length > 0 ? +pieces[0] : 0;
    //                 if (pieces.length === 1 && !isNaN(num)) {
    //                   leg_entry.actions = num;
    //                 } else {
    //                   debugger;
    //                 }
    //               } else if (fbType === "Lair Actions" && p_text.includes("On initiative count 20")) {
    //                 // <p class="c13"><span class="c5 c1">On initiative count 20 (losing initiative ties), the intoner can take a lair
    //                 //   action to cause one of the following effects; the intoner can’t use the same effect two rounds in a
    //                 //   row.</span></p>
    //               } else {
    //                 // Sometimes this is inside a row with others because of this odd nesting of <p> in <tr>
    //                 const compareText2 = common_util.cleanForCompare(p_text);
    //                 if (compareText2 === "actions") {
    //                   // <tr class="c64">
    //                   //   <td class="c18" colspan="6" rowspan="1">
    //                   //     <p class="c13"><span class="c27 c28">&nbsp;</span><span class="c11">A</span><span class="c0">CTIONS</span></p>
    //                   //   </td>
    //                   // </tr>
    //                   fbType = "Actions";
    //                 } else if (compareText2 === "legendary actions") {
    //                   fbType = "Legendary Actions";
    //                 } else if (compareText2 === "lair actions") {
    //                   fbType = "Lair Actions";
    //                 } else if (compareText2 === "reactions") {
    //                   fbType = "Reactions";
    //                 } else if (compareText2 === "bonus actions") {
    //                   fbType = "Bonus Actions";
    //                 } else {
    //                   processFB(p, []);
    //                 }
    //               }
    //             }
    //           }
    //           if (p && fbType === "Lair Actions") {
    //             // Check for a <ul> after it.
    //             let nextElementSibling = p.nextElementSibling;
    //             if (nextElementSibling instanceof HTMLUListElement) {
    //               // For some reason, Lair Actions are generally not given names, both by WotC and third parties, 
    //               // so we just need to put the descriptions in.
                  
    //               const li_els = nextElementSibling.getElementsByTagName("li");
    //               for (let li_index = 0; li_index < li_els.length; ++li_index) {
    //                 const li = li_els[li_index];
    //                 processFB(li, []);
    //               }
    //             }
    //           }
    //         }
    //       }
    //     }
    //   }

    //   if (skills_el) {
    //     // <p class="c35"><span class="c4">Skills</span><span class="c1 c37 c27">&nbsp;Arcana +10, Deception +13, Insight
    //     //     +8, Performance +13, Persuasion +13</span></p>
    //     const pieces = common_util.cleanForCompare(skills_el.innerText).split(" ");
    //     const pb = c.proficiency_modifier;
    //     let skill: Skill | undefined;
    //     for (let k = 0; k < pieces.length; ++k) {
    //       const piece = pieces[k].replace(",", "").replace("+", "");
    //       let skill_mod = +piece;
    //       if (isNaN(skill_mod)) {
    //         skill = SKILLS.find(o => o.name === common_util.capitalize_first(piece));
    //       } else if (skill) {
    //         const stat_score = c.ability_scores.getBasicScore(skill.use_ability_score);
    //         const stat_mod = common_util.modFromScore(stat_score);
    //         skill_mod -= stat_mod;
    //         attr_entry.skill_profs[skill._id] = skill_mod / pb;
    //       }
    //     }
    //   }
    //   attr_entry.level = c.challenge_rating;
    //   tbl_entry.level = c.challenge_rating;
    //   if (leg_entry.resistances > 0 || leg_entry.actions > 0) {
    //     leg_entry.level = c.challenge_rating;
    //     c.legendaries_table.entries = [leg_entry];
    //   }
    //   if (Object.keys(resources_entry.data).length > 0) {
    //     resources_entry.level = c.challenge_rating;
    //     c.resources_table.entries = [resources_entry];
    //   }
    // }
    // c.max_challenge_rating = c.challenge_rating;
    // c.min_challenge_rating = c.challenge_rating;
    // if (existing_c) {
    //   if (existing_c.name.includes(looking_for_creature_name)) {
    //     // debugger;
    //     console.log(existing_c.name);
    //   }
    //   existing_c.size_ugn.copy(c.size_ugn);
    //   existing_c.armor_class.copy(c.armor_class);
    //   existing_c.ac_flavor = c.ac_flavor;
    //   existing_c.hit_dice = c.hit_dice;
    //   existing_c.ability_scores = c.ability_scores;
    //   existing_c.fbs = c.fbs;
    //   existing_c.attributes_table.entries = c.attributes_table.entries;
    //   existing_c.table.entries = c.table.entries;
    //   existing_c.legendaries_table.entries = c.legendaries_table.entries;
    //   existing_c.spellcasting_table.entries = c.spellcasting_table.entries;
    //   existing_c.spellcasting_ability = c.spellcasting_ability;
    //   existing_c.resources_table.entries = c.resources_table.entries;
    //   existing_c.small_name = c.small_name;
    //   existing_c.creature_type = c.creature_type;
    //   existing_c.subtypes = c.subtypes;
    //   return existing_c;
    // } else {
    //   const c_name = c.name;
    //   const old_dominant = all_creatures.find(o => o.name === c_name && o.isDominant());
    //   if (old_dominant) {
    //     // c.dominance_level = old_dominant.dominance_level + 1;
    //     c.variant_of_id = old_dominant.variant_group_id;
    //   }
    //   return c;
    // }
  }

  // This is my attempt to be able to easily parse Pointy Hat's stat blocks
  const parseTablePH = async (m_el: Element, sb_id: string, looking_for_creature_name: string, looking_for_fb_name: string) => {
    const all_creatures = await api.getObjects<Creature>({ data_type: "creature" });
    const old_creatures = all_creatures.filter(o => o.source_book_ids.includes(sb_id));

    const c = new Creature();
    c.ability_scores.strength = new UpgradableNumber(); // Force it to be blank, because this can't be true, so we can check if it has a valid value later.
    
    if (sb_id) {
      c.source_books_old = [{ type: "Source Book", id: sb_id, third_party: false }];
      c.source_books = [];
      const sb = api.source_books.find(o => o._id === sb_id);
      if (sb) {
        c.source_books.push(sb);
      }
    }

    const attr_entry = new Attributes();
    const tbl_entry = new TableEntry();
    const resources_entry = new TableEntry();
    c.attributes_table.entries = [attr_entry];
    c.table.entries = [tbl_entry];
    let legendary = false;
    const leg_entry = new Legendaries();
    let existing_c: Creature | undefined;
    const table_els = m_el.getElementsByTagName("table");
    const the_table = table_els[0];
    if (the_table) {
      const the_rows = the_table.rows;
      let skills_el: HTMLElement | undefined;
      let fbType: CreatureFBType = "Traits";

      const processFB = (p: HTMLElement, extra_ps: HTMLElement[]) => {
        // It's probably a FeatureBase
        // <p class="Stat-Block-Styles_Stat-Block-Body"><span
        //     class="Sans-Serif-Character-Styles_Inline-Subhead-Sans-Serif">Amphibious.</span>&nbsp;The aboleth can breathe air
        //   and water.</p>
        // if (c.name.includes(looking_for_creature_name)) {
        //   const hangings = m_el.getElementsByClassName("Stat-Block-Styles_Stat-Block-Hanging");
        //   for (let k = 0; k < hangings.length; ++k) {
        //     const hanging = hangings[k];
        //     console.log(hanging.outerHTML);
        //   }
        // }
        
        const name_spans = p.getElementsByTagName("span");
        const name_strongs = p.getElementsByTagName("strong");
        let name = "";
        if (name_spans.length > 0 || name_strongs.length > 0) {
          const name_span = name_spans.length > 0 ? name_spans[0] : name_strongs[0];
          name = name_span.innerText.trim();
          if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
            console.log(name);
          }
          if (name.endsWith(".")) {
            name = name.substring(0, name.length - 1);
          }
          name = common_util.normalizeDashes(name);
          name = common_util.replaceAll(name, "’", "'");
        }
        let desc0 = common_util.normalizeSpaces(p.innerText.substring(name.length + 1)).trim();
        if (desc0.startsWith(".")) {
          desc0 = desc0.substring(1).trim();
        }
        for (const pk of extra_ps) {
          desc0 += `<p>${pk.innerText}</p>`;
        }
        if (fbType === "Lair Actions" && name.length > 50) {
          // debugger;
          desc0 = name + " " + desc0;
          name = `${c.name} Lair Action ${c.lair_action_fbs.length + 1}`;
        }
        let content_str = `${desc0}`;
        if (content_str.includes("cost it extra moment")) {
          content_str = common_util.replaceAll(content_str, "cost it extra moment", "cost it extra movement");
        }

        if (name.includes("Legendary Resistance")) {
          // Not a FeatureBase. We store these in the legendaries table
          legendary = true;
          let legendary_resistances_str = name.split("(")[1];
          if (legendary_resistances_str) {
            let lair_str = "";
            if (legendary_resistances_str.includes(" or ")) {
              lair_str = legendary_resistances_str.split(" or ")[1];
              lair_str = lair_str.substring(0, lair_str.indexOf("/"));
              
              leg_entry.res_lair = +lair_str;
              leg_entry.actions = 0;
            }
            legendary_resistances_str = legendary_resistances_str.substring(0, legendary_resistances_str.indexOf("/"));
            leg_entry.resistances = +legendary_resistances_str;
          }
        } else if (name.includes("Spellcasting")) {
          // Not a FeatureBase. We store this in the spellcasting table.
          // <p class="c13"><span class="c12">Spellcasting.</span><span class="c17">&nbsp;</span><span class="c1 c5">The
          //     intoner casts one of the following spells, requiring no material components and using Charisma as its
          //     spellcasting ability (spell save DC 19, +11 to hit with spell attacks):</span></p>
          // <p class="c3"><span class="c5 c1"></span></p>
          // <ul class="c20 lst-kix_wqejzcrszqhw-0 start">
          //   <li class="c13 c44 li-bullet-0"><span class="c1">At will: </span><span class="c19 c1">minor illusion,
          //       prestidigitation, vicious mockery, charm person</span></li>
          //   <li class="c13 c44 li-bullet-0"><span class="c1">3/day each: counterspell, </span><span class="c19 c1">cure
          //       wounds, shatter, suggestion, thunderwave (cast as a 3rd-level spell)</span></li>
          //   <li class="c13 c44 li-bullet-0"><span class="c1">2/day each: </span><span class="c19 c1">confusion, dominate
          //       person, modify memory</span></li>
          //   <li class="c13 c44 li-bullet-0"><span class="c1">1/day each: </span><span class="c1 c24">irresistible dance,
          //       mass suggestion, finger of death</span></li>
          // </ul>
          if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Spellcasting") {
            debugger;
          }
          const sc_entry = new SpellcastingEntry();
          sc_entry.level = c.challenge_rating;
          sc_entry.description = content_str;
          if (sc_entry.description.toLocaleLowerCase().includes("spellcasting ability is ")) {
            let spellcasting_ability = common_util.capitalize_first(
              sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf("spellcasting ability is ") + 24));
            spellcasting_ability = spellcasting_ability.substring(0, 3).toUpperCase();
            c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
          } else if (sc_entry.description.toLocaleLowerCase().includes("as the spellcasting")) {
            // The aarakocra casts one of the following spells, requiring no Material components 
            // and using Wisdom as the spellcasting ability (spell save DC 13):
            let index = sc_entry.description.toLocaleLowerCase().indexOf("as the spellcasting") - 2;
            while (index > 0 && sc_entry.description[index] !== " ") {
              index--;
            }
            index++;
            const spellcasting_ability = sc_entry.description.substring(index, index + 3).toLocaleUpperCase();
            if (ABILITY_SCORES.includes(spellcasting_ability)) {
              c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
            }
          } else if (sc_entry.description.toLocaleLowerCase().includes("as its spellcasting")) {
            // The aarakocra casts one of the following spells, requiring no Material components 
            // and using Wisdom as the spellcasting ability (spell save DC 13):
            let index = sc_entry.description.toLocaleLowerCase().indexOf("as its spellcasting") - 2;
            while (index > 0 && sc_entry.description[index] !== " ") {
              index--;
            }
            index++;
            const spellcasting_ability = sc_entry.description.substring(index, index + 3).toLocaleUpperCase();
            if (ABILITY_SCORES.includes(spellcasting_ability)) {
              c.spellcasting_ability = spellcasting_ability as AbilityScoreType;
            }
          }
          if (sc_entry.description.toLocaleLowerCase().includes(" dc ")) {
            if (c.dc.isBlank()) {
              let dc_str = sc_entry.description.substring(sc_entry.description.toLocaleLowerCase().indexOf(" dc ") + 4).trim();
              if (dc_str.includes(" ")) {
                dc_str = dc_str.substring(0, dc_str.indexOf(" "));
              }
              dc_str = dc_str.replace(",","").replace(".","").replace(")","").replace(":","").trim();
              const dc_num = +dc_str;
              if (c.spellcasting_ability !== "None" && 
                dc_num === (8 + c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
              ) {
                c.dc = new UpgradableNumber();
                c.dc.base = 8;
                c.dc.add_prof_bonus_mult = 1;
                c.dc.add_ability_mod_mult = 1;
                c.dc.add_ability_scores = [c.spellcasting_ability];
                sc_entry.description = common_util.replaceAll(sc_entry.description, ` dc ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
                sc_entry.description = common_util.replaceAll(sc_entry.description, ` DC ${dc_num}`, ` {dc_string.${c.spellcasting_ability}}`);
              } else {
                c.dc = new UpgradableNumber(dc_num);
              }
            }
          }
          if (sc_entry.description.toLocaleLowerCase().includes(" to hit ")) {
            let attack_str = sc_entry.description.substring(0, sc_entry.description.toLocaleLowerCase().indexOf(" to hit ")).trim();
            attack_str = attack_str.substring(attack_str.lastIndexOf("+")+1);
            const attack_num = +attack_str;
            if (c.spellcasting_ability !== "None" && 
              attack_num === (c.proficiency_modifier + c.current_ability_scores.getModifier(c.spellcasting_ability))
            ) {
              c.magic_attack = new UpgradableNumber();
              c.magic_attack.add_prof_bonus_mult = 1;
              c.magic_attack.add_ability_mod_mult = 1;
              c.magic_attack.add_ability_scores = [c.spellcasting_ability];
              sc_entry.description = common_util.replaceAll(sc_entry.description, `+${attack_num} to hit `, `{attack_string.${c.spellcasting_ability}} to hit `);
            } else {
              c.magic_attack = new UpgradableNumber(attack_num);
            }
          }
          if (c.spellcasting_ability !== "None") {
            const stat = c.getBasicModifier(c.spellcasting_ability);
            const pb = c.proficiency_modifier;

            if (c.dc.base > 0) {
              c.dc.base -= stat + pb;
            } else {
              c.dc.base = 8;
            }
            c.dc.add_prof_bonus_mult = 1;
            c.dc.add_ability_mod_mult = 1;
            c.dc.add_ability_scores.push(c.spellcasting_ability);

            if (c.magic_attack.base > 0) {
              c.magic_attack.base -= stat + pb;
            }
            c.magic_attack.add_prof_bonus_mult = 1;
            c.magic_attack.add_ability_mod_mult = 1;
            c.magic_attack.add_ability_scores.push(c.spellcasting_ability);
            c.spellcasting_table.entries = [sc_entry];

            // Look for a <ul> right after p
            // debugger;
            let nextElem = p.nextElementSibling;
            while (nextElem && !(nextElem instanceof HTMLUListElement)) {
              nextElem = nextElem.nextElementSibling;
            }
            if (nextElem instanceof HTMLUListElement) {
              const li_els = nextElem.getElementsByTagName("li");
              for (let li_index = 0; li_index < li_els.length; ++li_index) {
                const li = li_els[li_index];
                const innerText = common_util.cleanForCompare(li.innerText);
                const start_string = innerText.substring(0, innerText.indexOf(":"));

                let spell_level = -1;
                let cast_type: "Slot" | "At Will" | "X/Day" | "Ritual" = "Slot";
                let x = -1;
                if (start_string.includes("cantrip") || start_string.includes("level")) {
                  const level_char = start_string[0];
                  if (level_char === "c") {
                    spell_level = 0;
                  } else {
                    spell_level = +level_char;
                    if (isNaN(spell_level)) {
                      debugger;
                    }
                  }
                  
                  const pieces = start_string.split("(");
                  if (pieces[1]) {
                    const use_string = pieces[1];
                    if (use_string.includes("at will")) {
                      cast_type = "At Will";
                    } else if (use_string.includes("ritual")) {
                      cast_type = "Ritual";
                    } else if (use_string.includes("/day")) {
                      const pieces2 = use_string.split("/");
                      x = +pieces2[0];
                    } else if (use_string.includes("slot")) {
                      const pieces2 = use_string.split(" ");
                      const slot_count = +pieces2[0];
                      if (!isNaN(slot_count)) {
                        resources_entry.data[`Slot: ${spell_level}`] = slot_count;
                      }
                    } else {
                      debugger;
                    }
                  }
                } else {
                  // Different format.
                  // <p class="Stat-Block-Styles_Stat-Block-Hanging">At will:&nbsp;
                  //   <span><a>detect magic</a></span>,&nbsp;
                  //   <span><a>fog cloud</a></span>,&nbsp;
                  //   <span><a>light</a></span>
                  // </p>
                  // <p class="Stat-Block-Styles_Stat-Block-Hanging">3/day each:&nbsp;
                  //   <span><a>feather fall</a></span>,&nbsp;
                  //   <span><a>fly</a></span>,&nbsp;
                  //   <span><a>misty step</a></span>,&nbsp;
                  //   <span><a>telekinesis</a></span>
                  // </p>
                  if (start_string.includes("at will")) {
                    cast_type = "At Will";
                  } else if (start_string.includes("/day")) {
                    cast_type = "X/Day";
                    const pieces = start_string.split("/");
                    const x_str = pieces[0].trim();
                    x = +x_str;
                    if (isNaN(x)) {
                      debugger;
                    }
                  } else {
                    debugger;
                  }
                }

                // sc_entry.description += `\n${innerText}`;
                const spell_anchors = li.getElementsByTagName("a");
                if (spell_anchors.length > 0) {
                  for (let k = 0; k < spell_anchors.length; ++k) {
                    const spell_anchor = spell_anchors[k];
                    let spell_name = common_util.cleanForCompare(spell_anchor.innerText);
                    spell_name = spell_name.replace("at will:", "").replace(`${x}/day each: `, "").trim();
                    let extra_text: string | undefined;
                    if (spell_name.includes("(")) {
                      let index = spell_name.indexOf("(");
                      let sub = spell_name.substring(index);
                      index = sub.indexOf(")");
                      sub = sub.replace("(","").replace(")","").trim();
                      extra_text = sub;
                      spell_name = spell_name.replace(`(${extra_text})`, "").trim();
                    }
                    let spells_with_name = api.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
                    let spell: Spell | undefined;
                    if (spells_with_name.length === 1) {
                      spell = spells_with_name[0];
                    } else if (spells_with_name.length > 1) {
                      spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
                      if (!spell) {
                        // const max_dominance_level = Math.max(...spells_with_name.map(o => o.dominance_level));
                        spell = spells_with_name[0].getDominant() as Spell; // spells_with_name.find(o => o.dominance_level === max_dominance_level);
                      }
                    }
                    if (spell) {
                      spell_level = spell.level;
                      sc_entry.spells.push({
                        spell_level,
                        spell_id: spell._id,
                        cast_type,
                        x,
                        extra_text
                      });
                    } else {
                      debugger;
                    }
                  }
                } else {
                  const spell_pieces = li.innerText.split(",").map(o => o.trim());
                  let last_spell_id = "";
                  for (let k = 0; k < spell_pieces.length; ++k) {
                    const spell_anchor = spell_pieces[k];
                    let spell_name = common_util.cleanForCompare(spell_anchor);
                    spell_name = spell_name.replace("at will:", "").replace(`${x}/day each: `, "").trim();
                    let extra_text: string | undefined;
                    if (spell_name.includes("(")) {
                      let index = spell_name.indexOf("(");
                      let sub = spell_name.substring(index);
                      index = sub.indexOf(")");
                      sub = sub.replace("(","").replace(")","").trim();
                      extra_text = sub;
                      spell_name = spell_name.replace(`(${extra_text})`, "").trim();
                    }
                    let spells_with_name = api.spells.filter(o => common_util.cleanCompare(o.name, spell_name));
                    let spell: Spell | undefined;
                    if (spells_with_name.length === 1) {
                      spell = spells_with_name[0];
                    } else if (spells_with_name.length > 1) {
                      spell = spells_with_name.find(o => o.source_book_ids.includes(sb_id));
                      if (!spell) {
                        // const max_dominance_level = Math.max(...spells_with_name.map(o => o.dominance_level));
                        spell = spells_with_name[0].getDominant() as Spell; // spells_with_name.find(o => o.dominance_level === max_dominance_level);
                      }
                    }
                    if (spell) {
                      spell_level = spell.level;
                      sc_entry.spells.push({
                        spell_level,
                        spell_id: spell._id,
                        cast_type,
                        x,
                        extra_text
                      });
                      last_spell_id = spell._id;
                    } else if (last_spell_id !== "") {
                      const c_spell = sc_entry.spells.find(o => o.spell_id === last_spell_id);
                      if (c_spell) {
                        if (!c_spell.extra_text) {
                          c_spell.extra_text = "";
                        }
                        c_spell.extra_text += spell_anchor;
                      } else {
                        debugger;
                      }
                    } else {
                      debugger;
                    }
                  }
                }
              }
            }
          }
        } else {
          const fb = new FeatureBase();
          fb.creature_fb_type = fbType;

          const descVersions: string[] = [desc0];
          content_str = common_util.normalizeDashes(content_str);
          content_str = common_util.replaceAll(content_str, "’", "'");
          if (desc0 !== content_str) {
            descVersions.push(content_str);
          }
          if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
            console.log([...descVersions]);
          }

          let ff: FloatingFeature | undefined = undefined; 
          if (!ff) {
            let desc_helper = common_util.cleanSpaces(common_util.replaceAll(common_util.replaceAll(content_str, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`));
            if (!descVersions.includes(desc_helper)) {
              descVersions.push(desc_helper);
            }
            if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
              console.log([...descVersions]);
            }
            let new_descs: string[] = [];
            for (const dv of descVersions) {
              const new_desc = common_util.replaceAll(common_util.replaceAll(dv, ` ${c.name} `, ` {cfs} `), ` ${c.name}'`, ` {cfs}'`);
              if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                new_descs.push(new_desc);
              }
            }
            descVersions.push(...new_descs);
            if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
              console.log([...descVersions]);
            }
            if (desc_helper === content_str) {
              const desc_helper2 = common_util.replaceAll(desc_helper, "\n", "");
              if (c.small_name.length === 0) {
                if (c.name.includes(" ")) {
                  // debugger;
                  const words = common_util.cleanForCompare(c.name).split(" ").map(o => o.toLocaleLowerCase());
                  let small_name = "";
                  for (let i = 0; i < words.length; ++i) {
                    const word = words[i];
                    if (desc_helper2.includes(` ${word} `)) {
                      if (small_name === "") {
                        small_name = word;
                      } else {
                        const prev_word = words[i - 1];
                        if (small_name.includes(prev_word) && desc_helper2.includes(` ${small_name} ${word} `)) {
                          small_name += ` ${word}`;
                        }
                      }
                    }
                  }
                  c.small_name = small_name;
                } else {
                  // Check for some things that don't fit the above method.
                  if (c.name.toLocaleLowerCase().includes("sphinx")) {
                    c.small_name = "sphinx";
                  } else {
                    c.small_name = c.name.toLocaleLowerCase();
                  }
                }
              }
              if (c.small_name.length > 0) {
                if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                  console.log(content_str);
                }
                desc_helper = common_util.replaceAll(content_str, c.small_name, `{cfs}`);
                
                new_descs = [];
                for (const dv of descVersions) {
                  const new_desc = common_util.replaceAll(dv, c.small_name, `{cfs}`);
                  if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                    new_descs.push(new_desc);
                  }
                }
                descVersions.push(...new_descs);
                if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                  console.log([...descVersions]);
                }
              }
            }
            const makeFF = (desc_str: string) => {
              const ff_obj: any = {
                name,
                description: desc_str,
                feature_type: "Creature Ability"
              };
              if (name === "Multiattack") {
                ff_obj.feature_type = "Minion Extra Attacks";
                // Usually says something along the lines of "makes X attacks";
                const sub = content_str.toLocaleLowerCase();
                
                const pieces = common_util.cleanForCompare(sub).split(" ");
                const num_strings = ["two","three","four","five","six","seven","eight","nine","ten","2","3","4","5","6","7","8","9","10"];
                let num_string = common_util.findOverlap(num_strings, pieces);
                if (num_string.length === 0) {
                  // It's more complicated, so just make it 2
                  num_string = "2";
                }
                if (num_string.length > 0) {
                  switch(num_string) {
                    case "two": ff_obj.num = 2; break;
                    case "three": ff_obj.num = 3; break;
                    case "four": ff_obj.num = 4; break;
                    case "five": ff_obj.num = 5; break;
                    case "six": ff_obj.num = 6; break;
                    case "seven": ff_obj.num = 7; break;
                    case "eight": ff_obj.num = 8; break;
                    case "nine": ff_obj.num = 9; break;
                    case "ten": ff_obj.num = 10; break;
                    default: ff_obj.num = +num_string; break;
                  }
                  ff_obj.num -= 1;
                  return new UpgradableNumberFeature(ff_obj);
                }
              } else {
                const ca = new CreatureAbility(ff_obj);
                if (ca instanceof CreatureAbility) {
                  if (c.name.includes(looking_for_creature_name) && ca.name.includes(looking_for_fb_name)) {
                    console.log(desc_str);
                  }
                  let using_ability: AbilityScoreType | "" = "";
                  let using_ability_mod = -5;
                  let using_ability_atk: AbilityScoreType | "" = "";
                  let using_ability_atk_mod = -5;
                  
                  if (desc_str.includes("DC") && !desc_str.includes("rapple")) {
                    // New format: Strength Saving Throw: DC 12
                    // Old format: DC 19 Dexterity saving throw
                    // Needs to work with both
                    // debugger;
                    let dc = 0;
                    let save_type = "";
                    const pieces = common_util.cleanForCompare(desc_str).split(" ").map(o => o.replace(",",""));
                    let dc_index = -1;
                    for (let i = 0; i < pieces.length; ++i) {
                      const piece = pieces[i];
                      if (piece === "dc") {
                        dc_index = i;
                        break;
                      }
                    }
                    const dc_str = pieces[dc_index + 1].replace(",", "").replace(".","").replace(")","").replace(":","");
                    dc = +dc_str;
                    if (isNaN(dc)) {
                      console.error("DC Parse Error");
                      // debugger;
                    } else {
                      // Check New Method first
                      let type_maybe = pieces[dc_index - 3];
                      if (type_maybe.length > 3) {
                        type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                        if (ABILITY_SCORES.includes(type_maybe)) {
                          save_type = type_maybe;
                        }
                      }
                      if (save_type === "") {
                        // Didn't find it, so Check Old Method
                        type_maybe = pieces[dc_index + 2];
                        if (type_maybe) {
                          if (type_maybe.length > 3) {
                            type_maybe = type_maybe.substring(0, 3).toLocaleUpperCase();
                            if (ABILITY_SCORES.includes(type_maybe)) {
                              save_type = type_maybe;
                            }
                          }
                        } else {
                          let spell_index = pieces.indexOf("spell");
                          if (spell_index < 0) {
                            spell_index = pieces.indexOf("Spell");
                          }
                          let spell: ModelBase | undefined = undefined;
                          if (spell_index > -1) {
                            // Check the words before and after spell_index
                            let spell_name = "";
                            let use_index = spell_index - 1;
                            while (use_index > -1) {
                              spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                              // console.log(spell_name);
                              if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                debugger;
                              }
                              
                              spell = api.getSpellByName(spell_name);
                              if (spell) {
                                break;
                              } else {
                                use_index--;
                              }
                            }
                            if (!spell) {
                              spell_name = "";
                              use_index = spell_index + 1;
                              while (use_index < pieces.length) {
                                spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                  debugger;
                                }
                                
                                spell = api.getSpellByName(spell_name);
                                if (spell) {
                                  break;
                                } else {
                                  use_index++;
                                }
                              }
                            }
                          }
                          if (spell instanceof Spell) {
                            const save_effect = spell.effects.find(o => o.attack_type === "Save");
                            if (save_effect) {
                              save_type = save_effect.saving_throw_ability_score;
                            } else {
                              console.error(pieces);
                              console.error(dc_index);
                              console.error(dc);
                              debugger;
                            }
                          } else {
                            spell_index = pieces.indexOf("casts");
                            if (spell_index < 0) {
                              spell_index = pieces.indexOf("cast");
                            }
                            if (spell_index > -1) {
                              // Check the words before and after spell_index
                              let spell_name = "";
                              let use_index = spell_index - 1;
                              while (use_index > -1) {
                                spell_name = (`${pieces[use_index].toLocaleLowerCase()} ${spell_name}`).trim();
                                // console.log(spell_name);
                                if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                  debugger;
                                }
                                
                                spell = api.getSpellByName(spell_name);
                                if (spell) {
                                  break;
                                } else {
                                  use_index--;
                                }
                              }
                              if (!spell) {
                                spell_name = "";
                                use_index = spell_index + 1;
                                while (use_index < pieces.length) {
                                  spell_name = (`${spell_name} ${pieces[use_index].toLocaleLowerCase()}`).trim();
                                  if (c.name.includes(looking_for_creature_name) && spell_name.toLocaleLowerCase().includes("sleep")) {
                                    debugger;
                                  }
                                  
                                  spell = api.getSpellByName(spell_name);
                                  if (spell) {
                                    break;
                                  } else {
                                    use_index++;
                                  }
                                }
                              }
                            }
                            if (spell instanceof Spell) {
                              const save_effect = spell.effects.find(o => o.attack_type === "Save");
                              if (save_effect) {
                                save_type = save_effect.saving_throw_ability_score;
                              } else {
                                console.error(pieces);
                                console.error(dc_index);
                                console.error(dc);
                                // debugger;
                              }
                            } else {
                              console.error(pieces);
                              console.error(dc_index);
                              console.error(dc);
                              debugger;
                            }
                          }
                        }
                      }
                      
                      const ability_mod = dc - 8 - c.proficiency_modifier;
                      
                      if (ca.name.includes("Breath")) {
                        // Breath Weapons the DC is generally based on CON.
                        if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                          using_ability = "CON";
                        }
                      }
                      if (using_ability === "") {
                        // This order is the most likely.
                        if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                          using_ability = "CHA";
                        } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                          using_ability = "STR";
                        } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                          using_ability = "DEX";
                        } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                          using_ability = "WIS";
                        } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                          using_ability = "INT";
                        } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                          using_ability = "CON";
                        }
                      }
                      if (using_ability === "") {
                        // Could be anything.  Just leave it as a base.
                        ca.dc.base = dc;
                      } else {
                        ca.dc.base = 8;
                        ca.dc.add_prof_bonus_mult = 1;
                        ca.dc.add_ability_mod_mult = 1;
                        ca.dc.add_ability_scores.push(using_ability);
                        ca.description = ca.description.replace(`DC ${dc}`, `{dc_string.${using_ability}}`);
                        using_ability_mod = ability_mod;
                      }
                      ca.saving_throw_ability_score = ABILITY_SCORE_FULL_TO_ABBREV_MAP[save_type];
                    }
                  }
                  if (desc_str.includes("Attack Roll:") || desc_str.includes("Attack:")) {
                    // New stuff:
                    // <p><strong><em>Fist.</em></strong> <em>Melee Attack
                    //   Roll:</em> +5, reach 5 ft. <em>Hit:</em> 5 (1d4 + 3) Bludgeoning damage.</p>
                    // Old stuff:
                    // <p><span>Tentacle.&nbsp;</span><span>Melee Weapon Attack:</span>&nbsp;+9 to hit, reach
                    //   10 ft., one target.&nbsp;<span>Hit:</span>&nbsp;12 (2d6 + 5) bludgeoning damage.
                    //   If the target is a creature, it must succeed on a DC 14 Constitution saving throw or become diseased. The
                    //   disease has no effect for 1 minute and can be removed by any magic that cures disease. After 1 minute, the
                    //   diseased creature’s skin becomes translucent and slimy, the creature can’t regain hit points unless it is
                    //   underwater, and the disease can be removed only by <a>heal</a> or
                    //   another disease-curing spell of 6th level or higher. When the creature is outside a body of water, it takes 6
                    //   (1d12) acid damage every 10 minutes unless moisture is applied to the skin before 10 minutes have passed.</p>

                    if (c.name.includes(looking_for_creature_name) && name.includes(looking_for_fb_name)) {
                      debugger;
                    }
                    let attack_num = 0;
                    let index = desc_str.indexOf("Attack Roll:");
                    if (index > -1) {
                      let attack_str = desc_str.substring(index + 12).trim();
                      index = attack_str.indexOf(",");
                      attack_str = attack_str.substring(0, index);
                      attack_str = attack_str.replace("+", "");
                      attack_num = +attack_str;
                    } else {
                      index = desc_str.indexOf("Attack:");
                      if (index > -1) {
                        let attack_str = desc_str.substring(index + 7).trim();
                        index = attack_str.indexOf(",");
                        attack_str = attack_str.substring(0, index);
                        attack_str = attack_str.replace("+", "");
                        attack_str = attack_str.replace("to hit", "").trim();
                        attack_num = +attack_str;
                      }
                    }
                    if (isNaN(attack_num)) {
                      console.error("Attack Parse Error");
                      debugger;
                    } else {
                      // Check New Method first
                      const ability_mod = attack_num - c.proficiency_modifier;
                      
                      if (using_ability_atk === "") {
                        if (desc_str.includes("Spell")) {
                          // This order is the most likely.
                          if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                            using_ability_atk = "CHA";
                          } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                            using_ability_atk = "WIS";
                          } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                            using_ability_atk = "INT";
                          } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                            using_ability_atk = "STR";
                          } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                            using_ability_atk = "DEX";
                          } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability_atk = "CON";
                          }
                        } else if (desc_str.includes("Ranged")) {
                          // This order is the most likely.
                          if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                            using_ability_atk = "DEX";
                          } else if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                            using_ability_atk = "STR";
                          } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                            using_ability_atk = "CHA";
                          } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                            using_ability_atk = "WIS";
                          } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                            using_ability_atk = "INT";
                          } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability_atk = "CON";
                          }
                        } else {
                          // This order is the most likely.
                          if (common_util.modFromScore(c.ability_scores.STR.base) === ability_mod) {
                            using_ability_atk = "STR";
                          } else if (common_util.modFromScore(c.ability_scores.DEX.base) === ability_mod) {
                            using_ability_atk = "DEX";
                          } else if (common_util.modFromScore(c.ability_scores.CHA.base) === ability_mod) {
                            using_ability_atk = "CHA";
                          } else if (common_util.modFromScore(c.ability_scores.WIS.base) === ability_mod) {
                            using_ability_atk = "WIS";
                          } else if (common_util.modFromScore(c.ability_scores.INT.base) === ability_mod) {
                            using_ability_atk = "INT";
                          } else if (common_util.modFromScore(c.ability_scores.CON.base) === ability_mod) {
                            using_ability_atk = "CON";
                          }
                        }
                      }
                      if (using_ability_atk === "") {
                        // Could be anything.  Just leave it as a base.
                        ca.attack_bonus.base = attack_num;
                      } else {
                        ca.attack_bonus.add_prof_bonus_mult = 1;
                        ca.attack_bonus.add_ability_mod_mult = 1;
                        ca.attack_bonus.add_ability_scores.push(using_ability_atk);
                        if (ca.description.includes(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`)) {
                          ca.description = ca.description.replace(`Attack Roll: ${common_util.addPlusMaybe(attack_num)}`, `Attack Roll: {attack_string.${using_ability_atk}}`);
                        } else if (ca.description.includes(`Attack: ${common_util.addPlusMaybe(attack_num)}`)) {
                          ca.description = ca.description.replace(`Attack: ${common_util.addPlusMaybe(attack_num)}`, `Attack: {attack_string.${using_ability_atk}}`);
                        }
                        using_ability_atk_mod = ability_mod;
                      }
                    }
                  }
                  if (desc_str.includes("reach")) {
                    let sub = desc_str.substring(desc_str.indexOf("reach") + 5).trim();
                    sub = sub.substring(0, sub.indexOf(" ")).trim();
                    ca.range = sub;
                  } else if (desc_str.includes("range")) {
                    let sub = desc_str.substring(desc_str.indexOf("range") + 5).trim();
                    sub = sub.substring(0, sub.indexOf(" ")).trim();
                    ca.range = sub;
                  }
                  let damage_str = desc_str.toLocaleLowerCase();
                  const lower_case = ca.description.toLowerCase();
                  // if (name.includes(looking_for_fb_name)) {
                  //   console.log(damage_str);
                  // }
                  while (damage_str.includes("damage")) {
                    let damage_sub = damage_str.substring(0, damage_str.indexOf("damage") - 1).trim();
                    damage_str = damage_str.substring(damage_str.indexOf("damage") + 6);
                    const words = damage_sub.split(" ");
                    // console.log(words);
                    const damage_type = common_util.capitalize_first(words[words.length - 1]);
                    if (damage_sub.includes("(") && damage_sub.includes(")")) {
                      let start_index = damage_sub.indexOf("(");
                      start_index -= 2;
                      while (start_index >= 0 && damage_sub[start_index] !== " ") {
                        start_index--;
                      }
                      let replace_me = damage_sub.substring(start_index);
                      const end_index = replace_me.indexOf(")") + 1;
                      replace_me = replace_me.substring(0, end_index).trim();
                      damage_sub = damage_sub.substring(damage_sub.indexOf("("));
                      damage_sub = damage_sub.substring(0, damage_sub.indexOf(")"));
                      damage_sub = damage_sub.replace("(","").replace(")","");
                      damage_sub = common_util.normalizeDashes(damage_sub);
                      let count = 0;
                      let size = 0;
                      let flat = 0;
                      if (damage_sub.includes("+")) {
                        const flat_str = damage_sub.substring(damage_sub.indexOf("+")).replace("+","");
                        damage_sub = damage_sub.substring(0, damage_sub.indexOf("+")).replace("+","");
                        flat = +flat_str;
                      } else if (damage_sub.includes("-")) {
                        const flat_str = damage_sub.substring(damage_sub.indexOf("-")).replace("-","");
                        damage_sub = damage_sub.substring(0, damage_sub.indexOf("-")).replace("-","");
                        flat = -1 * +flat_str;
                      } 
                      if (damage_sub.includes("d")) {
                        const pieces = damage_sub.split("d");
                        count = +pieces[0];
                        size = +pieces[1];
                      }
                      const effect = new AbilityEffect();
                      effect.type = "Damage";
                      effect.damage_types.push(DAMAGE_TYPES.includes(damage_type) ? damage_type as DamageType : "Typeless");
                      if (ca.effects.length === 0) {
                        const first_part = lower_case.substring(0, 20);
                        if (first_part.includes("melee")) {
                          if (first_part.includes("spell")) {
                            effect.attack_type = "Melee Spell";
                          } else {
                            effect.attack_type = "Melee";
                          }
                        } else if (first_part.includes("ranged")) {
                          if (first_part.includes("spell")) {
                            effect.attack_type = "Ranged Spell";
                          } else {
                            effect.attack_type = "Ranged";
                          }
                        } else if (ca.saving_throw_ability_score !== "") {
                          effect.attack_type = "Save";
                          effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                        }
                        if (ca.range !== "") {
                          try {
                            const range = +ca.range;
                            if (!isNaN(range)) {
                              effect.target_int.range.base = range;
                            }
                          } catch (ex) { }
                        }
                        // X-foot cone
                        if (ca.description.includes("-foot")) {
                          const foot_index = ca.description.indexOf("-foot");
                          let area_str = "";
                          let i = 1;
                          while (ca.description[foot_index - i] !== " ") {
                            area_str = ca.description[foot_index - i] + area_str;
                            ++i;
                          }
                          // debugger;
                          i = 6;
                          let shape_str = "";
                          while (ca.description[foot_index + i] !== " ") {
                            shape_str += ca.description[foot_index + i];
                            ++i;
                          }
                          if (shape_str.endsWith(".")) {
                            shape_str = shape_str.substring(0, shape_str.length - 1);
                          }
                          effect.target_int.target_type = "Area";
                          try {
                            const area = +area_str;
                            if (!isNaN(area)) {
                              effect.target_int.area.base = area;
                            }
                          } catch (ex) { }
                          shape_str = common_util.capitalize_first(shape_str);
                          if (AREA_TYPES.includes(shape_str)) {
                            effect.target_int.area_type = shape_str as AreaType;
                          } else {
                            effect.target_int.area_type = "Sphere";
                          }
                        }
                      }
                      const potence = new Potence();
                      potence.level = 1;
                      potence.rolls.count.base = count;
                      potence.rolls.size.base = size;
                      // console.log(damage_str);
                      // console.log(damage_sub);
                      if (flat !== 0) {
                        if (using_ability !== "" && using_ability_atk === "") {
                          if (using_ability_mod <= flat) {
                            potence.rolls.flat.base = flat - using_ability_mod;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability);
                            }
                          } else {
                            potence.rolls.flat.base = flat;
                          }
                        } else if (using_ability === "" && using_ability_atk !== "") {
                          if (using_ability_atk_mod <= flat) {
                            potence.rolls.flat.base = flat - using_ability_atk_mod;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability_atk === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                            }
                          } else {
                            potence.rolls.flat.base = flat;
                          }
                        } else if (using_ability !== "" && using_ability_atk !== "") {
                          if (using_ability_atk_mod === flat) {
                            potence.rolls.flat.base = 0;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability_atk === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                            }
                          } else if (using_ability_mod === flat) {
                            potence.rolls.flat.base = 0;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability);
                            }
                          } else if (using_ability_mod < flat) {
                            potence.rolls.flat.base = flat - using_ability_mod;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability);
                            }
                          } else if (using_ability_atk_mod < flat) {
                            potence.rolls.flat.base = flat - using_ability_atk_mod;
                            potence.rolls.flat.add_ability_mod_mult = 1;
                            if (using_ability_atk === c.spellcasting_ability) {
                              potence.rolls.flat.add_ability_scores.push("Spellcasting");
                            } else {
                              potence.rolls.flat.add_ability_scores.push(using_ability_atk);
                            }
                          } else {
                            potence.rolls.flat.base = flat;
                          }
                        }
                      }
                      ca.description = ca.description.replace(replace_me, potence.potence_string);
                      effect.potences.push(potence);
                      ca.effects.push(effect);
                    }
                  }
                  if (ca.effects.length === 0) {
                    const effect = new AbilityEffect();
                    if (ca.dc.isBlank()) {
                      effect.type = "Utility";
                    } else {
                      effect.type = "Control";
                      effect.attack_type = "Save";
                      effect.saving_throw_ability_score = ca.saving_throw_ability_score;
                      const conditions = api.conditions;
                      for (const condition of conditions.filter(o => o.common)) {
                        const condition_name = condition.name;
                        if (ca.description.includes(condition_name) || ca.description.includes(condition_name.toLowerCase())) {
                          effect.utility_detail = condition_name;
                        }
                      }
                    }
                    ca.effects.push(effect);
                  }
                  if (fbType === "Legendary Actions") {
                    // debugger;
                    ca.consumed_resource.resource_consumed = "Legendary Action";
                    if (ca.name.includes("(Costs") && ca.name.includes("Actions)")) {
                      try {
                        // (Costs X Actions)
                        const actions_str = ca.name.substring(ca.name.indexOf("(Costs"));
                        const pieces = common_util.cleanForCompare(actions_str).split(" ");
                        const action_num = +pieces[1];
                        if (!isNaN(action_num)) {
                          ca.consumed_resource.amount_consumed = action_num;
                        }
                      } catch (ex) { }
                    } else {
                      ca.consumed_resource.amount_consumed = 1;
                    }
                  } else {
                    if (ca.name.includes("/Day)")) {
                      ca.consumed_resource.amount_consumed = 1;
                      
                      const times_index = ca.name.indexOf("/Day");
                      
                      let times_str = "";
                      let i = 1;
                      while (ca.name[times_index - i] !== " ") {
                        times_str = ca.name[times_index - i] + times_str;
                        ++i;
                      }
                      
                      try {
                        const times = +times_str;
                        if (!isNaN(times)) {
                          ca.consumed_resource.special_resource_amount = new UpgradableNumber(times);
                        }
                      } catch (ex) { }
                    }
                    if (fbType === "Bonus Actions") {
                      ca.casting_time = "BA";
                    } else if (fbType === "Reactions") {
                      ca.casting_time = "RA";
                    }
                  }
                  // Bite. Melee Weapon Attack: +12 to hit, reach 10 ft., one target. 
                  // Hit: 18 (2d10 + 7) piercing damage plus 5 (1d10) lightning damage.

                  // Frightful Presence. Each creature of the dragon's choice that is 
                  // within 120 feet of the dragon and aware of it must succeed on a 
                  // DC 17 Wisdom saving throw or become frightened for 1 minute. 
                  // A creature can repeat the saving throw at the end of each of its turns, 
                  // ending the effect on itself on a success. If a creature's saving throw 
                  // is successful or the effect ends for it, the creature is immune to the 
                  // dragon's Frightful Presence for the next 24 hours.

                  if (ca.range.length > 0 && ca.effects.length > 0) {
                    const pieces = ca.range.split("/");
                    const range1 = +pieces[0];
                    const range2 = pieces.length > 1 ? +pieces[1] : 0;
                    if (!isNaN(range1) && !isNaN(range2)) {
                      ca.effects[0].target_int.range.base = range1;
                      ca.effects[0].target_int.range2.base = range2;
                    }
                  }
                  // Lightning Breath (Recharge 5–6)
                  // Ink Cloud (1/Day)
                  if (ca.name.includes("(Recharge 5-6)")) {
                    ca.consumed_resource.recharge_die = 6;
                    ca.consumed_resource.recharge_on = [5,6];
                    ca.consumed_resource.resource_consumed = "Recharge";
                  } else if (ca.name.includes("(Recharge 4-6)")) {
                    ca.consumed_resource.recharge_die = 6;
                    ca.consumed_resource.recharge_on = [4,5,6];
                    ca.consumed_resource.resource_consumed = "Recharge";
                  } else if (ca.name.includes("(Recharge 6)")) {
                    ca.consumed_resource.recharge_die = 6;
                    ca.consumed_resource.recharge_on = [6];
                    ca.consumed_resource.resource_consumed = "Recharge";
                  } else if (ca.name.includes("/Day)")) {
                    ca.consumed_resource.resource_consumed = "Special";
                    ca.consumed_resource.amount_consumed = 1;
                    ca.consumed_resource.special_resource_refresh_rule = "Long Rest";
                    try {
                      let pieces = ca.name.split("(");
                      pieces = pieces[1].split("/");
                      const x_day = +pieces[0];
                      if (!isNaN(x_day)) {
                        ca.consumed_resource.special_resource_amount = new UpgradableNumber(x_day);
                      }
                    } catch (ex) {
                      ca.consumed_resource.special_resource_amount = new UpgradableNumber(1);
                    }
                  }
                }
                return ca;
              }
            }
            new_descs = [];
            for (const dv of descVersions) {
              const temp_ff = makeFF(dv);
              
              if (temp_ff && !descVersions.includes(temp_ff.description)) {
                new_descs.push(temp_ff.description);
              }
            }
            descVersions.push(...new_descs);
            ff = makeFF(desc_helper);
            if (ff && !descVersions.includes(ff.description)) {
              descVersions.push(ff.description);
            }
            
            const desc = common_util.cleanSpaces(ff ? `${ff.description}` : `${content_str}`);
            if (!descVersions.includes(desc)) {
              descVersions.push(desc);
            }
            if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
              console.log([...descVersions]);
            }
            new_descs = [];
            for (const dv of descVersions) {
              const new_desc = common_util.cleanSpaces(dv);
              if (!descVersions.includes(new_desc) && !new_descs.includes(new_desc)) {
                new_descs.push(new_desc);
              }
            }
            descVersions.push(...new_descs);
            let ffsWithName: FloatingFeature[] = [];
            if (ff) {
              if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
                console.log([...descVersions]);
              }
              const feature_type = ff.feature_type;
              ffsWithName = ff && ff.feature_type === "Creature Ability" ? 
                api.all_creature_features.filter(o => o.name === name) :
                api.all_floating_features.filter(o => o.feature_type === feature_type && o.name === name);
            }
            if (name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name)) {
              console.log([...descVersions]);
              console.log(ffsWithName.map(o => o.description));
            }
            content_str = desc; // Update the fb's description to be the best cleaned one, so we can update the ff to use it as well.
            let old_ff = ffsWithName.find(o => 
              common_util.cleanCompare(o.description, desc) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
            );
            if (!old_ff) {
              for (const dv of descVersions) {
                old_ff = ffsWithName.find(o => 
                  common_util.cleanCompare(o.description, dv) //, name.includes(looking_for_fb_name) && c.name.includes(looking_for_creature_name))
                );
                if (old_ff) {
                  break;
                }
              }
            }
            if (old_ff) {
              ff = old_ff;
            }
          }
          if (ff) {
            // debugger;
            if (fbType === "Actions" || fbType === "Bonus Actions" || fbType === "Reactions") {
              const feature = new Feature();
              feature.floating_feature = ff;
              feature.floating_feature_id = ff._id;
              fb.creature_fb_type = "Actions";
              fb.name = ff.name;
              fb.level = c.min_challenge_rating;
              fb.features.push(feature);
            } else if (fbType === "Legendary Actions") {
              const feature = new Feature();
              feature.floating_feature = ff;
              feature.floating_feature_id = ff._id;
              fb.creature_fb_type = "Legendary Actions";
              fb.name = ff.name;
              fb.level = c.min_challenge_rating;
              fb.features.push(feature);
            } else if (fbType === "Lair Actions") {
              const feature = new Feature();
              feature.floating_feature = ff;
              feature.floating_feature_id = ff._id;
              fb.creature_fb_type = "Lair Actions";
              fb.name = ff.name;
              fb.level = c.min_challenge_rating;
              fb.features.push(feature);
            } else if (fbType === "Traits") {
              const feature = new Feature();
              feature.floating_feature = ff;
              feature.floating_feature_id = ff._id;
              fb.creature_fb_type = "Traits";
              fb.name = ff.name;
              fb.level = c.min_challenge_rating;
              fb.features.push(feature);
            } else {
              // console.log(action_type);
              // console.log(content_str);
              debugger;
            }
          } else {
            // console.log(action_type);
            // console.log(content_str);
            debugger;
          }

          fb.name = name;
          fb.description = content_str;
          c.fbs.push(fb);
        }
      }

      const processThing = async (thing: HTMLElement) => {
        const rowText = thing.innerText;
        // It always starts with the name, so if it's not set then that's what's first.
        if (c.name === "") {
          // <tr class="c38">
          //   <td class="c73" colspan="6" rowspan="1">
          //     <h1 class="c13 c51 c72" id="h.hljn1xgkggk5"><span class="c22 c27 c34">INTONER</span><span
          //         class="c22 c27 c48"><br></span><span class="c56 c24 c27 c65">Medium Undead, Any Alignment</span></h1>
          //   </td>
          // </tr>
          const h1s = thing.getElementsByTagName("h1");
          if (h1s.length === 1 && h1s[0].childElementCount > 1 && h1s[0].children[0] instanceof HTMLElement) {
            c.name = h1s[0].children[0].innerText;
          }
          let content = rowText.replace(c.name, "").trim();
          c.name = common_util.capitalize_firsts(c.name.trim().toLocaleLowerCase());
          existing_c = old_creatures.find(o => o.name === c.name);
          if (existing_c) {
            await api.loadObject(existing_c);
          }
          
          // It's possible that the size creature_type, alignment line is part of this row as well, so check for it.
          if (content.length > 0) {
            if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Metadata") {
              debugger;
            }
            const pieces = content.split(",");
            const pieces2 = common_util.cleanForCompare(pieces[0]).split(" ");
            c.size_ugn.base = SIZE_TO_NUM_MAP[common_util.capitalize_first(pieces2[0])];
            if (pieces2.length === 2) {
              const ct_str = common_util.capitalize_first(pieces2[1]);
              if (isCreatureType(ct_str)) {
                c.creature_type = ct_str;
              }
            } else if (pieces2.length > 2) {
              // Huge fiend (demon), chaotic evil
              const ct_str = common_util.capitalize_first(pieces2[1]);
              if (isCreatureType(ct_str)) {
                c.creature_type = ct_str;
              }
              let subtype_str = pieces[0].substring(common_util.cleanForCompare(pieces[0]).indexOf(pieces2[1]) + pieces2[1].length).trim();
              subtype_str = common_util.capitalize_firsts(subtype_str.replace("(", "").replace(")", ""));
              c.subtypes = [subtype_str];
            } else {
              debugger;
            }
            if (common_util.cleanForCompare(c.name).includes(c.creature_type.toLocaleLowerCase())) {
              c.small_name = c.creature_type.toLocaleLowerCase();
            }
            const alignment_string = pieces[1].trim();
            c.alignment = alignment_string;
          }
        } else if (c.creature_type === "None") {
          debugger;
          // I haven't seen a version where it's not part of the same thing as name, 
          // but I expect there will be some.
        } else if (c.ability_scores.strength.isBlank() && rowText.includes("STR") && thing instanceof HTMLTableRowElement) {
          // For Ability Scores, thing_were_looking_at is going to be the row
          // <tr class="c67">
          //   <td class="c45" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">STR</span></p>
          //     <p class="c6"><span class="c1 c37 c27">11 (+0)</span></p>
          //   </td>
          //   <td class="c36" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">DEX</span></p>
          //     <p class="c6"><span class="c1 c27">20 (+5)</span></p>
          //   </td>
          //   <td class="c36" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">CON</span></p>
          //     <p class="c6"><span class="c1 c27">16 (+3)</span></p>
          //   </td>
          //   <td class="c36" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">INT</span></p>
          //     <p class="c6"><span class="c1 c27">18 (+4)</span></p>
          //   </td>
          //   <td class="c36" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">WIS</span></p>
          //     <p class="c6"><span class="c1 c27">14 (+2)</span></p>
          //   </td>
          //   <td class="c71" colspan="1" rowspan="1">
          //     <p class="c6"><span class="c4 c37">CHA</span></p>
          //     <p class="c6"><span class="c1 c27">23 (+6)</span></p>
          //   </td>
          // </tr>
          const cells = thing.cells;
          for (let cell_index = 0; cell_index < cells.length; ++cell_index) {
            const cell = cells[cell_index];
            const p_els = cell.getElementsByTagName("p");
            const stat_p = p_els[0];
            const score_p = p_els[1];
            const stat = common_util.cleanForCompare(stat_p.innerText.trim()).toLocaleUpperCase();
            if (isStatType(stat)) {
              const pieces = common_util.cleanForCompare(score_p.innerText).split(" ");
              const score = +pieces[0];
              // console.log(score);
              if (!isNaN(score)) {
                // console.log(`${stat}: ${score}`);
                c.ability_scores.setAbilityScore(stat, score);
              } else {
                debugger;
              }
            } else {
              debugger;
            }
          }
        } else if (rowText.length > 0) {
          console.log(rowText);
          // At this point we want to check for the possibility that there are <p>s inside it 
          // that need to be processed separately, 
          // and sometimes <ul>s that need to be processed with the thing above them.
          let p_els: HTMLCollection | undefined;
          if (thing instanceof HTMLTableRowElement) {
            p_els = thing.getElementsByTagName("p");
          }
          if (p_els && p_els.length > 0) {
            for (let p_index = 0; p_index < p_els.length; ++p_index) {
              const p = p_els[p_index];
              if (p instanceof HTMLElement) {
                await processThing(p);
              }
            }
          } else {
            if (rowText.includes("Armor Class") && c.armor_class.isBlank()) {
              let pieces = common_util.cleanForCompare(rowText).split(" ");
              if (pieces.length > 2) {
                c.armor_class.base = +pieces[2];
                pieces = pieces.slice(3);
                c.ac_flavor = pieces.join(" ").replace("(","").replace(")","");
              }
            } else {
              if (c.hit_dice.length === 0 && rowText.includes("Hit Points")) {
                let index = rowText.indexOf("(");
                let hd_str = rowText.substring(index + 1).replace(")", "");
                index = hd_str.indexOf(" ");
                if (index > 0) {
                  hd_str = hd_str.substring(0, index);
                } else {
                  index = hd_str.indexOf("+");
                  if (index > 0) {
                    hd_str = hd_str.substring(0, index);
                  } else {
                    index = hd_str.indexOf("-");
                    if (index > 0) {
                      hd_str = hd_str.substring(0, index);
                    } 
                  }
                }
                const pieces = hd_str.split("d");
                if (pieces.length === 2) {
                  const hd = new HitDiceUpgradable();
                  hd.count.base = +pieces[0];
                  c.hit_dice.push(hd);
                }
              } else if (Object.keys(attr_entry.speeds).length === 0 && rowText.includes("Speed")) {
                if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Speed") {
                  debugger;
                }
                let spd_str = rowText;
                const pieces = common_util.cleanForCompare(spd_str).split(" ");
                let spd_type: SpeedType = "walk";
                for (const piece of pieces) {
                  if (isSpeedType(piece)) {
                    spd_type = piece;
                  } else if (spd_type === "fly" && piece.includes("hover")) {
                    attr_entry.hover = true;
                  } else if (!piece.includes("ft.")) {
                    attr_entry.speeds[spd_type] = +piece;
                  }
                }
              } else if (rowText.includes("Challenge") && c.challenge_rating === 0) {
                // <tr class="c8">
                //   <td class="c25" colspan="6" rowspan="1">
                //     <p class="c35"><span class="c4">Saving Throws</span><span class="c1 c37 c27">&nbsp;DEX +11, INT +10, CHA
                //         +13</span></p>
                //     <p class="c35"><span class="c4">Skills</span><span class="c1 c37 c27">&nbsp;Arcana +10, Deception +13, Insight
                //         +8, Performance +13, Persuasion +13</span></p>
                //     <p class="c35"><span class="c4">Damage Resistances</span><span class="c1 c27 c37">&nbsp;Cold, Necrotic, Poison;
                //         Bludgeoning, Piercing, and Slashing from Nonmagical Attacks</span></p>
                //     <p class="c35"><span class="c4">Condition Immunities</span><span class="c1 c27">&nbsp;Charmed, Exhaustion,
                //         Frightened, Paralyzed, Poisoned<br></span><span class="c4">Senses </span><span class="c1 c37 c27">Darkvision
                //         120 ft., passive Perception 12</span></p>
                //     <p class="c35"><span class="c4">Languages</span><span class="c1 c37 c27">&nbsp;The languages it knew in
                //         life</span></p>
                //     <p class="c35"><span class="c4">Challenge </span><span class="c1 c27">18 (20,000 XP) &nbsp; &nbsp; &nbsp; &nbsp;
                //         &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                //         &nbsp; &nbsp; &nbsp; &nbsp;</span><span class="c4">Proficiency Bonus</span><span
                //         class="c1 c27">&nbsp;+6</span></p>
                //   </td>
                // </tr>
                const pieces = rowText.replace("Challenge", "").trim().split(" ");
                if (pieces.length > 0) {
                  const cr_str = pieces[0];
                  if (cr_str.includes("/")) { // 1/4 or 1/2
                    const pieces2 = cr_str.split("/");
                    const num = +pieces2[0];
                    const den = +pieces2[1];
                    if (!isNaN(num) && !isNaN(den)) {
                      c.challenge_rating = num / den;
                    } else {
                      debugger;
                    }
                  } else {
                    const num = +cr_str;
                    if (!isNaN(num)) {
                      c.challenge_rating = num;
                    } else {
                      debugger;
                    }
                  }
                } else {
                  debugger;
                }
              } else if (rowText.includes("Saving Throws") && attr_entry.save_profs.length === 0) {
                const pieces = common_util.cleanForCompare(rowText).split(" ");
                for (const piece of pieces) {
                  const stat = piece.replace(",", "").toLocaleUpperCase();
                  if (isStatType(stat)) {
                    attr_entry.save_profs.push(stat);
                  }
                }
              } else if (rowText.includes("Skills") && !skills_el) {
                skills_el = thing; // It needs to be processed after we know the challenge rating.
              } else if (rowText.includes("Resistances") && attr_entry.resistances_string.length === 0) {
                // debugger;
                // <p class="c35"><span class="c4">Damage Resistances</span><span class="c1 c27 c37">&nbsp;Cold, Necrotic, Poison;
                //     Bludgeoning, Piercing, and Slashing from Nonmagical Attacks</span></p>
                let str = common_util.cleanForCompare(rowText);
                str = str.substring(str.indexOf("damage resistances") + 18).trim();
                const pieces = str.split(";");
                // debugger;
                if (pieces.length === 1) {
                  // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
                  const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                  let bad = false;
                  for (const piece of pieces2) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_resistances.push(piece);
                    } else {
                      bad = true;
                      break;
                    }
                  }
                  if (bad) {
                    attr_entry.damage_resistances = [];
                    attr_entry.extra_damage_resistances = pieces2.join(", ");
                  }
                } else {
                  const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                  for (const piece of pieces2) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_resistances.push(piece);
                    } else {
                      debugger; // Shouldn't come here.
                    }
                  }
                  attr_entry.extra_damage_resistances = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
                }
              } else if (rowText.includes("Immunities") || rowText.includes("Senses")) {
                // <p class="c35"><span class="c4">Condition Immunities</span><span class="c1 c27">&nbsp;Charmed, Exhaustion,
                //   Frightened, Paralyzed, Poisoned<br></span><span class="c4">Senses </span><span class="c1 c37 c27">Darkvision
                //   120 ft., passive Perception 12</span></p>
                // debugger;
                // I expect there will be some statblocks that don't fit this model, 
                // but until I see them I can't plan for them.
                let what_were_doing = "";
                const children = thing.children;
                for (let c_index = 0; c_index < children.length; ++c_index) {
                  const child = children[c_index];
                  if (child instanceof HTMLSpanElement) {
                    let str = common_util.cleanForCompare(child.innerText);
                    if (str.includes("immunities") || str.includes("senses")) {
                      what_were_doing = str;
                    } else if (what_were_doing === "condition immunities") {
                      const pieces = str.split(" ");
                      const conditions = api.conditions;
                      for (const piece of pieces) {
                        const cond_str = common_util.capitalize_first(piece).replace(",", "").trim();
                        const cond = conditions.find(o => o.name === cond_str);
                        if (cond) {
                          attr_entry.condition_immunities.push(cond.name);
                        } else {
                          debugger;
                        }
                      }
                    } else if (what_were_doing === "damage immunities") {
                      let str = common_util.cleanForCompare(rowText);
                      str = str.substring(str.indexOf("damage immunities") + 17).trim();
                      const pieces = str.split(";");
                      // debugger;
                      if (pieces.length === 1) {
                        // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
                        const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                        let bad = false;
                        for (const piece of pieces2) {
                          if (isDamageType(piece)) {
                            attr_entry.damage_immunities.push(piece);
                          } else {
                            bad = true;
                            break;
                          }
                        }
                        if (bad) {
                          attr_entry.damage_immunities = [];
                          attr_entry.extra_damage_immunities = pieces2.join(", ");
                        }
                      } else {
                        const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                        for (const piece of pieces2) {
                          if (isDamageType(piece)) {
                            attr_entry.damage_immunities.push(piece);
                          } else {
                            debugger; // Shouldn't come here.
                          }
                        }
                        attr_entry.extra_damage_immunities = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
                      }
                    } else if (what_were_doing === "senses") {
                      if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Senses") {
                        debugger;
                      }
                      const pieces = common_util.cleanForCompare(rowText).split(" ");
                      let sense: Sense | undefined;
                      for (const piece of pieces) {
                        const sense_str = piece.toLocaleLowerCase().replace(",", "").replace(" ", "");
                        if (sense) {
                          const num = +sense_str;
                          if (!isNaN(num)) {
                            attr_entry.senses[sense.name] = num;
                            continue;
                          } else {
                            sense = undefined;
                          }
                        }
                        const sense_maybe = SENSES.find(o => o.name.toLocaleLowerCase().replace(" ", "").includes(sense_str));
                        if (sense_maybe) {
                          sense = sense_maybe;
                        }
                      }
                    }
                  }
                }
              } else if (rowText.includes("Vulnerabilities")) {
                let str = common_util.cleanForCompare(rowText);
                str = str.substring(str.indexOf("damage vulnerabilities") + 22).trim();
                const pieces = str.split(";");
                // debugger;
                if (pieces.length === 1) {
                  // It's possible that it's just something that should be extra. Search for words that are not DamageTypes.
                  const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                  let bad = false;
                  for (const piece of pieces2) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_vulnerabilities.push(piece);
                    } else {
                      bad = true;
                      break;
                    }
                  }
                  if (bad) {
                    attr_entry.damage_vulnerabilities = [];
                    attr_entry.extra_damage_vulnerabilities = pieces2.join(", ");
                  }
                } else {
                  const pieces2 = common_util.capitalize_firsts(pieces[0]).split(",").map(o => o.trim());
                  for (const piece of pieces2) {
                    if (isDamageType(piece)) {
                      attr_entry.damage_vulnerabilities.push(piece);
                    } else {
                      debugger; // Shouldn't come here.
                    }
                  }
                  attr_entry.extra_damage_vulnerabilities = common_util.capitalize_firsts(pieces[1], true, ["and", "or", "from"]);
                }
              } else if (rowText.includes("Languages")) {
                const language_str = rowText.replace("Languages", "").trim();
                attr_entry.languages = language_str;
              } else {
                const compareText = common_util.cleanForCompare(rowText);
                if (compareText === "actions") {
                  // <tr class="c64">
                  //   <td class="c18" colspan="6" rowspan="1">
                  //     <p class="c13"><span class="c27 c28">&nbsp;</span><span class="c11">A</span><span class="c0">CTIONS</span></p>
                  //   </td>
                  // </tr>
                  fbType = "Actions";
                } else if (compareText === "legendary actions") {
                  fbType = "Legendary Actions";
                } else if (compareText === "lair actions") {
                  fbType = "Lair Actions";
                } else if (compareText === "reactions") {
                  fbType = "Reactions";
                } else if (compareText === "bonus actions") {
                  fbType = "Bonus Actions";
                } else {
                  if (fbType === "Legendary Actions" && rowText.includes("Only one legendary action option")) {
                    // <p class="c13"><span class="c5 c1">The intoner can take 3 legendary actions, choosing from the options below.
                    //   Only one legendary action option can be used at a time and only at the end of another creature’s turn. The
                    //   intoner regains spent legendary actions at the start of its turn.</span></p>
                    let desc = rowText;
                    let index = desc.indexOf("can take");
                    desc = desc.substring(index + 8);
                    index = desc.indexOf("legendary actions");
                    desc = desc.substring(0, index).trim();
                    const pieces = common_util.cleanForCompare(desc).split(" ");
                    const num = pieces.length > 0 ? +pieces[0] : 0;
                    if (pieces.length === 1 && !isNaN(num)) {
                      leg_entry.actions = num;
                    } else {
                      debugger;
                    }
                  } else if (fbType === "Lair Actions" && rowText.includes("On initiative count 20")) {
                    // <p class="c13"><span class="c5 c1">On initiative count 20 (losing initiative ties), the intoner can take a lair
                    //   action to cause one of the following effects; the intoner can’t use the same effect two rounds in a
                    //   row.</span></p>
                  } else {
                    processFB(thing, []);
                  }
                  if (fbType === "Lair Actions") {
                    // Check for a <ul> after it.
                    if (c.name.includes(looking_for_creature_name) && looking_for_fb_name === "Lair Actions") {
                      debugger;
                    }
                    let nextElementSibling = thing.nextElementSibling;
                    while (nextElementSibling && !(nextElementSibling instanceof HTMLUListElement)) {
                      nextElementSibling = nextElementSibling.nextElementSibling;
                    }
                    if (nextElementSibling instanceof HTMLUListElement) {
                      // For some reason, Lair Actions are generally not given names, both by WotC and third parties, 
                      // so we just need to put the descriptions in.
                      
                      const li_els = nextElementSibling.getElementsByTagName("li");
                      for (let li_index = 0; li_index < li_els.length; ++li_index) {
                        const li = li_els[li_index];
                        processFB(li, []);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      console.log(the_rows);
      for (let row_index = 0; row_index < the_rows.length; ++row_index) {
        const row = the_rows[row_index];
        await processThing(row);
      }

      if (skills_el) {
        // <p class="c35"><span class="c4">Skills</span><span class="c1 c37 c27">&nbsp;Arcana +10, Deception +13, Insight
        //     +8, Performance +13, Persuasion +13</span></p>
        const pieces = common_util.cleanForCompare(skills_el.innerText).split(" ");
        const pb = c.proficiency_modifier;
        let skill: Skill | undefined;
        for (let k = 0; k < pieces.length; ++k) {
          const piece = pieces[k].replace(",", "").replace("+", "");
          let skill_mod = +piece;
          if (isNaN(skill_mod)) {
            skill = SKILLS.find(o => o.name === common_util.capitalize_first(piece));
          } else if (skill) {
            const stat_score = c.ability_scores.getBasicScore(skill.use_ability_score);
            const stat_mod = common_util.modFromScore(stat_score);
            skill_mod -= stat_mod;
            attr_entry.skill_profs[skill._id] = skill_mod / pb;
          }
        }
      }
      attr_entry.level = c.challenge_rating;
      tbl_entry.level = c.challenge_rating;
      if (leg_entry.resistances > 0 || leg_entry.actions > 0) {
        leg_entry.level = c.challenge_rating;
        c.legendaries_table.entries = [leg_entry];
      }
      if (Object.keys(resources_entry.data).length > 0) {
        resources_entry.level = c.challenge_rating;
        c.resources_table.entries = [resources_entry];
      }
    }
    c.max_challenge_rating = c.challenge_rating;
    c.min_challenge_rating = c.challenge_rating;
    if (existing_c) {
      if (existing_c.name.includes(looking_for_creature_name)) {
        // debugger;
        console.log(existing_c.name);
      }
      existing_c.size_ugn.copy(c.size_ugn);
      existing_c.armor_class.copy(c.armor_class);
      existing_c.ac_flavor = c.ac_flavor;
      existing_c.hit_dice = c.hit_dice;
      existing_c.ability_scores = c.ability_scores;
      existing_c.fbs = c.fbs;
      existing_c.attributes_table.entries = c.attributes_table.entries;
      existing_c.table.entries = c.table.entries;
      existing_c.legendaries_table.entries = c.legendaries_table.entries;
      existing_c.spellcasting_table.entries = c.spellcasting_table.entries;
      existing_c.spellcasting_ability = c.spellcasting_ability;
      existing_c.resources_table.entries = c.resources_table.entries;
      existing_c.small_name = c.small_name;
      existing_c.creature_type = c.creature_type;
      existing_c.subtypes = c.subtypes;
      return existing_c;
    } else {
      const c_name = c.name;
      const old_dominant = all_creatures.find(o => o.name === c_name && o.isDominant());
      if (old_dominant) {
        // c.dominance_level = old_dominant.dominance_level + 1;
        c.variant_of_id = old_dominant.variant_group_id;
      }
      return c;
    }
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth - 1} message="Loading Creatures" />);
  } else if (disabled) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <ArrowBackButton
            onClick={ () => {
              setNavigateTo(`/dandd/creature`);
            }}
          />
        </Grid>
        <Grid size={12}>
          <DisplayStringWithReplacementsRecursive display_string={`{creature_stat_block.${obj._id}}`} />
        </Grid>
      </Grid>
    );
  } else { 
    const menu_options: IStringHash = disabled ? {
      "Home": "main",
      "HP": "hp",
      "Attributes": "attributes",
      "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table"
    } : {
      "Home": "main",
      "HP": "hp",
      "Attributes": "attributes",
      "More Attributes": "more_attributes",
      "Even More Attributes": "even_more_attributes",
      "Ability Scores": "ability_scores",
      "Resources": "resources",
      "Actions": "actions",
      "Legendary Actions": "legendary_actions",
      "Lair Actions": "lair_actions",
      "Traits": "traits",
      "Spells": "spells",
      "Table": "table",
      "Parse": "parse",
    };
    // if (obj.ridable_type !== "None") {
    //   delete menu_options["Lair Actions"];
    //   delete menu_options["Legendary Actions"];
    // }
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          ref={heightFinder}
          size={12}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <ArrowBackButton
                onClick={ () => {
                  setNavigateTo(`/dandd/creature`);
                }}
              />&nbsp;
              <span className="header">
                { obj._id === "" ? "Create Creature" : `Edit ${obj.name}` }
              </span>
            </Grid>
            { !disabled &&
              <Grid size={12}>
                <TemplateBox
                  obj={obj}
                  type="Creature"
                  useTemplate={(template: TemplateBase) => {
                    const new_obj = obj;
                    new_obj.copyTemplate(template);
                    setObj(new_obj);
                    updateCreatureInstances(new_obj);
                    setChangeTime(common_util.get_time_number());
                  }}
                />
              </Grid>
            }
            <Grid size={12}>
              <FloatMenu
                option_map={menu_options}
                selected={tab}
                onSelect={(changed: string) => {
                  // if (changed.includes("actions") || changed === "traits") {
                  //   updateCreatureInstance();
                  // }
                  setTab(changed as TabType);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          style={{ 
            height: (tab === "main" ? `${window.innerHeight - (topHeight + 106)}px` : `${window.innerHeight - (topHeight + 64)}px`), 
            width: "100%",
            overflowY: "auto", 
            overflowX: "hidden"
          }}
          size={12}>
          {/* { window.innerHeight } - ({ topHeight } + 64) = {window.innerHeight - (topHeight + 64)} */}
          { render_tab() }
        </Grid>
      </Grid>
    ); 
  }
}
